import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocument } from 'pdfjs-dist/webpack.mjs';

import {
  DEFAULT_ADMIN_DASHBOARD_CARD_ORDER,
  moveAdminDashboardCard,
  moveAdminDashboardCardByOffset,
  normalizeAdminDashboardCardOrder,
} from '../utils/adminDashboardCardOrder';
import {
  buildOpenOperations,
  buildVerificationReviewWorklist,
  filterVerificationReviewWorklist,
  VERIFICATION_REVIEW_FILTERS,
} from '../utils/adminOperations';
import { filterActiveScans, getNextScanAfterRemoval, skipScanInRound } from '../utils/scanQueue';
import { assignScanWithRecovery } from '../utils/scanAssignment';
import { getMissingPersonalAgreementFields } from '../utils/usageAgreementCompleteness';
import { getUsageAgreementStatusView } from '../utils/usageAgreementAdminStatus';
import { getLang } from '../i18n';
import CompanyDataChangeHistory from './CompanyDataChangeHistory';
import ChatAutomationSettingsCard from './ChatAutomationSettingsCard';
import GoogleReviewAdminSettingsCard from './GoogleReviewAdminSettingsCard';
import MarketingLeadOverview from './MarketingLeadOverview';
import ControlledInvoiceReview from './ControlledInvoiceReview';
import FinanceEmergencyControl from './FinanceEmergencyControl';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const ADMIN_STARTUP_TIMEOUT_MS = 12000;

async function loadAdminStartupResource({ path, label, token, fallback }) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), ADMIN_STARTUP_TIMEOUT_MS);

  try {
    const response = await fetch(`${API_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    const responseData = await response.json().catch(() => fallback);

    return {
      response,
      data: response.ok ? responseData : fallback,
      warning: response.ok ? '' : (responseData?.error || `${label} konnte nicht geladen werden.`),
    };
  } catch (error) {
    return {
      response: null,
      data: fallback,
      warning: error?.name === 'AbortError'
        ? `${label} hat länger als 12 Sekunden benötigt und wurde vorerst übersprungen.`
        : `${label} konnte nicht geladen werden.`,
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

function formatRevenue(amountMinor, currency = 'EUR') {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(Number(amountMinor || 0) / 100);
}

function formatSubscriptionCounts(renewalCount = 0, newSubscriptionCount = 0) {
  const renewalLabel = renewalCount === 1 ? 'Abo-Verlängerung' : 'Abo-Verlängerungen';
  const newLabel = newSubscriptionCount === 1 ? 'neues Abo' : 'neue Abos';
  return `${renewalCount} ${renewalLabel} · ${newSubscriptionCount} ${newLabel}`;
}

function formatReleaseTimestamp(value) {
  const match = String(value || '').match(/^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})$/);
  if (!match) return value || 'Noch nicht verfügbar';
  const [, year, month, day, hour, minute, second] = match;
  return `${day}.${month}.${year}, ${hour}:${minute}:${second} Uhr`;
}

function formatWaitingTime(milliseconds) {
  const hours = Math.max(0, Math.floor(Number(milliseconds || 0) / (60 * 60 * 1000)));
  if (hours < 24) return `${hours} Std.`;
  const days = Math.floor(hours / 24);
  return `${days} Tag${days === 1 ? '' : 'e'}`;
}

const PRODUCTION_STATUS_LABELS = {
  confirmed: 'Bestätigt',
  partial: 'Teilweise bestätigt',
  unknown: 'Unbekannt',
  inconsistent: 'Widerspruch',
};

function productionStatusLabel(value) {
  return PRODUCTION_STATUS_LABELS[value] || 'Unbekannt';
}

function configurationStateLabel(value) {
  return ({
    enabled: 'Aktiv', disabled: 'Inaktiv', unknown: 'Unbekannt', invalid: 'Ungültig',
    all_eligible_customers: 'Alle berechtigten Kunden', restricted: 'Eingeschränkt',
  })[value] || value || 'Unbekannt';
}

function localMonthKey(dateValue = new Date()) {
  const date = new Date(dateValue);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function formatForecastDate(dateValue) {
  return new Date(`${dateValue}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}
const FOLLOWUP_OPTIONS = [
  'Ausweis Vorderseite',
  'Ausweis Rückseite',
  'Selfie',
  'Gewerbeanmeldung',
  'Handelsregisterauszug',
  'Vollmacht',
  'Zusatzdokument',
];

const QUICK_FILTER_OPTIONS = [
  { key: 'all', label: 'Alle' },
  { key: 'open', label: 'Offen' },
  { key: 'new', label: 'Neu' },
  { key: 'verified', label: 'Verifiziert' },
  { key: 'followup', label: 'Nachforderung offen' },
  { key: 'reversal', label: 'Rückabwicklung erforderlich' },
  { key: 'manual_review', label: 'Manuell prüfen' },
];

const DOCUMENT_LABELS = {
  id_front: 'Ausweis Vorderseite',
  id_back: 'Ausweis Rückseite',
  selfie: 'Selfie',
  gewerbeanmeldung: 'Gewerbeanmeldung',
  handelsregisterauszug: 'Handelsregisterauszug',
  vollmacht: 'Vollmacht',
  business_doc: 'Unternehmensdokument',
  extra_doc: 'Zusatzdokument',
};

const PORTAL_REQUEST_FIELD_LABELS = {
  customer_type: 'Unternehmensart',
  workflow_type: 'Vorgang',
  evidence_label: 'Erforderlicher Nachweis',
  company_name: 'Firmenname',
  legal_form: 'Rechtsform',
  representative_name: 'Vertreter / Inhaber',
  business_activity: 'Tätigkeit / Branche',
  company_street: 'Unternehmensstraße',
  company_postal_code: 'Unternehmens-PLZ',
  company_city: 'Unternehmensort',
  company_country: 'Unternehmensland',
  register_court: 'Registergericht',
  register_number: 'Registernummer',
  tax_number: 'Steuernummer',
  vat_id: 'USt-ID',
  planned_foundation_date: 'Geplantes Gründungsdatum',
  display_name: 'Briefkasten-Name',
  display_note: 'Hinweis / Wunsch',
  logo_document_id: 'Logo-Dokument',
  logo_original_filename: 'Logo-Datei',
};

function stripePaymentMethodLabel(type) {
  const labels = {
    card: 'Karte',
    sepa_debit: 'SEPA-Lastschrift',
    paypal: 'PayPal',
  };
  return labels[String(type || '').trim().toLowerCase()] || 'Nicht verfügbar';
}

function normalizeUtfText(value) {
  return String(value || '')
    .replace(/\u00C3\u0192\u00C2\u00A4/g, 'ä')
    .replace(/\u00C3\u0192\u00C2\u00B6/g, 'ö')
    .replace(/\u00C3\u0192\u00C2\u00BC/g, 'ü')
    .replace(/\u00C3\u0192\u00E2\u20AC\u017E/g, 'Ä')
    .replace(/\u00C3\u0192\u00E2\u20AC\u201C/g, 'Ö')
    .replace(/\u00C3\u0192\u00C5\u201C/g, 'Ü')
    .replace(/\u00C3\u0192\u00C5\u00B8/g, 'ß')
    .replace(/\u00C3\u00A4/g, 'ä')
    .replace(/\u00C3\u00B6/g, 'ö')
    .replace(/\u00C3\u00BC/g, 'ü')
    .replace(/\u00C3\u201E/g, 'Ä')
    .replace(/\u00C3\u2013/g, 'Ö')
    .replace(/\u00C3\u0152/g, 'Ü')
    .replace(/\u00C3\u0178/g, 'ß')
    .replace(/\u00E2\u20AC\u201C/g, '–')
    .replace(/\u00E2\u20AC\u201D/g, '—')
    .replace(/\u00E2\u20AC\u017E/g, '„')
    .replace(/\u00E2\u20AC\u0153/g, '“')
    .replace(/\u00E2\u20AC\u009D/g, '”')
    .replace(/\u00E2\u20AC\u02DC/g, '‘')
    .replace(/\u00E2\u20AC\u2122/g, '’')
    .replace(/\u00EF\u00BB\u00BF/g, '')
    .replace(/\uFFFD/g, 'ü')
    .trim();
}

function getIdentityStatusMeta(status) {
  const value = normalizeUtfText(status).toLowerCase();

  const map = {
    new: { label: 'Neu', tone: 'neutral' },
    submitted: { label: 'Eingereicht', tone: 'info' },
    in_review: { label: 'In Prüfung', tone: 'info' },
    additional_requested: { label: 'Nachforderung offen', tone: 'warning' },
    approved: { label: 'Freigegeben', tone: 'success' },
    already_verified: { label: 'Als erledigt', tone: 'success' },
    rejected: { label: 'Abgelehnt', tone: 'danger' },
    rueckabwicklung_erforderlich: { label: 'Rückabwicklung prüfen', tone: 'warning' },
    rueckabwicklung_bestaetigt: { label: 'Rückabwicklung bestätigt', tone: 'neutral' },
    rueckabgewickelt: { label: 'Rückabgewickelt', tone: 'neutral' },
    frist_verlaengert: { label: 'Frist verlängert', tone: 'info' },
  };

  return map[value] || { label: status || '-', tone: 'neutral' };
}

function getBusinessStatusMeta(status) {
  const value = normalizeUtfText(status).toLowerCase();

  const map = {
    not_required: { label: 'Nicht erforderlich', tone: 'neutral' },
    pending: { label: 'Ausstehend', tone: 'warning' },
    submitted: { label: 'Eingereicht', tone: 'info' },
    in_review: { label: 'In Prüfung', tone: 'info' },
    approved: { label: 'Freigegeben', tone: 'success' },
    already_verified: { label: 'Als erledigt', tone: 'success' },
    rejected: { label: 'Abgelehnt', tone: 'danger' },
    rueckabwicklung_erforderlich: { label: 'Rückabwicklung prüfen', tone: 'warning' },
    rueckabwicklung_bestaetigt: { label: 'Rückabwicklung bestätigt', tone: 'neutral' },
    rueckabgewickelt: { label: 'Rückabgewickelt', tone: 'neutral' },
    frist_verlaengert: { label: 'Frist verlängert', tone: 'info' },
  };

  return map[value] || { label: status || '-', tone: 'neutral' };
}

function getOrderVerificationStatusMeta(status) {
  const value = normalizeUtfText(status).toLowerCase();

  const map = {
    auto_verification_created: { label: 'Automatisch erstellt', tone: 'success' },
    manually_requested: { label: 'Verifizierung angefragt', tone: 'info' },
    existing_verification_reused: { label: 'Bestehender Fall genutzt', tone: 'neutral' },
    already_verified: { label: 'Als erledigt', tone: 'success' },
    company_data_pending: { label: 'Unternehmensdaten noch offen', tone: 'warning' },
    pending_manual_review: { label: 'Manuelle Prüfung nötig', tone: 'warning' },
    awaiting_manual_review: { label: 'Manuelle Prüfung nötig', tone: 'warning' },
    no_verification_required: { label: 'Keine Verifizierung nötig', tone: 'neutral' },
    open_verification_exists: { label: 'Offener Fall vorhanden', tone: 'warning' },
    existing_customer_open_verification: { label: 'Offener Fall vorhanden', tone: 'warning' },
    auto_verification_eligible: { label: 'Automatik möglich', tone: 'info' },
    rejected_customer: { label: 'Früher abgelehnt', tone: 'danger' },
  };

  return map[value] || { label: status || '-', tone: 'neutral' };
}

function getPremiumOfferStatusMeta(status) {
  const value = normalizeUtfText(status).toLowerCase();

  const map = {
    sent: { label: 'Premium angeboten', tone: 'info' },
    accepted: { label: 'Premium aktiv', tone: 'success' },
    expired: { label: 'Premium beendet', tone: 'neutral' },
    cancelled: { label: 'Premium storniert', tone: 'neutral' },
  };

  return map[value] || null;
}

function getMatchSourceLabel(value) {
  const normalized = normalizeUtfText(value).toLowerCase();

  const map = {
    shopify_customer_id: 'Shopify Customer ID',
    email: 'E-Mail',
    reusable_verification: 'Wiederverwendbare Verifizierung',
    open_verification: 'Offener Verifizierungsfall',
    rejected_verification: 'Abgelehnter Fall',
  };

  return map[normalized] || (value ? normalizeUtfText(value) : 'Kein Match');
}

function getAutoDecisionLabel(value) {
  const normalized = normalizeUtfText(value).toLowerCase();

  const map = {
    auto_created: 'Automatisch erstellt',
    skipped_existing_verification: 'Übersprungen, bestehende Verifizierung',
    skipped_manual_review: 'Übersprungen, manuelle Prüfung',
    auto_disabled: 'Automatik deaktiviert',
    waiting_manual_request: 'Wartet auf manuelle Anfrage',
    reuse_existing_verification: 'Bestehende Verifizierung genutzt',
    reuse_already_verified: 'Bereits verifiziert genutzt',
    marked_already_verified: 'Manuell als bereits verifiziert markiert',
    manual_request_created_case: 'Fall erstellt',
    existing_customer_without_verification: 'Bestandskunde ohne Fall',
    manual_review_existing_customer: 'Bestandskunde manuell prüfen',
    new_customer_auto_flow: 'Neukunde Auto-Flow',
    manual_review_new_customer: 'Neukunde manuell prüfen',
    new_verification_after_rejection: 'Neu nach Ablehnung',
    manual_review_after_rejection: 'Manuell nach Ablehnung',
    use_existing_open_case: 'Offenen Fall verwenden',
  };

  return map[normalized] || normalizeUtfText(value);
}

function getDocumentLabel(value) {
  return DOCUMENT_LABELS[value] || normalizeUtfText(value);
}

function getCustomerTypeLabel(value) {
  const normalized = normalizeUtfText(value).toLowerCase();

  const map = {
    privat: 'Privatperson',
    unternehmen: 'Deutsche Kapitalgesellschaft',
    gewerbe: 'Einzelunternehmen / Gewerbe',
    einzelunternehmen: 'Einzelunternehmen / Gewerbe',
    kapitalgesellschaft: 'Deutsche Kapitalgesellschaft',
    verein: 'Verein / eingetragener Verein (e. V.)',
    ausland: 'Ausländisches / nicht-deutsches Unternehmen',
    freelancer: 'Freelancer / Freiberuflich',
    gruendung: 'Gründung geplant',
  };

  return map[normalized] || (value ? normalizeUtfText(value) : '-');
}

function getLegalEntityDisplayName(entity) {
  if (normalizeUtfText(entity?.profile_label)) return normalizeUtfText(entity.profile_label);
  if (normalizeUtfText(entity?.legal_name)) return normalizeUtfText(entity.legal_name);
  if (entity?.entity_type === 'formation') return `Unternehmen in Gründung ${Number(entity.formation_number || 1)}`;
  if (entity?.entity_type === 'private') return 'Privatprofil';
  return 'Unternehmensprofil';
}

function companyProfileNextStepLabel(value) {
  return ({
    support_review_required: 'Supportprüfung erforderlich', book_subscription: 'Eigenes Abo buchen',
    start_verification: 'Unternehmensprüfung starten', complete_identity_verification: 'Identität abschließen',
    complete_company_verification: 'Unternehmensunterlagen vervollständigen', await_company_review: 'Prüfung abwarten',
    contact_support: 'Support kontaktieren', create_usage_agreement: 'Nutzungsvereinbarung erstellen',
    await_mailbox_setup: 'Postfach-Einrichtung abwarten', ready: 'Kein weiterer Schritt erforderlich',
  })[value] || 'Supportprüfung erforderlich';
}

function mapCustomerTypeToAgreementType(value) {
  const normalized = normalizeUtfText(value).toLowerCase();

  if (normalized === 'unternehmen' || normalized === 'kapitalgesellschaft') return 'kapitalgesellschaft';
  if (normalized === 'verein') return 'verein';
  if (normalized === 'gewerbe' || normalized === 'einzelunternehmen') return 'einzelunternehmen';
  if (normalized === 'ausland') return 'ausland';
  if (normalized === 'freelancer') return 'freelancer';
  if (normalized === 'gruendung') return 'gruendung';
  if (normalized === 'privat') return 'privat';

  return '';
}

function normalizeAdminCustomerType(value) {
  return mapCustomerTypeToAgreementType(value) || 'privat';
}

function splitFullName(value) {
  const fullName = normalizeUtfText(value);
  if (!fullName) {
    return { firstName: '', lastName: '' };
  }

  const parts = fullName.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts.slice(-1).join(''),
  };
}

function getAgreementTypeLabel(value) {
  const normalized = normalizeUtfText(value).toLowerCase();

  const map = {
    privat: 'Private Nutzungsvereinbarung',
    gewerbe: 'Einzelunternehmen-/Gewerbe-Nutzungsvereinbarung',
    einzelunternehmen: 'Einzelunternehmen-/Gewerbe-Nutzungsvereinbarung',
    unternehmen: 'Kapitalgesellschaft-Nutzungsvereinbarung',
    kapitalgesellschaft: 'Kapitalgesellschaft-Nutzungsvereinbarung',
    verein: 'Vereins-Nutzungsvereinbarung',
    ausland: 'Ausländisches Unternehmen-Nutzungsvereinbarung',
    freelancer: 'Freelancer-Nutzungsvereinbarung',
    gruendung: 'Gründung geplant / vorläufige Nutzungsvereinbarung',
  };

  return map[normalized] || (value ? normalizeUtfText(value) : '-');
}

const AGREEMENT_TYPE_OPTIONS = [
  { value: 'privat', label: 'Private Nutzungsvereinbarung' },
  { value: 'freelancer', label: 'Freelancer-Nutzungsvereinbarung' },
  { value: 'einzelunternehmen', label: 'Einzelunternehmen-/Gewerbe-Nutzungsvereinbarung' },
  { value: 'kapitalgesellschaft', label: 'Kapitalgesellschaft-Nutzungsvereinbarung' },
  { value: 'verein', label: 'Vereins-Nutzungsvereinbarung' },
  { value: 'ausland', label: 'Ausländisches Unternehmen-Nutzungsvereinbarung' },
  { value: 'gruendung', label: 'Gründung geplant / vorläufige Nutzungsvereinbarung' },
];

function formatDateOnly(value) {
  if (!value) return '';

  try {
    return new Date(value).toLocaleDateString('de-DE');
  } catch (err) {
    return value;
  }
}

function formatAgreementAddress(data, prefix = '') {
  if (!data) return '';

  const street = normalizeUtfText(data[prefix + 'street']);
  const postalCode = normalizeUtfText(data[prefix + 'postal_code']);
  const city = normalizeUtfText(data[prefix + 'city']);
  const country = normalizeUtfText(data[prefix + 'country']);

  return [street, [postalCode, city].filter(Boolean).join(' '), country]
    .filter(Boolean)
    .join(', ');
}

function hasUsageAgreementData(data) {
  if (!data) return false;

  return [
    data.first_name,
    data.last_name,
    data.birth_date,
    data.street,
    data.postal_code,
    data.city,
    data.country,
    data.company_name,
    data.legal_form,
    data.representative_name,
    data.business_activity,
    data.register_court,
    data.register_number,
    data.tax_number,
    data.vat_id,
    data.planned_foundation_date,
  ].some((value) => normalizeUtfText(value));
}

function hasGeneratedUsageAgreement(ver) {
  const note = normalizeUtfText(ver.admin_note).toLowerCase();

  return (
    note.includes('nutzungsvereinbarung') &&
    (
      note.includes('erstellt') ||
      note.includes('generiert') ||
      note.includes('gespeichert') ||
      note.includes('ersetzt')
    )
  );
}

function buildUsageAgreementCopyText(data, fallbackCustomer = {}) {
  if (!data) return '';

  const fullName = [data.first_name, data.last_name].map(normalizeUtfText).filter(Boolean).join(' ');
  const privateAddress = formatAgreementAddress(data);
  const companyAddress = formatAgreementAddress(data, 'company_');

  const lines = [
    'Nutzungsvereinbarung / Vertragsdaten',
    `Vertragsart: ${getAgreementTypeLabel(data.agreement_type)}`,
    `Name: ${fullName || normalizeUtfText(fallbackCustomer.name) || '-'}`,
    data.birth_date ? `Geburtsdatum: ${formatDateOnly(data.birth_date)}` : '',
    `Adresse: ${privateAddress || '-'}`,
    data.company_name ? `Firma: ${normalizeUtfText(data.company_name)}` : '',
    data.legal_form ? `Rechtsform: ${normalizeUtfText(data.legal_form)}` : '',
    data.representative_name ? `Vertreter / Inhaber / Geschäftsführer: ${normalizeUtfText(data.representative_name)}` : '',
    data.business_activity ? `Tätigkeit / Branche: ${normalizeUtfText(data.business_activity)}` : '',
    companyAddress ? `Unternehmensadresse: ${companyAddress}` : '',
    data.register_court ? `Registergericht: ${normalizeUtfText(data.register_court)}` : '',
    data.register_number ? `Registernummer: ${normalizeUtfText(data.register_number)}` : '',
    data.tax_number ? `Steuernummer: ${normalizeUtfText(data.tax_number)}` : '',
    data.vat_id ? `USt-ID: ${normalizeUtfText(data.vat_id)}` : '',
    data.planned_foundation_date ? `Geplantes Gründungsdatum: ${formatDateOnly(data.planned_foundation_date)}` : '',
  ];

  return lines.filter(Boolean).join('\n');
}

function buildBusinessUsageAgreementDocumentText(data, fallbackCustomer = {}) {
  if (!data) return '';

  const fullName = [data.first_name, data.last_name].map(normalizeUtfText).filter(Boolean).join(' ');
  const customerName = normalizeUtfText(data.company_name) || fullName || normalizeUtfText(fallbackCustomer.name) || '____________________________';
  const currentAddress = formatAgreementAddress(data) || '____________________________';
  const legalForm = normalizeUtfText(data.legal_form) || '____________________________';
  const representative = normalizeUtfText(data.representative_name) || fullName || '____________________________';
  const email = normalizeUtfText(fallbackCustomer.email) || '____________________________';
  const isPlannedFoundation = normalizeUtfText(data.agreement_type).toLowerCase() === 'gruendung';
  const plannedNote = isPlannedFoundation
    ? 'Hinweis: Diese Nutzungsvereinbarung wurde auf Basis der aktuell geplanten Gründungsdaten erstellt. Finale Firmendaten können nach Gründung über den persönlichen Upload-Link nachgereicht und anschließend aktualisiert werden.'
    : '';

  return [
    'YourGermanAddress – A Service by DE Office Solutions',
    'DE Office Solutions – www.yourgermanaddress.com – info@yourgermanaddress.com',
    '',
    'Nutzungsvereinbarung Geschäftsadresse – Gewerbliche Nutzung',
    '',
    '1. Parteien',
    '',
    'Anbieter:',
    'DE Office Solutions',
    'Inhaber: Steven Drieß',
    'Erfweiler Straße 12, 66994 Dahn',
    'E-Mail: info@yourgermanaddress.com',
    '',
    'Kunde:',
    'Name / Firma: ' + customerName,
    '(Der Kunde kann sowohl eine natürliche Person als auch eine juristische Person sein.)',
    'Aktuelle Anschrift: ' + currentAddress,
    'Rechtsform (falls vorhanden): ' + legalForm,
    'Vertreten durch (falls zutreffend): ' + representative,
    'E-Mail: ' + email,
    plannedNote,
    '',
    '2. Vertragsgegenstand',
    '',
    'Der Anbieter gestattet dem Kunden die Nutzung der folgenden Adresse als Geschäftsadresse (ladungsfähige Anschrift):',
    'Erfweiler Straße 12, 66994 Dahn',
    '',
    'Die Nutzung ist im Rahmen der jeweils gebuchten Leistungen zulässig, insbesondere für:',
    '• Gewerbeanmeldung und Ummeldung',
    '• steuerliche Registrierung',
    '• Eintragungen bei Behörden und Registern (z. B. Handelsregister)',
    '• Impressumspflichten (§5 TMG / §18 MStV)',
    '• behördliche und geschäftliche Korrespondenz',
    '',
    'Ein Wohnsitz oder melderechtlicher Haupt- oder Nebenwohnsitz wird ausdrücklich nicht begründet.',
    '',
    '3. Umfang des Nutzungsrechts',
    '',
    'Der Kunde erhält ein einfaches, nicht übertragbares Nutzungsrecht an der Adresse.',
    'Eine Weitergabe oder Untervermietung an Dritte ist nicht gestattet.',
    'An der Adresse finden keine operative Geschäftstätigkeit, kein Kundenverkehr, keine Lagerhaltung und keine Beschäftigung von Mitarbeitern statt.',
    '',
    '4. Nutzung durch Gesellschaften',
    '',
    'Der Kunde ist berechtigt, die Adresse auch für eigene oder von ihm gegründete, gehaltene oder vertretene Gesellschaften (insbesondere UG, GmbH oder vergleichbare in- oder ausländische Gesellschaften) zu nutzen.',
    'In diesem Fall bleibt der Kunde Vertragspartner dieser Vereinbarung, sofern keine gesonderte Vereinbarung mit der jeweiligen Gesellschaft getroffen wird.',
    'Der Kunde stellt sicher, dass alle Angaben zur jeweiligen Gesellschaft korrekt und vollständig sind.',
    '',
    '5. Sitz & Geschäftsleitung',
    '',
    'Die Adresse darf nicht verwendet werden als:',
    '• Ort der Geschäftsleitung',
    '• tatsächlicher Unternehmenssitz',
    '• Ort unternehmerischer Entscheidungen',
    '',
    '6. Betriebsstätte & steuerliche Einordnung',
    '',
    'Die vom Anbieter bereitgestellte Adresse dient ausschließlich als Geschäfts-, Post- und Empfangsadresse.',
    'Eine Nutzung der Adresse als steuerliche Betriebsstätte im Sinne der §§ 10, 12 AO oder als Ort der Geschäftsleitung gemäß § 10 AO ist ausdrücklich ausgeschlossen.',
    'Insbesondere begründet die Nutzung der Adresse keine tatsächliche Geschäftsleitung, keine feste Geschäftseinrichtung und keine operative Tätigkeit am Standort der Adresse.',
    'Der Kunde ist verpflichtet, gegenüber Finanzbehörden und sonstigen Stellen den tatsächlichen Ort der Geschäftsleitung sowie den Ort der unternehmerischen Tätigkeit korrekt anzugeben.',
    'Der Anbieter übernimmt keine Verantwortung für die steuerliche Einordnung des Kunden oder dessen unternehmerischer Tätigkeit.',
    'Die Nutzung der Adresse begründet keine steuerliche Ansässigkeit in Deutschland.',
    '',
    '7. Behördenanfragen',
    '',
    'Der Anbieter ist berechtigt, bei behördlichen Anfragen offenzulegen und entsprechende Auskünfte zu erteilen, dass:',
    '• es sich um eine Geschäftsadresse handelt',
    '• keine operative Tätigkeit vor Ort stattfindet',
    '',
    '8. Verweis auf AGB',
    '',
    'Diese Nutzungsvereinbarung ergänzt die Allgemeinen Geschäftsbedingungen des Anbieters.',
    'Insbesondere gelten die dort geregelten Bestimmungen zu:',
    '• Vertragsabschluss',
    '• Zahlung und Laufzeit',
    '• Nutzungsvoraussetzungen',
    '• Einschränkung oder Beendigung der Nutzung',
    '• Haftung',
    '',
    '9. Online-Vertragsannahme',
    '',
    'Diese Vereinbarung gilt als rechtswirksam angenommen, sobald der Kunde die Nutzungsvereinbarung im Rahmen des Bestellprozesses bestätigt und die Leistung bestellt.',
    '',
    '10. Governing Language',
    '',
    'This agreement is provided in both German and English for convenience. In the event of any discrepancies, inconsistencies, or differences in interpretation between the German and English versions, the German version shall prevail.',
    '',
    'Official Address Usage Confirmation – Business Use',
    '',
    'Purpose of this Document',
    'This document serves as an official confirmation for authorities, banks, platforms, and regulatory institutions.',
    '',
    'Address Status Confirmation',
    'The address provided by DE Office Solutions (Erfweiler Straße 12, 66994 Dahn, Germany) is a physical, serviceable business address suitable for official correspondence, tax communication, and regulatory contact.',
    '',
    'Mail Handling Authorization',
    'DE Office Solutions is authorized to receive official mail, governmental correspondence, and administrative documents on behalf of the Customer within the scope of the booked service plan.',
    '',
    'International Registration Use',
    'This agreement is suitable for use in international company registrations, platform verifications (including but not limited to Amazon, Stripe, PayPal), tax registrations, and compliance procedures where a German service address is required.',
    '',
    'Legal Validity',
    'This confirmation forms an integral part of the main Usage Agreement and is legally binding in connection with the contractual relationship between the Customer and the Provider.',
  ].filter((line) => line !== undefined && line !== null && line !== '').join('\n');
}

function buildPrivateUsageAgreementDocumentText(data, fallbackCustomer = {}) {
  if (!data) return '';

  const fullName = [data.first_name, data.last_name].map(normalizeUtfText).filter(Boolean).join(' ');
  const customerName = fullName || normalizeUtfText(fallbackCustomer.name) || '____________________________';
  const currentAddress = formatAgreementAddress(data) || '____________________________';
  const birthDate = data.birth_date ? formatDateOnly(data.birth_date) : '____________________________';
  const email = normalizeUtfText(fallbackCustomer.email) || '____________________________';

  return [
    'YourGermanAddress – A Service by DE Office Solutions',
    'DE Office Solutions – www.yourgermanaddress.com – info@yourgermanaddress.com',
    '',
    'Nutzungsvereinbarung Postadresse – Private Nutzung',
    '',
    '1. Parteien',
    '',
    'Anbieter:',
    'DE Office Solutions',
    'Inhaber: Steven Drieß',
    'Erfweiler Straße 12, 66994 Dahn',
    'E-Mail: info@yourgermanaddress.com',
    '',
    'Kunde:',
    'Name: ' + customerName,
    'Geburtsdatum: ' + birthDate,
    'Aktuelle Anschrift: ' + currentAddress,
    'E-Mail: ' + email,
    '',
    '2. Vertragsgegenstand',
    '',
    'Der Anbieter gestattet dem Kunden die Nutzung der folgenden Adresse als Post- und Empfangsadresse im Rahmen der jeweils gebuchten privaten Leistungen:',
    'Erfweiler Straße 12, 66994 Dahn',
    '',
    'Ein Wohnsitz oder melderechtlicher Haupt- oder Nebenwohnsitz wird ausdrücklich nicht begründet.',
    'Die Adresse darf nicht als Meldeadresse, Wohnanschrift oder tatsächlicher Aufenthaltsort verwendet werden.',
    '',
    '3. Umfang des Nutzungsrechts',
    '',
    'Der Kunde erhält ein einfaches, nicht übertragbares Nutzungsrecht an der Adresse im Umfang des gebuchten Tarifs.',
    'Eine Weitergabe, Untervermietung oder Nutzung durch Dritte ist nicht gestattet.',
    'Die Nutzung für gewerbliche Zwecke, Gesellschaften, Handelsregister, Gewerbeanmeldung, steuerliche Registrierung oder Impressumspflichten ist nicht Bestandteil dieser privaten Nutzungsvereinbarung, sofern hierfür kein entsprechender Geschäftstarif gebucht wurde.',
    '',
    '4. Postempfang und Bearbeitung',
    '',
    'Der Anbieter ist berechtigt, Postsendungen im Rahmen des gebuchten Leistungsumfangs entgegenzunehmen und entsprechend der gebuchten Leistung zu bearbeiten.',
    'Nicht vom Tarif umfasste Leistungen, insbesondere zusätzliche Empfänger, außergewöhnlich hohes Postaufkommen, Weiterleitungen oder Sonderbearbeitungen, können gesondert berechnet oder abgelehnt werden.',
    '',
    '5. Behördenanfragen',
    '',
    'Der Anbieter ist berechtigt, bei behördlichen oder rechtlich erforderlichen Anfragen offenzulegen und entsprechende Auskünfte zu erteilen, dass es sich um eine private Post- und Empfangsadresse handelt und kein Wohnsitz an der Adresse begründet wird.',
    '',
    '6. Verweis auf AGB',
    '',
    'Diese Nutzungsvereinbarung ergänzt die Allgemeinen Geschäftsbedingungen des Anbieters.',
    'Insbesondere gelten die dort geregelten Bestimmungen zu Vertragsabschluss, Zahlung und Laufzeit, Nutzungsvoraussetzungen, Einschränkung oder Beendigung der Nutzung sowie Haftung.',
    '',
    '7. Online-Vertragsannahme',
    '',
    'Diese Vereinbarung gilt als rechtswirksam angenommen, sobald der Kunde die Nutzungsvereinbarung im Rahmen des Bestellprozesses bestätigt und die Leistung bestellt.',
    '',
    'Private Address Usage Confirmation',
    '',
    'Purpose of this Document',
    'This document serves as a confirmation that the customer may use the address provided by DE Office Solutions as a private mailing and receiving address within the scope of the booked service plan.',
    '',
    'Address Status Confirmation',
    'The address provided by DE Office Solutions (Erfweiler Straße 12, 66994 Dahn, Germany) is a physical mailing and receiving address. It does not constitute a residential address or registered place of residence.',
    '',
    'Mail Handling Authorization',
    'DE Office Solutions is authorized to receive and process mail on behalf of the customer within the scope of the booked private service plan.',
    '',
    'Legal Validity',
    'This confirmation forms an integral part of the main Usage Agreement and is legally binding in connection with the contractual relationship between the Customer and the Provider.',
  ].join('\n');
}

function isBusinessCustomer(value) {
  const normalized = normalizeUtfText(value).toLowerCase();
  return ['unternehmen', 'gewerbe', 'einzelunternehmen', 'kapitalgesellschaft', 'verein', 'ausland', 'gruendung'].includes(normalized);
}

function isIdentityLocked(status) {
  const normalized = normalizeUtfText(status).toLowerCase();
  return normalized === 'approved' || normalized === 'already_verified' || isReversedStatus(normalized);
}

function isBusinessLocked(status) {
  const normalized = normalizeUtfText(status).toLowerCase();
  return normalized === 'approved' || normalized === 'already_verified' || normalized === 'not_required' || isReversedStatus(normalized);
}

function formatDateTime(value) {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleString('de-DE');
  } catch (err) {
    return value;
  }
}

const NOTIFICATION_STATE_LABELS = Object.freeze({
  critical: 'Kritisch',
  open: 'Offene Maßnahme',
  confirmed: 'Bestätigt',
  historical: 'Historischer Hinweis',
});

const NOTIFICATION_SOURCE_LABELS = Object.freeze({
  critical: 'Handlung erforderlich',
  confirmed: 'Bestätigt',
  external: 'Extern überwacht',
  unavailable: 'Nicht eingerichtet',
});

function getPortalFieldLabel(fieldName) {
  return PORTAL_REQUEST_FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' ');
}

function getPortalSnapshotValue(request, fieldName) {
  const customer = request.current_snapshot.customer || {};
  const display = request.current_snapshot.display_profile || {};
  const usage = request.current_snapshot.usage_agreement_data || {};

  if (Object.prototype.hasOwnProperty.call(customer, fieldName)) return customer[fieldName];
  if (Object.prototype.hasOwnProperty.call(display, fieldName)) return display[fieldName];
  if (Object.prototype.hasOwnProperty.call(usage, fieldName)) return usage[fieldName];

  return undefined;
}

function formatPortalValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

function formatFileSize(value) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function getSearchValue(value) {
  return normalizeUtfText(value).toLowerCase();
}

function verificationMatchesSearchTerm(ver, normalizedSearchTerm) {
  if (!normalizedSearchTerm) {
    return true;
  }

  const searchHaystack = [
    ver.id,
    ver.customers.name,
    ver.customers.email,
    ver.customers.company_name,
    ver.customers.customer_type,
    ver.orders.product_name,
    ver.orders.shopify_order_id,
    ver.orders.shopify_order_name,
  ]
    .map((item) => getSearchValue(item))
    .join(' ');

  return searchHaystack.includes(normalizedSearchTerm);
}

function verificationMatchesQuickFilter(ver, filterKey) {
  const identity = normalizeUtfText(ver.identity_status).toLowerCase();
  const business = normalizeUtfText(ver.business_status).toLowerCase();
  const customerType = normalizeUtfText(ver.customers.customer_type).toLowerCase();
  const businessRelevant = ['unternehmen', 'gewerbe', 'einzelunternehmen', 'kapitalgesellschaft', 'verein', 'ausland', 'gruendung'].includes(customerType);
  const identityDone = identity === 'approved' || identity === 'already_verified';
  const businessDone =
    !businessRelevant ||
    business === 'approved' ||
    business === 'already_verified' ||
    business === 'not_required';
  const reversed = isReversedStatus(identity) || isReversedStatus(business);

  switch (filterKey) {
    case 'open':
      return !reversed && !(identityDone && businessDone);
    case 'new':
      return !reversed && ['new', 'submitted', 'in_review'].includes(identity);
    case 'verified':
      return identityDone && businessDone;
    case 'followup':
      return !reversed && (
        identity === 'additional_requested' ||
        (businessRelevant && ['pending', 'submitted', 'in_review', 'additional_requested'].includes(business)) ||
        !!ver.followup_request.requested_documents.length
      );
    case 'reversal':
      return !reversed && (isReversalRequiredStatus(identity) || isVerificationPastReversalDeadline(ver));
    case 'manual_review':
      return !reversed && (identity === 'submitted' || identity === 'in_review' || business === 'in_review');
    default:
      return true;
  }
}

function orderMatchesQuickFilter(order, filterKey) {
  const verificationStatus = normalizeUtfText(order.verification_status).toLowerCase();
  const needsManualReview =
    !!order.manual_review_required ||
    ['pending_manual_review', 'awaiting_manual_review'].includes(verificationStatus);
  const isVerified = ['already_verified', 'existing_verification_reused'].includes(verificationStatus);
  const isOpen = !isVerified && verificationStatus !== 'no_verification_required';
  const isNew =
    Number(order.customer_order_index) <= 1 ||
    ['auto_verification_eligible', 'manually_requested', 'auto_verification_created'].includes(
      verificationStatus
    );
  const hasFollowup =
    verificationStatus === 'open_verification_exists' ||
    verificationStatus === 'existing_customer_open_verification' ||
    verificationStatus === 'company_data_pending';

  switch (filterKey) {
    case 'open':
      return isOpen;
    case 'new':
      return isNew;
    case 'verified':
      return isVerified;
    case 'followup':
      return hasFollowup;
    case 'manual_review':
      return needsManualReview;
    default:
      return true;
  }
}

function getLogisticsStatusMeta(status) {
  const value = normalizeUtfText(status).toLowerCase();

  const map = {
    new: { label: 'Neu', tone: 'neutral' },
    form_submitted: { label: 'Formular eingereicht', tone: 'info' },
    expected: { label: 'Erwartet', tone: 'warning' },
    received: { label: 'Eingegangen', tone: 'info' },
    shipping_costs_open: { label: 'Versandkosten offen', tone: 'warning' },
    paid: { label: 'Bezahlt', tone: 'success' },
    shipped: { label: 'Versendet', tone: 'info' },
    completed: { label: 'Abgeschlossen', tone: 'success' },
    cancelled: { label: 'Storniert', tone: 'danger' },
  };

  return map[value] || { label: status || '-', tone: 'neutral' };
}

function getLogisticsServiceLabel(serviceType, packageWeightClass) {
  if (serviceType === 'parcel_forwarding') {
    const weightMap = {
      bis_5kg: 'Paketweiterleitung bis 5 kg',
      bis_10kg: 'Paketweiterleitung bis 10 kg',
      bis_20kg: 'Paketweiterleitung bis 20 kg',
    };

    return weightMap[packageWeightClass] || 'Paketweiterleitung';
  }

  if (serviceType === 'physical_collective_shipping') {
    return 'Physischer Sammelversand';
  }

  return serviceType || '-';
}

function StatusBadge({ label, tone = 'neutral' }) {
  return <span className={`status-pill status-pill--${tone}`}>{label}</span>;
}

function getAdminRedirectPath() {
  if (typeof window === 'undefined') return '/admin/dashboard';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function buildAdminLoginPath() {
  return `/admin/login?redirect=${encodeURIComponent(getAdminRedirectPath())}`;
}

function isAuthFailureResponse(response) {
  return response.status === 401 || response.status === 403;
}

function MobileInfoRow({ label, children }) {
  return (
    <div className="mobile-info-row">
      <span className="mobile-info-label">{label}</span>
      <div className="mobile-info-value">{children}</div>
    </div>
  );
}

function ScanLetterheadThumbnail({ scan, token }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [pdfRendered, setPdfRendered] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || shouldLoad) return undefined;
    if (typeof window.IntersectionObserver !== 'function') {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new window.IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setShouldLoad(true);
      observer.disconnect();
    }, { rootMargin: '160px 0px' });
    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad || !token || !scan?.preview_url) return undefined;
    let active = true;
    let objectUrl = '';
    let pdfDocument;
    setLoadFailed(false);
    setPdfRendered(false);

    const requestUrl = scan.preview_url.startsWith('/api/')
      ? scan.preview_url
      : `${API_URL}${scan.preview_url}`;
    fetch(requestUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        if (!response.ok) throw new Error('Briefkopf-Vorschau konnte nicht geladen werden.');
        return response.blob();
      })
      .then(async (blob) => {
        if (!active) return;
        if (/^image\//i.test(blob.type) || /^image\//i.test(scan?.mime_type || '')) {
          objectUrl = URL.createObjectURL(blob);
          setThumbnailUrl(objectUrl);
          return;
        }

        pdfDocument = await getDocument({ data: await blob.arrayBuffer() }).promise;
        const page = await pdfDocument.getPage(1);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = canvasRef.current;
        if (!active || !canvas) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        if (active) setPdfRendered(true);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (pdfDocument) pdfDocument.destroy();
    };
  }, [scan?.mime_type, scan?.preview_url, shouldLoad, token]);

  const previewReady = Boolean(thumbnailUrl || pdfRendered);

  return (
    <div
      ref={containerRef}
      className={`scan-letterhead-thumbnail ${loadFailed ? 'is-fallback' : ''}`}
      aria-label={`Briefkopf-Vorschau ${normalizeUtfText(scan?.filename)}`}
      role="img"
    >
      {thumbnailUrl ? (
        <img src={thumbnailUrl} alt="" loading="lazy" />
      ) : null}
      <canvas
        ref={canvasRef}
        className={pdfRendered ? '' : 'is-loading'}
        title={`Briefkopf ${normalizeUtfText(scan?.filename)}`}
        aria-hidden="true"
      />
      {!previewReady ? (
        <span>{loadFailed ? 'Vorschau nicht verfügbar' : 'Briefkopf wird geladen …'}</span>
      ) : null}
    </div>
  );
}

function ScanInboxSection({ token, onRefreshVerification, onScanCountChange, onBack }) {
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [selectedScanIds, setSelectedScanIds] = useState(() => new Set());
  const [previewUrl, setPreviewUrl] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [linkFolderPath, setLinkFolderPath] = useState('');
  const [linkInboxPath, setLinkInboxPath] = useState('');
  const [linkingCustomerId, setLinkingCustomerId] = useState('');
  const [loadingScans, setLoadingScans] = useState(false);
  const [workingScanId, setWorkingScanId] = useState('');
  const [scanMessage, setScanMessage] = useState('');
  const [scanError, setScanError] = useState('');
  const [officialAuthorityMail, setOfficialAuthorityMail] = useState(false);
  const [officialSenderName, setOfficialSenderName] = useState('');
  const [officialPageCount, setOfficialPageCount] = useState('');
  const [inactiveContactOpen, setInactiveContactOpen] = useState(false);
  const [inactiveContactStage, setInactiveContactStage] = useState('friendly');
  const [inactiveContactPreview, setInactiveContactPreview] = useState(null);
  const [inactiveContactLoading, setInactiveContactLoading] = useState(false);
  const [inactiveContactError, setInactiveContactError] = useState('');
  const [confirmStrictContact, setConfirmStrictContact] = useState(false);
  const [confirmRecentContact, setConfirmRecentContact] = useState(false);
  const [skippedScanIds, setSkippedScanIds] = useState(() => new Set());
  const [queueRoundComplete, setQueueRoundComplete] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [highlightedCustomerIndex, setHighlightedCustomerIndex] = useState(0);
  const searchTimerRef = useRef(null);
  const customerSearchRef = useRef(null);

  const activeScans = useMemo(
    () => filterActiveScans(scans, skippedScanIds),
    [scans, skippedScanIds]
  );

  const loadScans = useCallback(async (preferredScanId = null) => {
    if (!token) return;
    setLoadingScans(true);
    setScanError('');

    try {
      const response = await fetch(`${API_URL}/admin/scans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Scans konnten nicht geladen werden.');
      }

      setScans(data || []);
      onScanCountChange?.((data || []).length);
      setSkippedScanIds((current) => new Set(
        [...current].filter((scanId) => (data || []).some((scan) => scan.id === scanId))
      ));
      setSelectedScanIds((current) => new Set(
        [...current].filter((scanId) => (data || []).some((scan) => scan.id === scanId))
      ));
      setSelectedScan((current) => {
        if (preferredScanId) {
          const preferred = (data || []).find((scan) => scan.id === preferredScanId);
          if (preferred) return preferred;
        }
        if (current && (data || []).some((scan) => scan.id === current.id)) {
          return current;
        }
        return (data || [])[0] || null;
      });
    } catch (err) {
      console.error(err);
      setScanError(normalizeUtfText(err.message || 'Scans konnten nicht geladen werden.'));
    } finally {
      setLoadingScans(false);
    }
  }, [onScanCountChange, token]);

  useEffect(() => {
    loadScans();
  }, [loadScans]);

  useEffect(() => {
    if (selectedScan && activeScans.some((scan) => scan.id === selectedScan.id)) return;
    setSelectedScan(activeScans[0] || null);
  }, [activeScans, selectedScan]);

  useEffect(() => {
    if (!selectedScan || !token) {
      setPreviewUrl('');
      return undefined;
    }

    let active = true;
    let objectUrl = '';

    const previewRequestUrl = selectedScan.preview_url.startsWith('/api/')
      ? selectedScan.preview_url
      : `${API_URL}${selectedScan.preview_url}`;

    fetch(previewRequestUrl, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Vorschau konnte nicht geladen werden.');
        }
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch((err) => {
        console.error(err);
        if (active) {
          setPreviewUrl('');
          setScanError(normalizeUtfText(err.message || 'Vorschau konnte nicht geladen werden.'));
        }
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedScan, token]);

  useEffect(() => {
    window.clearTimeout(searchTimerRef.current);
    setSelectedCustomer(null);

    const query = customerQuery.trim();
    if (query.length < 2 || !token) {
      setCustomerSuggestions([]);
      return undefined;
    }

    searchTimerRef.current = window.setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/admin/customers/search?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Kundensuche konnte nicht ausgeführt werden.');
        }

        setCustomerSuggestions(data || []);
      } catch (err) {
        console.error(err);
        setScanError(normalizeUtfText(err.message || 'Kundensuche konnte nicht ausgeführt werden.'));
      }
    }, 250);

    return () => window.clearTimeout(searchTimerRef.current);
  }, [customerQuery, token]);

  useEffect(() => {
    setHighlightedCustomerIndex(0);
  }, [customerSuggestions]);

  useEffect(() => {
    if (assignmentOpen && !selectedCustomer) customerSearchRef.current?.focus();
  }, [assignmentOpen, selectedCustomer]);

  useEffect(() => {
    setLinkFolderPath(selectedCustomer?.nextcloud_folder_path || '');
    setLinkInboxPath(selectedCustomer?.nextcloud_inbox_path || '');
  }, [selectedCustomer]);

  const readActionResponse = async (response, fallbackMessage) => {
    const responseText = await response.text();
    let data = {};
    if (responseText) {
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        const looksLikeHtml = /^\s*<!doctype html/i.test(responseText) || /^\s*<html/i.test(responseText);
        data = { error: looksLikeHtml ? fallbackMessage : responseText };
      }
    }

    if (!response.ok) {
      throw new Error(data.error || fallbackMessage);
    }

    return data;
  };

  const linkSelectedCustomerNextcloud = async () => {
    if (!selectedCustomer || !token) return;

    setLinkingCustomerId(selectedCustomer.id);
    setScanError('');
    setScanMessage('');

    try {
      const response = await fetch(`${API_URL}/admin/customers/${selectedCustomer.id}/nextcloud-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nextcloud_folder_path: linkFolderPath,
          nextcloud_inbox_path: linkInboxPath,
        }),
      });
      const data = await readActionResponse(response, 'Nextcloud-Verknüpfung konnte nicht gespeichert werden.');
      const linkedCustomer = {
        ...selectedCustomer,
        nextcloud_folder_path: data.customer.nextcloud_folder_path || '',
        nextcloud_inbox_path: data.customer.nextcloud_inbox_path || '',
        nextcloud_exists: !!(data.customer.nextcloud_folder_path || data.customer.nextcloud_inbox_path),
        nextcloud_missing_warning: data.customer.nextcloud_inbox_path
          ? null
          : selectedCustomer.nextcloud_missing_warning,
      };

      setSelectedCustomer(linkedCustomer);
      setCustomerSuggestions((current) => current.map((customer) => (
        customer.id === linkedCustomer.id ? { ...customer, ...linkedCustomer } : customer
      )));
      setScanMessage(normalizeUtfText(data.message || 'Nextcloud-Posteingang wurde verknüpft.'));
    } catch (err) {
      console.error(err);
      setScanError(normalizeUtfText(err.message || 'Nextcloud-Verknüpfung konnte nicht gespeichert werden.'));
    } finally {
      setLinkingCustomerId('');
    }
  };

  const pendingScanAssignments = useRef(new Map());
  const runScanAction = async (action) => {
    if (!selectedScan || !token) return;
    if (action === 'assign' && !selectedCustomer) return;
    if (action === 'delete') {
      const confirmed = window.confirm('Diesen Fehlscan wirklich dauerhaft löschen');
      if (!confirmed) return;
    }

    const assignmentScanIds = action === 'assign' && selectedScanIds.size > 0
      ? activeScans.filter((scan) => selectedScanIds.has(scan.id)).map((scan) => scan.id)
      : [selectedScan.id];
    setWorkingScanId(assignmentScanIds.length > 1 ? 'bulk' : selectedScan.id);
    setScanError('');
    setScanMessage('');

    try {
      const endpointFor = (scanId) => action === 'assign'
        ? `/admin/scans/${scanId}/assign`
        : action === 'ad'
          ? `/admin/scans/${scanId}/mark-ad`
          : action === 'delete'
            ? `/admin/scans/${scanId}`
            : `/admin/scans/${scanId}/defer`;

      const results = [];
      const failures = [];
      for (const scanId of assignmentScanIds) {
        try {
          if (action === 'assign') {
            const data = await assignScanWithRecovery({ apiUrl: API_URL, token, scanId,
              pending: pendingScanAssignments.current,
              onChecking: () => setScanMessage('Antwort verzögert. Der Verarbeitungsstand wird geprüft – bitte nicht erneut zuordnen.'),
              payload: { customer_id: selectedCustomer.id, send_notification: false,
                official_authority_mail: officialAuthorityMail,
                official_sender_name: officialAuthorityMail ? officialSenderName : undefined,
                official_page_count: officialAuthorityMail && officialPageCount ? Number(officialPageCount) : undefined } });
            results.push({ scanId, data });
            continue;
          }
          const response = await fetch(`${API_URL}${endpointFor(scanId)}`, {
            method: action === 'delete' ? 'DELETE' : 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: action === 'assign'
              ? JSON.stringify({
                  customer_id: selectedCustomer.id,
                  send_notification: false,
                  official_authority_mail: officialAuthorityMail,
                  official_sender_name: officialAuthorityMail ? officialSenderName : undefined,
                  official_page_count: officialAuthorityMail && officialPageCount ? Number(officialPageCount) : undefined,
                })
              : undefined,
          });
          const data = await readActionResponse(response, 'Scan-Aktion konnte nicht ausgeführt werden. Bitte Backend-Logs prüfen.');
          results.push({ scanId, data });
        } catch (err) {
          failures.push({ scanId, error: err });
        }
      }
      if (results.length === 0) throw failures[0]?.error || new Error('Kein Scan konnte zugeordnet werden.');
      const data = results[results.length - 1].data || {};
      const completedScanIds = new Set(results.map((result) => result.scanId));
      const nextScanId = failures[0]?.scanId
        || getNextScanAfterRemoval(activeScans, selectedScan.id, completedScanIds);

      setScanMessage(action === 'assign' && results.length > 1
        ? `${results.length} Scans wurden demselben Kunden zugeordnet.`
        : normalizeUtfText(data.message || 'Scan-Aktion wurde ausgeführt.'));
      if (failures.length > 0) {
        setScanError(`${failures.length} Vorgang/Vorgänge nicht bestätigt. Der erste offene Scan bleibt ausgewählt. ${failures[0].error.message}`);
      }
      if (failures.length > 0) {
        setSelectedScanIds(new Set(failures.map((failure) => failure.scanId)));
        setAssignmentOpen(true);
      } else {
        setCustomerQuery('');
        setCustomerSuggestions([]);
        setSelectedCustomer(null);
        setOfficialAuthorityMail(false);
        setOfficialSenderName('');
        setOfficialPageCount('');
        setAssignmentOpen(false);
        setSelectedScanIds(new Set());
      }
      if (action === 'defer') {
        const nextRound = skipScanInRound(scans, skippedScanIds, selectedScan.id);
        setSkippedScanIds(nextRound.skippedScanIds);
        setQueueRoundComplete(nextRound.roundComplete);
      } else {
        if (completedScanIds.size >= activeScans.length && failures.length === 0) {
          setQueueRoundComplete(true);
        }
        await loadScans(nextScanId);
      }
      if (action === 'assign' && onRefreshVerification) {
        const verificationIds = [...new Set(results.map((result) => result.data?.assignment?.verification_id).filter(Boolean))];
        for (const verificationId of verificationIds) {
          await onRefreshVerification(verificationId);
        }
      }
    } catch (err) {
      console.error(err);
      setScanError(normalizeUtfText(err.message || 'Scan-Aktion konnte nicht ausgeführt werden.'));
      setScanMessage('');
    } finally {
      setWorkingScanId('');
    }
  };

  const previewInactiveContact = async () => {
    if (!selectedCustomer || !token) return;
    setInactiveContactLoading(true);
    setInactiveContactError('');
    setInactiveContactPreview(null);
    setConfirmStrictContact(false);
    setConfirmRecentContact(false);
    try {
      const response = await fetch(`${API_URL}/admin/customers/${selectedCustomer.id}/inactive-subscription-contact/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stage: inactiveContactStage }),
      });
      const data = await readActionResponse(response, 'Nachrichtenvorschau konnte nicht erstellt werden.');
      setInactiveContactPreview(data);
    } catch (err) {
      setInactiveContactError(normalizeUtfText(err.message || 'Nachrichtenvorschau konnte nicht erstellt werden.'));
    } finally {
      setInactiveContactLoading(false);
    }
  };

  const sendInactiveContact = async () => {
    if (!selectedCustomer || !inactiveContactPreview || !token) return;
    setInactiveContactLoading(true);
    setInactiveContactError('');
    try {
      const response = await fetch(`${API_URL}/admin/customers/${selectedCustomer.id}/inactive-subscription-contact/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          stage: inactiveContactStage,
          confirm_strict: confirmStrictContact,
          confirm_recent_send: confirmRecentContact,
        }),
      });
      const data = await readActionResponse(response, 'Nachricht konnte nicht versendet werden.');
      setScanMessage(normalizeUtfText(data.message || 'Nachricht wurde versendet.'));
      setInactiveContactOpen(false);
      setInactiveContactPreview(null);
    } catch (err) {
      setInactiveContactError(normalizeUtfText(err.message || 'Nachricht konnte nicht versendet werden.'));
    } finally {
      setInactiveContactLoading(false);
    }
  };

  return (
    <section className="card admin-section-card scan-focus-card">
      <div className="scan-mobile-toolbar">
        <button type="button" onClick={onBack}>← Übersicht</button>
        <strong>Scans zuordnen</strong>
        <button type="button" disabled={loadingScans} onClick={() => {
          setSkippedScanIds(new Set());
          setQueueRoundComplete(false);
          loadScans();
        }}>{loadingScans ? 'Lädt …' : '↻'}</button>
      </div>
      <div className="admin-section-header scan-desktop-header">
        <div>
          <h2>Neue Scans</h2>
          <p className="subtitle">
            Zentraler Posteingang mit manueller Vorschau und bewusster Kundenzuordnung.
          </p>
        </div>
        <button type="button" className="action-button action-button--secondary" onClick={() => {
          setSkippedScanIds(new Set());
          setQueueRoundComplete(false);
          loadScans();
        }}>
          {loadingScans ? 'Lädt...' : 'Scans aktualisieren'}
        </button>
      </div>

      {scanError ? <div className="hint-box">{scanError}</div> : null}
      {scanMessage ? <div className="success-box">{scanMessage}</div> : null}

      {queueRoundComplete ? (
        <div className="scan-queue-complete" role="status">
          <strong>{scans.length > 0 ? 'Ende dieses Durchgangs' : 'Alles erledigt'}</strong>
          <span>{scans.length > 0
            ? 'Alle noch vorhandenen Scans wurden übersprungen. Sie können jetzt wieder von vorne geprüft werden.'
            : 'Im Scan-Eingang sind aktuell keine unbearbeiteten Scans mehr vorhanden.'}</span>
          {scans.length > 0 ? <button
            type="button"
            className="action-button action-button--secondary"
            onClick={() => {
              setSkippedScanIds(new Set());
              setQueueRoundComplete(false);
              setSelectedScan(scans[0] || null);
            }}
          >
            Übersprungene Scans erneut ansehen
          </button> : null}
        </div>
      ) : null}

      <div className="scan-workspace">
        <div className="scan-list">
          {activeScans.length === 0 ? (
            <p className="meta-text">Keine stabilen Scans im Eingang gefunden.</p>
          ) : activeScans.map((scan) => (
            <div
              key={scan.id}
              className={`scan-list-item ${selectedScan?.id === scan.id ? 'is-active' : ''} ${selectedScanIds.has(scan.id) ? 'is-checked' : ''}`}
            >
              <label className="scan-bulk-checkbox" title="Für gemeinsame Zuordnung auswählen">
                <input
                  type="checkbox"
                  checked={selectedScanIds.has(scan.id)}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setSelectedScanIds((current) => {
                      const next = new Set(current);
                      if (checked) next.add(scan.id); else next.delete(scan.id);
                      return next;
                    });
                    if (checked) setSelectedScan(scan);
                  }}
                  aria-label={`${normalizeUtfText(scan.filename)} für gemeinsame Zuordnung auswählen`}
                />
              </label>
              <ScanLetterheadThumbnail scan={scan} token={token} />
              <button type="button" className="scan-list-item__preview" onClick={() => setSelectedScan(scan)}>
                <strong>{normalizeUtfText(scan.filename)}</strong>
                <span>{formatFileSize(scan.size)} · {formatDateTime(scan.modified_at)}</span>
              </button>
            </div>
          ))}
        </div>

        <div className="scan-preview-panel">
          {selectedScan ? (
            <>
              <div className="scan-queue-progress">
                <strong>Aktueller Scan</strong>
                <span>Scan {Math.max(1, activeScans.findIndex((scan) => scan.id === selectedScan.id) + 1)} von {activeScans.length}</span>
              </div>
              {selectedScanIds.size > 0 ? (
                <div className="scan-bulk-summary" role="status">
                  {selectedScanIds.size} {selectedScanIds.size === 1 ? 'Scan ausgewählt' : 'Scans ausgewählt'} – die Zuordnung gilt gemeinsam für diese Auswahl.
                </div>
              ) : null}
              <div className="scan-preview">
                {previewUrl ? (
                  <iframe title={`Scan ${selectedScan.filename}`} src={previewUrl} />
                ) : (
                  <p className="meta-text">Vorschau wird geladen...</p>
                )}
              </div>
              <p className="meta-text">{normalizeUtfText(selectedScan.filename)}</p>
              {previewUrl ? (
                <a className="action-button action-button--secondary scan-open-pdf" href={previewUrl} target="_blank" rel="noreferrer">
                  PDF vollständig öffnen
                </a>
              ) : null}
            </>
          ) : (
            <p className="meta-text">Scan auswählen.</p>
          )}
        </div>

        <div className="scan-assignment-panel">
          {!assignmentOpen && !selectedCustomer ? (
            <button type="button" className="action-button action-button--success" disabled={!selectedScan} onClick={() => setAssignmentOpen(true)}>
              Zuordnen
            </button>
          ) : null}

          {assignmentOpen && !selectedCustomer ? <label>
            Kundensuche
            <input
              ref={customerSearchRef}
              type="search"
              value={customerQuery}
              onChange={(event) => setCustomerQuery(event.target.value)}
              placeholder="Name, Firma, E-Mail, Bestellung oder Briefkastenanzeige"
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setHighlightedCustomerIndex((current) => customerSuggestions.length
                    ? Math.min(current + 1, customerSuggestions.length - 1)
                    : 0);
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  setHighlightedCustomerIndex((current) => Math.max(current - 1, 0));
                } else if (event.key === 'Enter' && customerSuggestions[highlightedCustomerIndex]) {
                  event.preventDefault();
                  setSelectedCustomer(customerSuggestions[highlightedCustomerIndex]);
                  setScanError('');
                } else if (event.key === 'Escape') {
                  setAssignmentOpen(false);
                  setCustomerQuery('');
                  setCustomerSuggestions([]);
                }
              }}
            />
          </label> : null}

          {assignmentOpen && !selectedCustomer ? <div className="customer-suggestions">
            {customerSuggestions.map((customer, index) => (
              <button
                type="button"
                key={customer.id}
                className={`customer-suggestion ${index === highlightedCustomerIndex ? 'is-active' : ''}`}
                onMouseEnter={() => setHighlightedCustomerIndex(index)}
                onClick={() => {
                  setSelectedCustomer(customer);
                  setScanError('');
                }}
              >
                <strong>{normalizeUtfText(customer.display_name)}</strong>
                <span>{normalizeUtfText(customer.name) || '-'} · {normalizeUtfText(customer.email) || '-'}</span>
                <small>{normalizeUtfText(customer.package) || 'Paket unbekannt'} · {normalizeUtfText(customer.payment_status) || 'Zahlung unbekannt'}</small>
                {customer.mailbox_display_name ? (
                  <small>Briefkastenanzeige: {normalizeUtfText(customer.mailbox_display_name)}</small>
                ) : null}
                {customer.shopify_order_name || customer.shopify_order_number ? (
                  <small>Shopify: {normalizeUtfText(customer.shopify_order_name || customer.shopify_order_number)}</small>
                ) : null}
                {customer.subscription_provider ? (
                  <small>Abo: {customer.subscription_provider === 'stripe' ? 'Stripe' : 'Shopify / Seal'} · {normalizeUtfText(customer.subscription_status) || 'Status unbekannt'}</small>
                ) : null}
                {customer.nextcloud_folder_path ? (
                  <small>Nextcloud: {normalizeUtfText(customer.nextcloud_folder_path)}</small>
                ) : null}
                {customer.nextcloud_inbox_path ? (
                  <small>Posteingang: {normalizeUtfText(customer.nextcloud_inbox_path)}</small>
                ) : (
                  <small className="scan-warning-text">
                    {normalizeUtfText(customer.nextcloud_missing_warning) || 'Kein Nextcloud-Posteingang verknüpft.'}
                  </small>
                )}
                <StatusBadge
                  label={customer.status?.label || 'Prüfen'}
                  tone={customer.status?.severity === 'success' ? 'success' : customer.status?.severity === 'error' ? 'danger' : 'warning'}
                />
                {customer.status?.warnings?.length ? (
                  <ul>
                    {customer.status.warnings.map((warning) => (
                      <li key={warning}>{normalizeUtfText(warning)}</li>
                    ))}
                  </ul>
                ) : null}
              </button>
            ))}
          </div> : null}

          {selectedCustomer ? (
            <div className="scan-selected-customer">
              <div>
                <strong>{normalizeUtfText(selectedCustomer.display_name)}</strong>
                <span>{normalizeUtfText(selectedCustomer.email) || 'Keine E-Mail'}</span>
              </div>
              <StatusBadge
                label={selectedCustomer.status?.label || 'Prüfen'}
                tone={selectedCustomer.status?.severity === 'success' ? 'success' : selectedCustomer.status?.severity === 'error' ? 'danger' : 'warning'}
              />
              {selectedCustomer.status?.warnings?.length ? (
                <small className="scan-warning-text">{normalizeUtfText(selectedCustomer.status.warnings[0])}</small>
              ) : null}
              <button type="button" className="scan-change-customer" onClick={() => {
                setSelectedCustomer(null);
                setCustomerQuery('');
                setCustomerSuggestions([]);
              }}>
                Anderes Konto
              </button>
            </div>
          ) : null}

          {selectedCustomer && !selectedCustomer.nextcloud_inbox_path ? (
            <div className="nextcloud-link-panel">
              <label>
                Nextcloud-Kundenordner
                <input
                  type="text"
                  value={linkFolderPath}
                  onChange={(event) => {
                    const value = event.target.value;
                    setLinkFolderPath(value);
                    if (!linkInboxPath || linkInboxPath === `${linkFolderPath}/Posteingang`) {
                      setLinkInboxPath(value ? `${value.replace(/\/+$/, '')}/Posteingang` : '');
                    }
                  }}
                  placeholder="Kunden/Beispiel GmbH"
                />
              </label>
              <label>
                Nextcloud-Posteingang
                <input
                  type="text"
                  value={linkInboxPath}
                  onChange={(event) => setLinkInboxPath(event.target.value)}
                  placeholder="Kunden/Beispiel GmbH/Posteingang"
                />
              </label>
              <button
                type="button"
                className="action-button action-button--secondary"
                disabled={!linkFolderPath.trim() || !linkInboxPath.trim() || linkingCustomerId === selectedCustomer.id}
                onClick={linkSelectedCustomerNextcloud}
              >
                {linkingCustomerId === selectedCustomer.id ? 'Prüfe...' : 'Nextcloud-Ordner verknüpfen/ändern'}
              </button>
            </div>
          ) : null}

          {selectedCustomer && selectedCustomer.nextcloud_inbox_path ? (
            <details className="scan-advanced-options">
              <summary>Weitere Optionen</summary>
              <div className="nextcloud-link-panel">
                <label>
                  Nextcloud-Kundenordner
                  <input type="text" value={linkFolderPath} onChange={(event) => setLinkFolderPath(event.target.value)} />
                </label>
                <label>
                  Nextcloud-Posteingang
                  <input type="text" value={linkInboxPath} onChange={(event) => setLinkInboxPath(event.target.value)} />
                </label>
                <button
                  type="button"
                  className="action-button action-button--secondary"
                  disabled={!linkFolderPath.trim() || !linkInboxPath.trim() || linkingCustomerId === selectedCustomer.id}
                  onClick={linkSelectedCustomerNextcloud}
                >
                  {linkingCustomerId === selectedCustomer.id ? 'Prüfe...' : 'Ordner ändern'}
                </button>
              </div>
              <button
                type="button"
                className="action-button action-button--secondary"
                onClick={() => {
                  setInactiveContactOpen(true);
                  setInactiveContactPreview(null);
                  setInactiveContactError('');
                }}
              >
                Kunden wegen inaktivem Abo kontaktieren
              </button>
            </details>
          ) : null}

          {selectedCustomer ? (
            <div className="nextcloud-link-panel">
              <label className="recipient-checkbox">
                <input type="checkbox" checked={officialAuthorityMail} onChange={(event) => setOfficialAuthorityMail(event.target.checked)} />
                <span><strong>Behördenpost – höchste Priorität</strong><small>Nur wählen, wenn ein als aktiv bestätigter Behördlicher Empfangsservice vorliegt. Keine automatische Rechts- oder Fristenprüfung.</small></span>
              </label>
              {officialAuthorityMail ? <>
                <label>Absender / Behörde<input type="text" value={officialSenderName} onChange={(event) => setOfficialSenderName(event.target.value)} required /></label>
                <label>Seitenzahl (optional)<input type="number" min="1" step="1" value={officialPageCount} onChange={(event) => setOfficialPageCount(event.target.value)} /></label>
              </> : null}
            </div>
          ) : null}

          <div className="admin-action-grid admin-action-grid--responsive">
            {selectedCustomer ? <button
              type="button"
              className="action-button action-button--success"
              disabled={!selectedScan || !selectedCustomer || !!workingScanId || (officialAuthorityMail && !officialSenderName.trim())}
              onClick={() => runScanAction('assign')}
            >
              {workingScanId ? 'Zuordnung läuft...' : selectedScanIds.size > 1 ? `${selectedScanIds.size} Scans jetzt zuordnen` : 'Jetzt zuordnen'}
            </button> : null}
            <button
              type="button"
              className="action-button action-button--warning"
              disabled={!selectedScan || !!workingScanId}
              onClick={() => runScanAction('ad')}
            >
              Als Werbung aussortieren
            </button>
            <button
              type="button"
              className="action-button action-button--danger"
              disabled={!selectedScan || !!workingScanId}
              onClick={() => runScanAction('delete')}
            >
              Fehlscan löschen
            </button>
            <button
              type="button"
              className="action-button action-button--secondary"
              disabled={!selectedScan || !!workingScanId}
              onClick={() => runScanAction('defer')}
            >
              Überspringen
            </button>
          </div>
        </div>
      </div>

      {inactiveContactOpen && selectedCustomer ? (
        <div className="admin-refund-modal" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !inactiveContactLoading) setInactiveContactOpen(false);
        }}>
          <section className="admin-refund-dialog" role="dialog" aria-modal="true" aria-labelledby="inactive-contact-title">
            <div className="admin-section-header">
              <div>
                <h2 id="inactive-contact-title">Kundenkontakt bei inaktivem Abo</h2>
                <p className="subtitle">Empfänger: {normalizeUtfText(selectedCustomer.display_name)}</p>
              </div>
              <button type="button" className="action-button action-button--secondary" disabled={inactiveContactLoading} onClick={() => setInactiveContactOpen(false)}>Schließen</button>
            </div>

            {inactiveContactError ? <div className="hint-box">{inactiveContactError}</div> : null}
            <label className="admin-refund-control">
              Stufe
              <select value={inactiveContactStage} onChange={(event) => { setInactiveContactStage(event.target.value); setInactiveContactPreview(null); }}>
                <option value="friendly">Freundlicher Zahlungshinweis</option>
                <option value="address_update">Strenger Hinweis: Adresse / Abmeldung klären</option>
                <option value="authority_mail">Dringend: wichtige Behördenpost eingegangen</option>
              </select>
            </label>
            <div className="admin-refund-warning">
              Die E-Mail enthält bewusst keine Scanbilder, Behördennamen, Aktenzeichen, Steuerdaten oder Dokumentinhalte.
            </div>

            <button type="button" className="action-button action-button--secondary" disabled={inactiveContactLoading} onClick={previewInactiveContact}>
              {inactiveContactLoading ? 'Prüfung läuft...' : 'Vorschau und Abostatus prüfen'}
            </button>

            {inactiveContactPreview ? (
              <div className="inactive-contact-preview">
                <p><strong>Empfänger:</strong> {inactiveContactPreview.recipient_masked}</p>
                <p><strong>Betreff:</strong> {normalizeUtfText(inactiveContactPreview.subject)}</p>
                <pre>{normalizeUtfText(inactiveContactPreview.text)}</pre>
                {inactiveContactPreview.recent_send ? (
                  <label className="admin-refund-sepa">
                    <input type="checkbox" checked={confirmRecentContact} onChange={(event) => setConfirmRecentContact(event.target.checked)} />
                    <span>Innerhalb der letzten 24 Stunden wurde bereits eine Nachricht versendet. Erneuten Versand bewusst bestätigen.</span>
                  </label>
                ) : null}
                {inactiveContactPreview.strict ? (
                  <label className="admin-refund-sepa">
                    <input type="checkbox" checked={confirmStrictContact} onChange={(event) => setConfirmStrictContact(event.target.checked)} />
                    <span>Ich habe den strengeren Inhalt geprüft und bestätige den Versand bewusst.</span>
                  </label>
                ) : null}
                <button
                  type="button"
                  className="action-button action-button--success"
                  disabled={inactiveContactLoading || (inactiveContactPreview.strict && !confirmStrictContact) || (inactiveContactPreview.recent_send && !confirmRecentContact)}
                  onClick={sendInactiveContact}
                >
                  {inactiveContactLoading ? 'Wird versendet...' : 'E-Mail jetzt senden'}
                </button>
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
    </section>
  );
}

function normalizeVerificationItem(item = {}) {
  const customer = item.customers || {};
  const order = item.orders || {};
  const followupRequest = item.followup_request || {};
  const usageAgreementData = item.usage_agreement_data || {};

  return {
    ...item,
    latest_document_uploaded_at: item.latest_document_uploaded_at || null,
    customers: {
      ...customer,
      name: normalizeUtfText(customer.name),
      email: normalizeUtfText(customer.email),
      company_name: normalizeUtfText(customer.company_name),
      customer_type: normalizeUtfText(customer.customer_type),
    },
    orders: {
      ...order,
      product_name: normalizeUtfText(order.product_name),
      shopify_order_id: normalizeUtfText(order.shopify_order_id),
      shopify_order_name: normalizeUtfText(order.shopify_order_name),
      subscription_id: normalizeUtfText(order.subscription_id),
    },
    followup_request: {
      ...followupRequest,
      requested_documents: Array.isArray(followupRequest.requested_documents)
        ? followupRequest.requested_documents.map((doc) => normalizeUtfText(doc))
        : [],
      requested_message: normalizeUtfText(followupRequest.requested_message),
      created_at: followupRequest.created_at || null,
    },
    usage_agreement_data: {
      ...usageAgreementData,
      agreement_type: normalizeUtfText(usageAgreementData.agreement_type),
      first_name: normalizeUtfText(usageAgreementData.first_name),
      last_name: normalizeUtfText(usageAgreementData.last_name),
      street: normalizeUtfText(usageAgreementData.street),
      postal_code: normalizeUtfText(usageAgreementData.postal_code),
      city: normalizeUtfText(usageAgreementData.city),
      country: normalizeUtfText(usageAgreementData.country),
      company_name: normalizeUtfText(usageAgreementData.company_name),
      legal_form: normalizeUtfText(usageAgreementData.legal_form),
      representative_name: normalizeUtfText(usageAgreementData.representative_name),
      business_activity: normalizeUtfText(usageAgreementData.business_activity),
      company_street: normalizeUtfText(usageAgreementData.company_street),
      company_postal_code: normalizeUtfText(usageAgreementData.company_postal_code),
      company_city: normalizeUtfText(usageAgreementData.company_city),
      company_country: normalizeUtfText(usageAgreementData.company_country),
      register_court: normalizeUtfText(usageAgreementData.register_court),
      register_number: normalizeUtfText(usageAgreementData.register_number),
      tax_number: normalizeUtfText(usageAgreementData.tax_number),
      vat_id: normalizeUtfText(usageAgreementData.vat_id),
    },
  };
}

function compactArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function hasStableId(item) {
  return item && item.id !== null && item.id !== undefined;
}

function normalizePortalRequestItem(request = {}) {
  return {
    ...request,
    customers: request.customers || {},
    documents: request.documents || {},
    requested_payload: request.requested_payload || {},
    current_snapshot: request.current_snapshot || {},
    request_type: normalizeUtfText(request.request_type),
  };
}

function normalizeMailboxCandidateItem(candidate = {}) {
  return {
    ...candidate,
    customer: candidate.customer || null,
    subscription: candidate.subscription || {},
    display_profile: candidate.display_profile || null,
    suggested_display_name: normalizeUtfText(candidate.suggested_display_name),
  };
}

function normalizeShopifyOrderItem(order = {}) {
  return {
    ...order,
    email: normalizeUtfText(order.email),
    first_name: normalizeUtfText(order.first_name),
    last_name: normalizeUtfText(order.last_name),
    full_name: normalizeUtfText(order.full_name),
    company_name: normalizeUtfText(order.company_name),
    customer_address_formatted: normalizeUtfText(order.customer_address_formatted),
    main_product: normalizeUtfText(order.main_product),
    match_source: normalizeUtfText(order.match_source),
    auto_verification_decision: normalizeUtfText(order.auto_verification_decision),
    auto_verification_reason: normalizeUtfText(order.auto_verification_reason),
    detected_customer_type: normalizeUtfText(order.detected_customer_type),
    detected_agreement_type: normalizeUtfText(order.detected_agreement_type),
    premium_offer_status: normalizeUtfText(order.premium_offer_status),
    provider: normalizeUtfText(order.provider) || 'shopify_seal',
    provider_label: normalizeUtfText(order.provider_label) || 'Shopify / Seal',
    order_reference: normalizeUtfText(order.order_reference || order.shopify_order_name || order.shopify_order_id),
    external_order_id: normalizeUtfText(order.external_order_id || order.shopify_order_id),
    order_date: order.order_date || order.created_at_shopify || order.imported_at || null,
    payment_method_type: normalizeUtfText(order.payment_method_type),
  };
}

function normalizeLogisticsRequestItem(item = {}) {
  return {
    ...item,
    customer_email: normalizeUtfText(item.customer_email),
    customer_name: normalizeUtfText(item.customer_name),
    company_name: normalizeUtfText(item.company_name),
    service_type: normalizeUtfText(item.service_type),
    package_weight_class: normalizeUtfText(item.package_weight_class),
    recipient_name: normalizeUtfText(item.recipient_name),
    recipient_company: normalizeUtfText(item.recipient_company),
    recipient_street: normalizeUtfText(item.recipient_street),
    recipient_address_extra: normalizeUtfText(item.recipient_address_extra),
    recipient_zip: normalizeUtfText(item.recipient_zip),
    recipient_city: normalizeUtfText(item.recipient_city),
    recipient_country: normalizeUtfText(item.recipient_country),
    recipient_phone: normalizeUtfText(item.recipient_phone),
    sender_name: normalizeUtfText(item.sender_name),
    package_content_notes: normalizeUtfText(item.package_content_notes),
    inbound_tracking_number: normalizeUtfText(item.inbound_tracking_number),
    inbound_carrier: normalizeUtfText(item.inbound_carrier),
    dhl_tracking_number_outbound: normalizeUtfText(item.dhl_tracking_number_outbound),
    dhl_label_url: normalizeUtfText(item.dhl_label_url),
    customer_notes: normalizeUtfText(item.customer_notes),
    admin_note: normalizeUtfText(item.admin_note),
    shipping_cost_amount: normalizeUtfText(item.shipping_cost_amount),
    shipping_cost_currency: normalizeUtfText(item.shipping_cost_currency) || 'EUR',
    shopify_shipping_invoice_draft_order_id: normalizeUtfText(item.shopify_shipping_invoice_draft_order_id),
    shopify_shipping_invoice_name: normalizeUtfText(item.shopify_shipping_invoice_name),
    shopify_shipping_invoice_url: normalizeUtfText(item.shopify_shipping_invoice_url),
    shopify_shipping_invoice_sent_at: item.shopify_shipping_invoice_sent_at || null,
    logistics_request_files: compactArray(item.logistics_request_files)
      .map((file) => ({
          ...file,
          original_filename: normalizeUtfText(file.original_filename),
          mime_type: normalizeUtfText(file.mime_type),
          purpose: normalizeUtfText(file.purpose),
        })),
  };
}

function formatLogisticsRecipientAddress(item) {
  if (item.recipient_same_as_billing) {
    return 'Entspricht der Rechnungsadresse';
  }

  return [
    item.recipient_name,
    item.recipient_company,
    item.recipient_street,
    item.recipient_address_extra,
    [item.recipient_zip, item.recipient_city].filter(Boolean).join(' '),
    item.recipient_country,
  ].filter(Boolean).join(', ') || '-';
}

function formatMoneyValue(value, currency = 'EUR') {
  const amount = Number(String(value || '').replace(',', '.'));
  if (!Number.isFinite(amount) || amount <= 0) return '-';
  return `${amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || 'EUR'}`;
}

function parseLatestDeadlineExtension(adminNote) {
  const note = String(adminNote || '');
  const pattern = /\[\[FRIST_VERLAENGERT_UNTIL:([^\]]+)\]\]/g;
  let latest = null;
  let match;

  while ((match = pattern.exec(note)) !== null) {
    const parsed = new Date(match[1]);
    if (Number.isNaN(parsed.getTime())) continue;
    if (!latest || parsed > latest) latest = parsed;
  }

  return latest;
}

function getVerificationReversalDeadline(ver) {
  const order = Array.isArray(ver.orders) ? ver.orders[0] : ver.orders;
  const startValue =
    order?.created_at_shopify ||
    order?.created_at ||
    ver.shopify_order?.created_at_shopify ||
    ver.shopify_order?.created_at ||
    ver.shopify_order?.imported_at ||
    ver.created_at ||
    Date.now();
  const createdAt = new Date(startValue);
  const defaultDeadline = new Date(createdAt.getTime() + 14 * 24 * 60 * 60 * 1000);
  const extendedUntil = parseLatestDeadlineExtension(ver.admin_note);
  return extendedUntil && extendedUntil > defaultDeadline ? extendedUntil : defaultDeadline;
}

function isReversalRequiredStatus(status) {
  return normalizeUtfText(status).toLowerCase() === 'rueckabwicklung_erforderlich';
}

function isReversedStatus(status) {
  const normalized = normalizeUtfText(status).toLowerCase();
  return normalized === 'rueckabgewickelt' || normalized === 'rueckabwicklung_bestaetigt';
}

function isVerificationPastReversalDeadline(ver) {
  const identity = normalizeUtfText(ver.identity_status).toLowerCase();
  if (
    [
      'approved',
      'already_verified',
      'submitted',
      'in_review',
      'rejected',
      'cancelled',
      'canceled',
      'storniert',
      'rueckabgewickelt',
      'rueckabwicklung_bestaetigt',
    ].includes(identity)
  ) {
    return false;
  }

  return new Date() >= getVerificationReversalDeadline(ver);
}

function getRefundStatusMeta(status) {
  const value = normalizeUtfText(status).toLowerCase();
  const map = {
    pending: { label: 'Rückerstattung offen', tone: 'warning' },
    processing: { label: 'Rückerstattung läuft', tone: 'info' },
    succeeded: { label: 'Rückerstattung erledigt', tone: 'success' },
    completed: { label: 'Rückerstattung erledigt', tone: 'success' },
    failed: { label: 'Rückerstattung fehlgeschlagen', tone: 'danger' },
    not_requested: { label: 'Keine automatische Rückerstattung', tone: 'neutral' },
    none: { label: 'Keine automatische Rückerstattung', tone: 'neutral' },
  };

  return map[value] || { label: status ? `Rückerstattung: ${status}` : 'Rückerstattung offen', tone: 'warning' };
}

function formatRefundPreviewAmount(preview) {
  const amount = Number(preview?.amount);
  if (!Number.isFinite(amount)) return '-';
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: preview?.currency || 'EUR',
    }).format(amount);
  } catch (err) {
    return `${amount.toFixed(2)} ${preview?.currency || 'EUR'}`;
  }
}

const VALID_ADMIN_AREAS = [
  'open-operations',
  'system-status',
  'notifications',
  'marketing-leads',
  'metrics',
  'web-analytics',
  'scans',
  'portal-requests',
  'logistics',
  'mailbox-display',
  'chat-automation',
  'google-reviews',
  'access-reviews',
  'dashboard-focus',
  'activity',
];

const ADMIN_AREA_LABELS = {
  overview: 'Dashboard',
  'open-operations': 'Offene Vorgänge',
  'system-status': 'Systemstatus',
  notifications: 'Benachrichtigungen',
  'marketing-leads': 'Kontakte & Leads',
  metrics: 'Kennzahlen',
  'web-analytics': 'Web Analytics',
  scans: 'Scans zuordnen',
  'portal-requests': 'Portal-Anfragen',
  logistics: 'Logistik',
  'mailbox-display': 'Briefkastenanzeige',
  'chat-automation': 'Live-Chat',
  'google-reviews': 'Google-Bewertungen',
  'access-reviews': 'Zugangsprüfungen',
  'dashboard-focus': 'Bestellungen',
  activity: 'Aktivität',
};

function normalizeAdminArea(sectionId) {
  if (['shopify-orders', 'verifications'].includes(sectionId)) return 'dashboard-focus';
  return VALID_ADMIN_AREAS.includes(sectionId) ? sectionId : 'overview';
}

function adminNotificationStatus(settings) {
  const channel = settings.admin_notification_channel;
  if (channel === 'disabled-local-test') {
    return { ok: true, label: 'Benachrichtigungen im lokalen Test deaktiviert' };
  }
  if (channel === 'both') {
    return {
      ok: settings.telegram_admin_configured,
      label: settings.telegram_admin_configured
        ? 'Benachrichtigungen: Telegram + E-Mail aktiv'
        : 'Benachrichtigungen: E-Mail aktiv, Telegram nicht eingerichtet',
    };
  }
  if (channel === 'telegram') {
    return {
      ok: settings.telegram_admin_configured,
      label: settings.telegram_admin_configured
        ? 'Benachrichtigungen: Telegram aktiv · E-Mail-Fallback aktiv'
        : 'Benachrichtigungen: Telegram gewählt, aber nicht eingerichtet',
    };
  }
  return {
    ok: true,
    label: settings.telegram_admin_configured
      ? 'Benachrichtigungen: E-Mail aktiv · Telegram eingerichtet, aber nicht aktiv'
      : 'Benachrichtigungen: E-Mail aktiv · Telegram nicht eingerichtet',
  };
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState([]);
  const [shopifyOrders, setShopifyOrders] = useState([]);
  const [refundDialog, setRefundDialog] = useState(null);
  const [refundDetails, setRefundDetails] = useState(null);
  const [refundDraft, setRefundDraft] = useState({ mode: 'partial', amount: '', reason: '', sepaAcknowledged: false });
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [logisticsRequests, setLogisticsRequests] = useState([]);
  const [portalRequests, setPortalRequests] = useState([]);
  const [companyDataHistory, setCompanyDataHistory] = useState([]);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [legalEntityOverview, setLegalEntityOverview] = useState({ entities: [], migration_pending: true });
  const [legalEntitySavingId, setLegalEntitySavingId] = useState('');
  const [legalEntityCorrectionMessage, setLegalEntityCorrectionMessage] = useState('');
  const [unassignedScanCount, setUnassignedScanCount] = useState(0);
  const [mailboxCandidates, setMailboxCandidates] = useState([]);
  const [mailboxCandidateWarnings, setMailboxCandidateWarnings] = useState([]);
  const [mailboxCandidateSources, setMailboxCandidateSources] = useState({ seal: 0, stripe: 0, total: 0 });
  const [mailboxReconciliation, setMailboxReconciliation] = useState({ stripe: null });
  const [mailboxDrafts, setMailboxDrafts] = useState({});
  const [mailboxCandidatesLoading, setMailboxCandidatesLoading] = useState(false);
  const [accessReviews, setAccessReviews] = useState([]);
  const [accessReviewError, setAccessReviewError] = useState('');
  const [loadingAccessReviews, setLoadingAccessReviews] = useState(false);
  const [confirmingAccessCustomerId, setConfirmingAccessCustomerId] = useState(null);
  const [nextcloudInventory, setNextcloudInventory] = useState(null);
  const [nextcloudInventoryError, setNextcloudInventoryError] = useState('');
  const [loadingNextcloudInventory, setLoadingNextcloudInventory] = useState(false);
  const [logisticsDrafts, setLogisticsDrafts] = useState({});
  const [savingLogisticsId, setSavingLogisticsId] = useState(null);
  const [creatingShippingInvoiceId, setCreatingShippingInvoiceId] = useState(null);
  const [sendingShippingInvoiceId, setSendingShippingInvoiceId] = useState(null);
  const [deletingShippingInvoiceId, setDeletingShippingInvoiceId] = useState(null);
  const [notifyingLogisticsId, setNotifyingLogisticsId] = useState(null);
  const [savingMailboxCustomerId, setSavingMailboxCustomerId] = useState(null);
  const [workingPortalRequestId, setWorkingPortalRequestId] = useState(null);
  const [postageDrafts, setPostageDrafts] = useState({});
  const [creatingPostageCheckoutId, setCreatingPostageCheckoutId] = useState(null);
  const [withdrawalPreviews, setWithdrawalPreviews] = useState({});
  const [loadingWithdrawalPreviewId, setLoadingWithdrawalPreviewId] = useState(null);
  const [settings, setSettings] = useState({
    shopify_auto_verification_enabled: false,
    shopify_auto_verification_updated_at: null,
    admin_notification_channel: 'email',
    telegram_admin_configured: false,
    mailbox_display_token_configured: false,
    mailbox_display_url: null,
    production_release: null,
    chat_auto_acknowledgement: null,
    google_reviews: null,
  });
  const [productionStatus, setProductionStatus] = useState(null);
  const [notificationOverview, setNotificationOverview] = useState(null);
  const [startupWarnings, setStartupWarnings] = useState([]);
  const [adminDashboardCardOrder, setAdminDashboardCardOrder] = useState(
    DEFAULT_ADMIN_DASHBOARD_CARD_ORDER
  );
  const [draggedAdminCardId, setDraggedAdminCardId] = useState('');
  const [dragOverAdminCardId, setDragOverAdminCardId] = useState('');
  const [cardOrderSaveError, setCardOrderSaveError] = useState('');
  const [savingAdminCardOrder, setSavingAdminCardOrder] = useState(false);
  const notificationStatus = adminNotificationStatus(settings);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loadingSystemStatus, setLoadingSystemStatus] = useState(false);
  const [systemStatusError, setSystemStatusError] = useState('');
  const [subscriptionRevenue, setSubscriptionRevenue] = useState(null);
  const [subscriptionRevenueError, setSubscriptionRevenueError] = useState('');
  const [subscriptionForecastMonth, setSubscriptionForecastMonth] = useState(() => localMonthKey());
  const [subscriptionForecastWeek, setSubscriptionForecastWeek] = useState('');
  const [businessMetrics, setBusinessMetrics] = useState(null);
  const [businessMetricsRange, setBusinessMetricsRange] = useState('30d');
  const [businessMetricsError, setBusinessMetricsError] = useState('');
  const [loadingBusinessMetrics, setLoadingBusinessMetrics] = useState(false);
  const [websiteAnalytics, setWebsiteAnalytics] = useState(null);
  const [conversionAnalytics, setConversionAnalytics] = useState(null);
  const [websiteAnalyticsRange, setWebsiteAnalyticsRange] = useState('24h');
  const [websiteAnalyticsError, setWebsiteAnalyticsError] = useState('');
  const [conversionAnalyticsError, setConversionAnalyticsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [documentsByVerification, setDocumentsByVerification] = useState({});
  const [openRows, setOpenRows] = useState({});
  const [requestingOrderId, setRequestingOrderId] = useState(null);
  const [sendingPremiumOfferId, setSendingPremiumOfferId] = useState(null);
  const [sendingSealPaymentEmailId, setSendingSealPaymentEmailId] = useState(null);
  const [updatingVerificationIds, setUpdatingVerificationIds] = useState(() => new Set());
  const updatingVerificationIdsRef = useRef(new Set());
  const [sendingOrderActivationId, setSendingOrderActivationId] = useState(null);
  const [savingAutoSetting, setSavingAutoSetting] = useState(false);
  const [testingAdminNotification, setTestingAdminNotification] = useState(false);
  const [savingNotificationChannel, setSavingNotificationChannel] = useState(false);
  const [followupDrafts, setFollowupDrafts] = useState({});
  const [savingFollowupId, setSavingFollowupId] = useState(null);
  const [savingResetId, setSavingResetId] = useState(null);
  const [reopeningVerificationId, setReopeningVerificationId] = useState(null);
  const [editingVerificationId, setEditingVerificationId] = useState(null);
  const [verificationEditDrafts, setVerificationEditDrafts] = useState({});
  const [savingVerificationEditId, setSavingVerificationEditId] = useState(null);
  const [resendingVerificationId, setResendingVerificationId] = useState(null);
  const [sendingAccountActivationId, setSendingAccountActivationId] = useState(null);
  const [resendingCompletionId, setResendingCompletionId] = useState(null);
  const [deletingVerificationId, setDeletingVerificationId] = useState(null);
  const [workingReversalId, setWorkingReversalId] = useState(null);
  const [runningArchiveMaintenance, setRunningArchiveMaintenance] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [archiveFilter, setArchiveFilter] = useState('active');
  const [quickFilter, setQuickFilter] = useState('all');
  const [verificationReviewFilter, setVerificationReviewFilter] = useState('all');
  const [orderProviderFilter, setOrderProviderFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedVerificationId, setHighlightedVerificationId] = useState(null);
  const [pendingFocusVerificationId, setPendingFocusVerificationId] = useState(null);
  const [copiedFieldKey, setCopiedFieldKey] = useState('');
  const [downloadingAgreementKey, setDownloadingAgreementKey] = useState('');
  const [isMobileLayout, setIsMobileLayout] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 980 : false
  );
  const openOperations = useMemo(() => buildOpenOperations({
    systemStatus,
    verifications,
    portalRequests,
    logisticsRequests,
    accessReviews,
    nextcloudInventory,
    notifications: adminNotifications,
  }), [accessReviews, adminNotifications, logisticsRequests, nextcloudInventory, portalRequests, systemStatus, verifications]);
  const verificationReviewWorklist = useMemo(
    () => buildVerificationReviewWorklist(verifications),
    [verifications]
  );
  const visibleVerificationReviewWorklist = useMemo(
    () => filterVerificationReviewWorklist(verificationReviewWorklist, verificationReviewFilter),
    [verificationReviewFilter, verificationReviewWorklist]
  );
  const initialAdminArea = typeof window !== 'undefined'
    ? window.location.hash.replace(/^#/, '')
    : '';
  const [activeAdminArea, setActiveAdminArea] = useState(
    normalizeAdminArea(initialAdminArea)
  );

  const desktopVerificationRowRefs = useRef({});
  const mobileVerificationRowRefs = useRef({});
  const handledDeepLinkVerificationRef = useRef('');
  const suppressAdminCardClickRef = useRef(false);

  const token = localStorage.getItem('adminToken');

  const loadSystemStatus = useCallback(async ({ force = false } = {}) => {
    if (!token) return;
    setLoadingSystemStatus(true);
    setSystemStatusError('');
    try {
      const response = await fetch(`${API_URL}/admin/system-status${force ? '?refresh=1' : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (isAuthFailureResponse(response)) {
        localStorage.removeItem('adminToken');
        navigate(buildAdminLoginPath(), { replace: true });
        return;
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Systemstatus konnte nicht geladen werden.');
      setSystemStatus(data);
    } catch (err) {
      setSystemStatusError(err.message || 'Systemstatus konnte nicht geladen werden.');
    } finally {
      setLoadingSystemStatus(false);
    }
  }, [navigate, token]);

  useEffect(() => {
    loadSystemStatus();
  }, [loadSystemStatus]);

  const loadAccessReviews = useCallback(async ({ discover = false } = {}) => {
    if (!token) return;
    setLoadingAccessReviews(true);
    setAccessReviewError('');
    try {
      const response = await fetch(`${API_URL}/admin/customer-access-reviews${discover ? '/discover' : ''}`, {
        method: discover ? 'POST' : 'GET',
        headers: { Authorization: `Bearer ${token}`, ...(discover ? { 'Content-Type': 'application/json' } : {}) },
      });
      if (isAuthFailureResponse(response)) {
        localStorage.removeItem('adminToken');
        navigate(buildAdminLoginPath(), { replace: true });
        return;
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Zugangsprüfungen konnten nicht geladen werden.');
      setAccessReviews(data.reviews || []);
    } catch (error) {
      setAccessReviewError(error.message || 'Zugangsprüfungen konnten nicht geladen werden.');
    } finally {
      setLoadingAccessReviews(false);
    }
  }, [navigate, token]);

  useEffect(() => { loadAccessReviews(); }, [loadAccessReviews]);

  const loadNextcloudInventory = useCallback(async () => {
    if (!token) return;
    setLoadingNextcloudInventory(true);
    setNextcloudInventoryError('');
    try {
      const response = await fetch(`${API_URL}/admin/nextcloud-account-inventory`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (isAuthFailureResponse(response)) {
        localStorage.removeItem('adminToken');
        navigate(buildAdminLoginPath(), { replace: true });
        return;
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Nextcloud-Benutzerbestand konnte nicht geladen werden.');
      setNextcloudInventory(data.inventory || null);
    } catch (error) {
      setNextcloudInventoryError(error.message || 'Nextcloud-Benutzerbestand konnte nicht geladen werden.');
    } finally {
      setLoadingNextcloudInventory(false);
    }
  }, [navigate, token]);

  useEffect(() => { loadNextcloudInventory(); }, [loadNextcloudInventory]);

  const confirmAccessDeactivation = async (review) => {
    const customerName = review.customers?.company_name || review.customers?.name || review.customers?.email || `Kunde ${review.customer_id}`;
    if (!window.confirm(`Zugang für ${customerName} wirklich deaktivieren? Nextcloud-Dateien bleiben erhalten.`)) return;
    setConfirmingAccessCustomerId(review.customer_id);
    setAccessReviewError('');
    try {
      const response = await fetch(`${API_URL}/admin/customer-access-reviews/${encodeURIComponent(review.customer_id)}/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Zugang konnte nicht deaktiviert werden.');
      await loadAccessReviews();
    } catch (error) {
      setAccessReviewError(error.message || 'Zugang konnte nicht deaktiviert werden.');
    } finally {
      setConfirmingAccessCustomerId(null);
    }
  };

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadSystemStatus();
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [loadSystemStatus]);

  const loadSubscriptionRevenue = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/admin/subscription-revenue?forecast_month=${encodeURIComponent(subscriptionForecastMonth)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (isAuthFailureResponse(response)) {
        localStorage.removeItem('adminToken');
        navigate(buildAdminLoginPath(), { replace: true });
        return;
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Umsatzprognose konnte nicht geladen werden.');
      setSubscriptionRevenue(data);
      setSubscriptionRevenueError('');
    } catch (err) {
      setSubscriptionRevenueError(err.message || 'Umsatzprognose konnte nicht geladen werden.');
    }
  }, [navigate, subscriptionForecastMonth, token]);

  useEffect(() => {
    loadSubscriptionRevenue();
    const timer = window.setInterval(loadSubscriptionRevenue, 60_000);
    return () => window.clearInterval(timer);
  }, [loadSubscriptionRevenue]);

  useEffect(() => {
    const weeks = subscriptionRevenue?.forecast?.weeks || [];
    if (!weeks.length) return;
    const today = subscriptionRevenue.forecast.today?.date;
    const containingToday = subscriptionRevenue.forecast.month === localMonthKey()
      ? weeks.find((week) => today >= week.start && today <= week.end)
      : null;
    setSubscriptionForecastWeek((current) => (
      weeks.some((week) => week.key === current) ? current : (containingToday?.key || weeks[0].key)
    ));
  }, [subscriptionRevenue]);

  const subscriptionForecastMonths = useMemo(() => Array.from({ length: 12 }, (_, index) => {
    const today = new Date();
    const date = new Date(today.getFullYear(), today.getMonth() + index, 1);
    return {
      key: localMonthKey(date),
      label: date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
    };
  }), []);

  const loadBusinessMetrics = useCallback(async () => {
    if (!token) return;
    setLoadingBusinessMetrics(true);
    try {
      const response = await fetch(`${API_URL}/admin/business-metrics?range=${encodeURIComponent(businessMetricsRange)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (isAuthFailureResponse(response)) {
        localStorage.removeItem('adminToken');
        navigate(buildAdminLoginPath(), { replace: true });
        return;
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Kennzahlen konnten nicht geladen werden.');
      setBusinessMetrics(data);
      setBusinessMetricsError('');
    } catch (err) {
      setBusinessMetricsError(err.message || 'Kennzahlen konnten nicht geladen werden.');
    } finally {
      setLoadingBusinessMetrics(false);
    }
  }, [businessMetricsRange, navigate, token]);

  useEffect(() => {
    if (activeAdminArea !== 'metrics') return undefined;
    loadBusinessMetrics();
    const timer = window.setInterval(loadBusinessMetrics, 5 * 60_000);
    return () => window.clearInterval(timer);
  }, [activeAdminArea, loadBusinessMetrics]);

  const loadWebsiteAnalytics = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/admin/website-analytics?range=${encodeURIComponent(websiteAnalyticsRange)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (isAuthFailureResponse(response)) {
        localStorage.removeItem('adminToken');
        navigate(buildAdminLoginPath(), { replace: true });
        return;
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Website-Analytics konnten nicht geladen werden.');
      setWebsiteAnalytics(data);
      setWebsiteAnalyticsError('');
    } catch (err) {
      setWebsiteAnalyticsError(err.message || 'Website-Analytics konnten nicht geladen werden.');
    }
  }, [navigate, token, websiteAnalyticsRange]);

  const loadConversionAnalytics = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/admin/conversion-analytics?range=${encodeURIComponent(websiteAnalyticsRange)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (isAuthFailureResponse(response)) {
        localStorage.removeItem('adminToken');
        navigate(buildAdminLoginPath(), { replace: true });
        return;
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Conversion-Analytics konnten nicht geladen werden.');
      setConversionAnalytics(data);
      setConversionAnalyticsError('');
    } catch (err) {
      setConversionAnalyticsError(err.message || 'Conversion-Analytics konnten nicht geladen werden.');
    }
  }, [navigate, token, websiteAnalyticsRange]);

  useEffect(() => {
    if (activeAdminArea !== 'web-analytics') return undefined;
    loadWebsiteAnalytics();
    loadConversionAnalytics();
    const timer = window.setInterval(() => {
      loadWebsiteAnalytics();
      loadConversionAnalytics();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [activeAdminArea, loadConversionAnalytics, loadWebsiteAnalytics]);

  const openAdminArea = useCallback((sectionId) => {
    if (typeof window === 'undefined') return;
    const nextArea = normalizeAdminArea(sectionId);
    setActiveAdminArea(nextArea);
    const hash = nextArea === 'overview' ? '' : `#${nextArea}`;
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileLayout(window.innerWidth <= 980);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncAreaFromHash = () => {
      const sectionId = window.location.hash.replace(/^#/, '');
      setActiveAdminArea(normalizeAdminArea(sectionId));
    };
    syncAreaFromHash();
    window.addEventListener('hashchange', syncAreaFromHash);
    return () => window.removeEventListener('hashchange', syncAreaFromHash);
  }, []);

  const saveLegalEntityCorrection = async (event, entity) => {
    event.preventDefault();
    setLegalEntitySavingId(entity.id);
    setLegalEntityCorrectionMessage('');
    try {
      const form = new FormData(event.currentTarget);
      const response = await fetch(`${API_URL}/admin/legal-entities/${encodeURIComponent(entity.id)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_type: entity.entity_type,
          legal_name: form.get('legal_name'), legal_form: form.get('legal_form'),
          registered_office: form.get('registered_office'), register_court: form.get('register_court'),
          registration_number: form.get('registration_number'), country_code: form.get('country_code'),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Korrektur konnte nicht gespeichert werden.');
      setLegalEntityOverview((current) => ({
        ...current,
        entities: current.entities.map((item) => item.id === entity.id ? { ...item, ...payload.entity } : item),
      }));
      setLegalEntityCorrectionMessage('Unternehmensprofil wurde atomar korrigiert; die interne ID blieb unverändert.');
    } catch (error) {
      setLegalEntityCorrectionMessage(error.message || 'Korrektur konnte nicht gespeichert werden.');
    } finally {
      setLegalEntitySavingId('');
    }
  };

  const loadMailboxCandidates = useCallback(async () => {
    if (!token) return;

    setMailboxCandidatesLoading(true);
    const resource = await loadAdminStartupResource({
      path: '/admin/mailbox-display-candidates',
      label: 'Briefkasten-Kandidaten',
      token,
      fallback: { candidates: [], warnings: [], sources: { seal: 0, stripe: 0, total: 0 }, reconciliation: { stripe: null } },
    });

    if (resource.response && isAuthFailureResponse(resource.response)) {
      localStorage.removeItem('adminToken');
      navigate(buildAdminLoginPath(), { replace: true });
      return;
    }

    const data = resource.data || {};
    const rows = Array.isArray(data) ? data : data.candidates || [];
    const normalizedCandidates = compactArray(rows).map(normalizeMailboxCandidateItem);
    const warnings = compactArray(data.warnings);
    if (resource.warning) warnings.unshift({ source: 'System', message: resource.warning });

    setMailboxCandidates(normalizedCandidates);
    setMailboxCandidateWarnings(warnings);
    setMailboxCandidateSources(data.sources || {
      seal: 0,
      stripe: 0,
      total: normalizedCandidates.length,
    });
    setMailboxReconciliation(data.reconciliation || { stripe: null });
    setMailboxDrafts((prev) => {
      const next = { ...prev };
      normalizedCandidates.forEach((candidate) => {
        const customerId = candidate.customer?.id;
        if (!customerId || Object.prototype.hasOwnProperty.call(next, customerId)) return;
        next[customerId] = candidate.display_profile?.display_name || candidate.suggested_display_name || '';
      });
      return next;
    });
    setMailboxCandidatesLoading(false);
  }, [navigate, token]);

  const fetchData = useCallback(async () => {
    if (!token) {
      navigate(buildAdminLoginPath(), { replace: true });
      return;
    }

    setLoading(true);
    setError('');
    setStartupWarnings([]);
    void loadMailboxCandidates();

    try {
      const resources = await Promise.all([
        { path: '/admin/verifications', label: 'Verifizierungen', fallback: [] },
        { path: '/admin/orders', label: 'Bestellungen', fallback: [] },
        { path: '/admin/settings', label: 'Einstellungen', fallback: {} },
        { path: '/admin/production-status', label: 'Produktionsstände', fallback: null },
        { path: '/admin/logistics-requests', label: 'Logistik-Aufträge', fallback: [] },
        { path: '/admin/portal-requests', label: 'Portal-Anfragen', fallback: [] },
        { path: '/admin/company-data-history', label: 'Unternehmensdatenverlauf', fallback: { items: [] } },
        { path: '/admin/notifications', label: 'Interne Hinweise', fallback: [] },
        { path: '/admin/notification-overview', label: 'Benachrichtigungsübersicht', fallback: null },
        { path: '/admin/legal-entities', label: 'Unternehmensprofile', fallback: { entities: [], migration_pending: true } },
      ].map((resource) => loadAdminStartupResource({ ...resource, token })));

      const [
        verificationsResource,
        shopifyOrdersResource,
        settingsResource,
        productionStatusResource,
        logisticsResource,
        portalRequestsResource,
        companyDataHistoryResource,
        notificationsResource,
        notificationOverviewResource,
        legalEntitiesResource,
      ] = resources;

      if (resources.some(({ response }) => response && isAuthFailureResponse(response))) {
        localStorage.removeItem('adminToken');
        navigate(buildAdminLoginPath(), { replace: true });
        return;
      }

      setStartupWarnings(resources.map(({ warning }) => warning).filter(Boolean));

      const verificationsData = verificationsResource.data;
      const shopifyOrdersData = shopifyOrdersResource.data;
      const settingsData = settingsResource.data;
      const productionStatusData = productionStatusResource.data;
      const logisticsData = logisticsResource.data;
      const portalRequestsData = portalRequestsResource.data;
      const companyDataHistoryData = companyDataHistoryResource.data;
      const notificationsData = notificationsResource.data;
      const notificationOverviewData = notificationOverviewResource.data;
      const legalEntitiesData = legalEntitiesResource.data;

      const normalizedVerifications = compactArray(verificationsData)
        .map(normalizeVerificationItem)
        .filter(hasStableId);
      const normalizedShopifyOrders = compactArray(shopifyOrdersData)
        .map(normalizeShopifyOrderItem)
        .filter(hasStableId);
      const normalizedLogisticsRequests = compactArray(logisticsData)
        .map(normalizeLogisticsRequestItem)
        .filter(hasStableId);
      const normalizedPortalRequests = compactArray(portalRequestsData)
        .map(normalizePortalRequestItem)
        .filter(hasStableId);

      setVerifications(normalizedVerifications);
      setShopifyOrders(normalizedShopifyOrders);
      setLogisticsRequests(normalizedLogisticsRequests);
      setPortalRequests(normalizedPortalRequests);
      setCompanyDataHistory(compactArray(companyDataHistoryData.items));
      setAdminNotifications(compactArray(notificationsData));
      setNotificationOverview(notificationOverviewData);
      setLegalEntityOverview(legalEntitiesData);
      setSettings({
        shopify_auto_verification_enabled:
          settingsData.shopify_auto_verification_enabled || false,
        shopify_auto_verification_updated_at:
          settingsData.shopify_auto_verification_updated_at || null,
        admin_notification_channel: settingsData.admin_notification_channel || 'email',
        telegram_admin_configured: !!settingsData.telegram_admin_configured,
        mailbox_display_token_configured: !!settingsData.mailbox_display_token_configured,
        mailbox_display_url: settingsData.mailbox_display_url || null,
        production_release: settingsData.production_release || null,
        chat_auto_acknowledgement: settingsData.chat_auto_acknowledgement || null,
        google_reviews: settingsData.google_reviews || null,
      });
      setProductionStatus(productionStatusData);
      setAdminDashboardCardOrder(normalizeAdminDashboardCardOrder(
        settingsData.admin_dashboard_card_order
      ));

      setFollowupDrafts((prev) => {
        const next = { ...prev };

        normalizedVerifications.forEach((ver) => {
          if (!next[ver.id]) {
            next[ver.id] = {
              open: false,
              requestedDocuments: ver.followup_request.requested_documents || [],
              requestedMessage: ver.followup_request.requested_message || '',
            };
          }
        });

        return next;
      });

      setLogisticsDrafts((prev) => {
        const next = { ...prev };

        normalizedLogisticsRequests.forEach((item) => {
          next[item.id] = {
            status: item.status || 'new',
            dhl_tracking_number_outbound: item.dhl_tracking_number_outbound || '',
            dhl_label_url: item.dhl_label_url || '',
            customer_notes: item.customer_notes || '',
            admin_note: item.admin_note || '',
          };
        });

        return next;
      });

    } catch (err) {
      console.error(err);
      setError(err.message || 'Fehler beim Abruf');
    } finally {
      setLoading(false);
    }
  }, [loadMailboxCandidates, navigate, token]);

  const refreshVerification = useCallback(async (verificationId) => {
    if (!token || !verificationId) return null;

    const response = await fetch(`${API_URL}/admin/verifications/${verificationId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || 'Verifizierung konnte nicht aktualisiert werden.');
    }

    const normalized = normalizeVerificationItem(data);
    setVerifications((prev) => {
      const exists = prev.some((item) => String(item.id) === String(normalized.id));
      return exists
        ? prev.map((item) => (String(item.id) === String(normalized.id) ? normalized : item))
        : [normalized, ...prev];
    });
    setFollowupDrafts((prev) => ({
      ...prev,
      [normalized.id]: {
        ...(prev[normalized.id] || {}),
        open: prev[normalized.id]?.open || false,
        requestedDocuments: normalized.followup_request.requested_documents || prev[normalized.id]?.requestedDocuments || [],
        requestedMessage: normalized.followup_request.requested_message || prev[normalized.id]?.requestedMessage || '',
      },
    }));

    return normalized;
  }, [token]);

  const saveMailboxDisplayName = useCallback(async (candidate) => {
    const customerId = candidate.customer?.id;
    if (!token || !customerId) return;

    const displayName = normalizeUtfText(mailboxDrafts[customerId]);
    if (!displayName) {
      alert('Bitte einen Anzeigenamen eintragen.');
      return;
    }

    setSavingMailboxCustomerId(customerId);

    try {
      const response = await fetch(`${API_URL}/admin/mailbox-display-profiles`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: customerId,
          display_name: displayName,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Briefkastenanzeige konnte nicht gespeichert werden.');
      }

      setMailboxCandidates((prev) => compactArray(prev).map((item) => {
        if (!item || item.customer?.id !== customerId) return item;
        return {
          ...item,
          display_profile: {
            ...(item.display_profile || {}),
            ...(data.profile || {}),
            display_name: displayName,
            status: 'active',
          },
          suggested_display_name: displayName,
        };
      }));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Briefkastenanzeige konnte nicht gespeichert werden.'));
    } finally {
      setSavingMailboxCustomerId(null);
    }
  }, [mailboxDrafts, token]);

  const fillMailboxSuggestions = useCallback(() => {
    setMailboxDrafts((prev) => {
      const next = { ...prev };
      compactArray(mailboxCandidates).forEach((candidate) => {
        if (!candidate) return;
        const customerId = candidate.customer?.id;
        if (!customerId) return;
        next[customerId] = candidate.display_profile?.display_name || candidate.suggested_display_name || next[customerId] || '';
      });
      return next;
    });
  }, [mailboxCandidates]);

  const copyMailboxList = useCallback(async () => {
    const lines = compactArray(mailboxCandidates).map((candidate) => {
      const customerId = candidate.customer?.id;
      const displayName = customerId
        ? normalizeUtfText(mailboxDrafts[customerId] || candidate.display_profile?.display_name || candidate.suggested_display_name)
        : '';

      return [
        displayName || '-',
        normalizeUtfText(candidate.customer?.company_name || '-'),
        normalizeUtfText(candidate.customer?.name || '-'),
        normalizeUtfText(candidate.customer?.email || candidate.subscription?.email || '-'),
        candidate.display_profile?.display_name ? 'aktiv' : 'offen',
      ].join('\t');
    });

    const text = ['Anzeigename\tFirma\tName\tE-Mail\tStatus', ...lines].join('\n');

    try {
      await navigator.clipboard.writeText(text);
      alert('Briefkastenliste wurde kopiert.');
    } catch (err) {
      alert(text);
    }
  }, [mailboxCandidates, mailboxDrafts]);

  const runArchiveMaintenance = useCallback(async () => {
    if (!token || runningArchiveMaintenance) return;

    setRunningArchiveMaintenance(true);
    try {
      const response = await fetch(`${API_URL}/admin/verifications/archive/run`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Archivierung konnte nicht ausgeführt werden.');
      }
      alert(normalizeUtfText(data.message || 'Archivierung wurde ausgeführt.'));
      await fetchData();
    } catch (err) {
      alert(normalizeUtfText(err.message || 'Archivierung konnte nicht ausgeführt werden.'));
    } finally {
      setRunningArchiveMaintenance(false);
    }
  }, [fetchData, runningArchiveMaintenance, token]);

  const refreshShopifyOrders = useCallback(async () => {
    if (!token) return [];

    const response = await fetch(`${API_URL}/admin/orders`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json().catch(() => ([]));

    if (!response.ok) {
      throw new Error(data.error || 'Bestellungen konnten nicht aktualisiert werden.');
    }

    const normalized = compactArray(data).map(normalizeShopifyOrderItem).filter(hasStableId);
    setShopifyOrders(normalized);
    return normalized;
  }, [token]);

  const handlePortalRequestAction = useCallback(async (requestId, action, extraPayload = {}) => {
    if (!token || !requestId) return;

    const confirmed = window.confirm(
      action === 'approve'
        ? 'Diese Portal-Anfrage wirklich freigeben?'
        : 'Diese Portal-Anfrage wirklich ablehnen'
    );

    if (!confirmed) return;

    const adminNote = window.prompt(
      action === 'approve'
        ? 'Optionale Nachricht an den Kunden zur Freigabe:'
        : 'Optionale Nachricht an den Kunden zur Ablehnung:',
      ''
    );

    setWorkingPortalRequestId(requestId);

    try {
      const response = await fetch(`${API_URL}/admin/portal-requests/${requestId}/${action}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ admin_note: adminNote || undefined, ...extraPayload }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Portal-Anfrage konnte nicht bearbeitet werden.');
      }

      alert(normalizeUtfText(data.message || 'Portal-Anfrage wurde bearbeitet.'));
      await fetchData();
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Portal-Anfrage konnte nicht bearbeitet werden.'));
    } finally {
      setWorkingPortalRequestId(null);
    }
  }, [fetchData, token]);

  const createMailPostageCheckout = useCallback(async (requestId) => {
    const amount = normalizeUtfText(postageDrafts[requestId]);
    if (!amount) {
      alert('Bitte die tatsächlichen Versandkosten eintragen.');
      return;
    }
    setCreatingPostageCheckoutId(requestId);
    try {
      const response = await fetch(`${API_URL}/admin/portal-requests/${requestId}/postage-checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ shipping_cost_amount: amount }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Stripe-Zahlungslink konnte nicht erstellt werden.');
      alert(data.message || 'Stripe-Zahlungslink wurde erstellt.');
      await fetchData();
    } catch (err) {
      alert(normalizeUtfText(err.message || 'Stripe-Zahlungslink konnte nicht erstellt werden.'));
    } finally {
      setCreatingPostageCheckoutId(null);
    }
  }, [fetchData, postageDrafts, token]);

  const loadWithdrawalPreview = useCallback(async (requestId) => {
    if (!token || !requestId) return null;
    setLoadingWithdrawalPreviewId(requestId);
    try {
      const response = await fetch(`${API_URL}/admin/portal-requests/${requestId}/withdrawal-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Erstattungsvorschau konnte nicht geladen werden.');
      setWithdrawalPreviews((current) => ({ ...current, [requestId]: data.preview }));
      return data.preview;
    } catch (err) {
      alert(normalizeUtfText(err.message || 'Erstattungsvorschau konnte nicht geladen werden.'));
      return null;
    } finally {
      setLoadingWithdrawalPreviewId(null);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate(buildAdminLoginPath(), { replace: true });
      return;
    }

    fetchData();
  }, [navigate, token, fetchData]);


  const copyText = useCallback(async (value, fieldKey) => {
    const textToCopy = normalizeUtfText(value);

    if (!textToCopy) {
      return;
    }

    try {
      if (navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const tempTextarea = document.createElement('textarea');
        tempTextarea.value = textToCopy;
        tempTextarea.setAttribute('readonly', '');
        tempTextarea.style.position = 'absolute';
        tempTextarea.style.left = '-9999px';
        document.body.appendChild(tempTextarea);
        tempTextarea.select();
        document.execCommand('copy');
        document.body.removeChild(tempTextarea);
      }

      setCopiedFieldKey(fieldKey);
      window.setTimeout(() => {
        setCopiedFieldKey((current) => (current === fieldKey ? '' : current));
      }, 1800);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      alert('Kopieren fehlgeschlagen. Bitte erneut versuchen.');
    }
  }, []);

  const renderCopyButton = useCallback(
    (value, fieldKey) => {
      const normalizedValue = normalizeUtfText(value);
      const isCopied = copiedFieldKey === fieldKey;

      return (
        <button
          type="button"
          className={`admin-inline-copy-button ${isCopied ? 'admin-inline-copy-button--copied' : ''}`}
          onClick={() => copyText(normalizedValue, fieldKey)}
          disabled={!normalizedValue}
          title={isCopied ? 'Kopiert' : 'Kopieren'}
          aria-label={isCopied ? 'Kopiert' : 'Kopieren'}
        >
          {isCopied ? '✓' : 'Kop.'}
        </button>
      );
    },
    [copiedFieldKey, copyText]
  );


  const regenerateUsageAgreementPdf = useCallback(async (verificationId, type, hasExistingAgreement = true) => {
    const normalizedType = normalizeUtfText(type).toLowerCase();
    const regenerateKey = `${verificationId}-regenerate-${normalizedType}`;
    const actionVerb = hasExistingAgreement ? 'ersetzen' : 'erstellen';

    if (!window.confirm(`Nutzungsvereinbarung aus den aktuell gespeicherten Daten ${actionVerb}`)) {
      return;
    }

    setDownloadingAgreementKey(regenerateKey);

    try {
      const response = await fetch(`${API_URL}/admin/verifications/${verificationId}/usage-agreement/regenerate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: normalizedType, replace_existing: true }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || 'Neue Nutzungsvereinbarung konnte nicht erstellt werden.');
      }

      alert(payload.message || (hasExistingAgreement ? 'Nutzungsvereinbarung wurde ersetzt.' : 'Nutzungsvereinbarung wurde erstellt.'));
      await refreshVerification(verificationId);
    } catch (err) {
      console.error('Usage agreement regeneration failed:', err);
      alert(err.message || 'Neue Nutzungsvereinbarung konnte nicht erstellt werden.');
    } finally {
      setDownloadingAgreementKey('');
    }
  }, [refreshVerification, token]);

  const renderOrderCustomerBlock = useCallback(
    (order) => {
      const fieldRows = [
        { shortLabel: 'Name', value: order.full_name, key: `order-${order.id}-name`, className: '' },
        { shortLabel: 'Mail', value: order.email, key: `order-${order.id}-email`, className: 'is-email' },
        { shortLabel: 'Firma', value: order.company_name, key: `order-${order.id}-company`, className: '' },
        {
          shortLabel: 'Adr.',
          value: order.customer_address_formatted,
          key: `order-${order.id}-address`,
          className: 'is-address',
        },
      ];

      return (
        <div className="admin-order-customer-compact">
          {fieldRows.map((field) => (
            <div key={field.key} className={`admin-order-customer-compact__row ${field.className}`.trim()}>
              <span className="admin-order-customer-compact__label">{field.shortLabel}:</span>
              <span className="admin-order-customer-compact__value">{field.value || '-'}</span>
              {renderCopyButton(field.value, field.key)}
            </div>
          ))}
        </div>
      );
    },
    [renderCopyButton]
  );

  const normalizedSearchTerm = useMemo(() => getSearchValue(searchTerm), [searchTerm]);

  const visibleVerifications = useMemo(() => {
    return verifications.filter((ver) => {
      const archiveStatus = normalizeUtfText(ver.archive_status || 'active').toLowerCase();
      const archiveBucket = normalizeUtfText(ver.archive_bucket).toLowerCase();

      if (archiveFilter === 'active' && archiveStatus !== 'active') {
        return false;
      }

      if (archiveFilter === 'completed' && archiveBucket !== 'completed') {
        return false;
      }

      if (archiveFilter === 'cancelled' && archiveBucket !== 'cancelled') {
        return false;
      }

      if (archiveFilter === 'archived' && archiveStatus !== 'archived') {
        return false;
      }

      const businessRelevant = isBusinessCustomer(ver.customers.customer_type);
      const identityDone = isIdentityLocked(ver.identity_status);
      const businessDone = businessRelevant ? isBusinessLocked(ver.business_status) : true;

      if (hideCompleted && identityDone && businessDone) {
        return false;
      }

      if (!verificationMatchesQuickFilter(ver, quickFilter)) {
        return false;
      }

      return verificationMatchesSearchTerm(ver, normalizedSearchTerm);
    }).sort((left, right) => {
      const leftOpenExpress = left.express_activation && !isIdentityLocked(left.identity_status);
      const rightOpenExpress = right.express_activation && !isIdentityLocked(right.identity_status);
      if (leftOpenExpress !== rightOpenExpress) return leftOpenExpress ? -1 : 1;

      const leftDeadline = left.express_deadline
        ? new Date(left.express_deadline).getTime()
        : Number.POSITIVE_INFINITY;
      const rightDeadline = right.express_deadline
        ? new Date(right.express_deadline).getTime()
        : Number.POSITIVE_INFINITY;
      return leftDeadline - rightDeadline;
    });
  }, [verifications, archiveFilter, hideCompleted, quickFilter, normalizedSearchTerm]);

  useEffect(() => {
    if (!pendingFocusVerificationId) return;

    const visibleTarget = visibleVerifications.find(
      (ver) => String(ver.id) === String(pendingFocusVerificationId)
    );

    if (!visibleTarget) return;

    const targetNode = isMobileLayout
      ? mobileVerificationRowRefs.current[visibleTarget.id]
      : desktopVerificationRowRefs.current[visibleTarget.id];

    if (!targetNode || typeof targetNode.scrollIntoView !== 'function') {
      return;
    }

    targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPendingFocusVerificationId(null);
  }, [isMobileLayout, pendingFocusVerificationId, visibleVerifications]);

  const visibleShopifyOrders = useMemo(() => {
    return shopifyOrders.filter((order) => {
      if (orderProviderFilter !== 'all' && order.provider !== orderProviderFilter) {
        return false;
      }
      if (!orderMatchesQuickFilter(order, quickFilter)) {
        return false;
      }

      if (!normalizedSearchTerm) {
        return true;
      }

      const searchHaystack = [
        order.id,
        order.external_order_id,
        order.order_reference,
        order.provider_label,
        order.full_name,
        order.email,
        order.company_name,
        order.customer_address_formatted,
        order.main_product,
      ]
        .map((item) => getSearchValue(item))
        .join(' ');

      return searchHaystack.includes(normalizedSearchTerm);
    });
  }, [shopifyOrders, orderProviderFilter, quickFilter, normalizedSearchTerm]);

  const visibleLogisticsRequests = useMemo(() => {
    // Bestell- und Verifizierungsfilter gelten bewusst nicht für die eigenständige
    // Logistikansicht. Dort werden ohne separaten Logistikfilter alle Aufträge gezeigt.
    return logisticsRequests;
  }, [logisticsRequests]);

  const dashboardStats = useMemo(() => {
    const openCases = verifications.filter((ver) => verificationMatchesQuickFilter(ver, 'open')).length;
    const newCases = verifications.filter((ver) => verificationMatchesQuickFilter(ver, 'new')).length;
    const verifiedCases = verifications.filter((ver) => verificationMatchesQuickFilter(ver, 'verified')).length;
    const followupCases = verifications.filter((ver) => verificationMatchesQuickFilter(ver, 'followup')).length;
    const reversalCases = verifications.filter((ver) => verificationMatchesQuickFilter(ver, 'reversal')).length;
    const manualReviewOrders = shopifyOrders.filter((order) => orderMatchesQuickFilter(order, 'manual_review')).length;
    const openLogistics = logisticsRequests.filter((item) => !['completed', 'cancelled'].includes(normalizeUtfText(item.status).toLowerCase())).length;

    return {
      openCases,
      newCases,
      verifiedCases,
      followupCases,
      reversalCases,
      manualReviewOrders: manualReviewOrders + openLogistics,
    };
  }, [verifications, shopifyOrders, logisticsRequests]);

  const newUploadCount = useMemo(() => {
    return visibleVerifications.filter((ver) => {
      if (!ver.latest_document_uploaded_at) return false;
      if (!ver.followup_request.created_at) return false;

      return new Date(ver.latest_document_uploaded_at) > new Date(ver.followup_request.created_at);
    }).length;
  }, [visibleVerifications]);

  const activityOverview = useMemo(() => {
    const now = Date.now();
    const last24Hours = 24 * 60 * 60 * 1000;

    const recentOrders = shopifyOrders.filter((order) => {
      if (!order.created_at_shopify) return false;
      const createdAt = new Date(order.created_at_shopify).getTime();
      return Number.isFinite(createdAt) && now - createdAt <= last24Hours;
    });

    const recentUploads = verifications.filter((ver) => {
      if (!ver.latest_document_uploaded_at) return false;
      const uploadedAt = new Date(ver.latest_document_uploaded_at).getTime();
      return Number.isFinite(uploadedAt) && now - uploadedAt <= last24Hours;
    });

    const openReviews = verifications.filter((ver) => {
      const identity = normalizeUtfText(ver.identity_status).toLowerCase();
      const business = normalizeUtfText(ver.business_status).toLowerCase();

      return ['submitted', 'in_review'].includes(identity) || business === 'in_review';
    });

    const followupOpen = verifications.filter((ver) => {
      const identity = normalizeUtfText(ver.identity_status).toLowerCase();
      return identity === 'additional_requested' || !!ver.followup_request.requested_documents.length;
    });

    const openLogistics = logisticsRequests.filter((item) => {
      const status = normalizeUtfText(item.status).toLowerCase();
      return !['completed', 'cancelled'].includes(status);
    });

    const timeline = [];

    recentOrders.forEach((order) => {
      timeline.push({
        key: `order-${order.id}`,
        type: 'order',
        tone: 'info',
        title: order.full_name || order.email || order.shopify_order_name || 'Neue Bestellung',
        subtitle: `Neue Shopify-Bestellung · ${order.shopify_order_name || order.shopify_order_id || '-'}`,
        date: order.created_at_shopify,
        verificationId: order.matched_verification_id || null,
      });
    });

    recentUploads.forEach((ver) => {
      timeline.push({
        key: `upload-${ver.id}`,
        type: 'upload',
        tone: 'warning',
        title: ver.customers.name || ver.customers.email || `Fall #${ver.id}`,
        subtitle: 'Neue Nachreichung / neuer Upload',
        date: ver.latest_document_uploaded_at,
        verificationId: ver.id,
      });
    });

    followupOpen.forEach((ver) => {
      const requestedDocs = Array.isArray(ver.followup_request.requested_documents)
        ? ver.followup_request.requested_documents.filter(Boolean).slice(0, 2).join(', ')
        : '';

      timeline.push({
        key: `followup-${ver.id}`,
        type: 'followup',
        tone: 'neutral',
        title: ver.customers.name || ver.customers.email || `Fall #${ver.id}`,
        subtitle: requestedDocs
          ? `Nachforderung offen · ${requestedDocs}`
          : 'Nachforderung offen',
        date: ver.followup_request.created_at || ver.updated_at || ver.created_at || null,
        verificationId: ver.id,
      });
    });

    openLogistics.slice(0, 6).forEach((item) => {
      timeline.push({
        key: `logistics-${item.id}`,
        type: 'logistics',
        tone: 'info',
        title: item.customer_name || item.customer_email || `Logistik #${item.id}`,
        subtitle: `Logistik · ${item.shopify_order_name || item.shopify_order_id || '-'}`,
        date: item.updated_at || item.created_at,
        verificationId: null,
      });
    });

    const recentActivity = timeline
      .filter((item) => item.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 6);

    return {
      newOrders24h: recentOrders.length,
      newUploads24h: recentUploads.length,
      openReviews: openReviews.length,
      followupsOpen: followupOpen.length,
      recentActivity,
    };
  }, [shopifyOrders, verifications, logisticsRequests]);

  const focusVerificationRow = useCallback((verificationId) => {
    if (!verificationId) return;

    const targetVerification = verifications.find(
      (ver) => String(ver.id) === String(verificationId)
    );
    if (!targetVerification) {
      window.alert(`Fall #${verificationId} wurde nicht gefunden. Die Shopify-Verknüpfung ist möglicherweise veraltet.`);
      return;
    }
    const highlightId = targetVerification.id;

    setHideCompleted(false);
    setArchiveFilter('all');
    setQuickFilter('all');
    setSearchTerm('');
    setHighlightedVerificationId(highlightId);
    setPendingFocusVerificationId(highlightId);

    window.setTimeout(() => {
      setHighlightedVerificationId((current) =>
        current === highlightId ? null : current
      );
    }, 2600);
  }, [verifications]);

  useEffect(() => {
    if (loading || typeof window === 'undefined') return;

    const verificationId = new URLSearchParams(window.location.search).get('verification');
    if (!verificationId || handledDeepLinkVerificationRef.current === verificationId) return;

    handledDeepLinkVerificationRef.current = verificationId;
    openAdminArea('dashboard-focus');
    focusVerificationRow(verificationId);
  }, [focusVerificationRow, loading, openAdminArea]);

  const updateStatus = async (
    id,
    identityStatus,
    businessStatus,
    extraPayload = {}
  ) => {
    const actionKey = String(id);
    if (updatingVerificationIdsRef.current.has(actionKey)) return null;
    updatingVerificationIdsRef.current.add(actionKey);
    setUpdatingVerificationIds((current) => new Set(current).add(actionKey));

    try {
      const response = await fetch(`${API_URL}/admin/verifications/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          identity_status: identityStatus,
          business_status: businessStatus,
          ...extraPayload,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        await refreshVerification(id);
        const nextcloudDetails = data.nextcloud_onboarding?.error
          ? `\n\nNextcloud-Fehler: ${data.nextcloud_onboarding.error}`
          : data.nextcloud_warning
            ? `\n\nNextcloud-Hinweis: ${data.nextcloud_warning}`
            : data.nextcloud_onboarding?.skipped
              ? `\n\nNextcloud nicht ausgeführt: ${data.nextcloud_onboarding.reason || 'Unbekannter Grund'}`
              : '';

        alert(normalizeUtfText(`${data.message || 'Status aktualisiert'}${nextcloudDetails}`));
        return data;
      } else {
        try {
          const refreshed = await refreshVerification(id);
          const identityMatches = !identityStatus || refreshed?.identity_status === identityStatus;
          const businessMatches = !businessStatus || refreshed?.business_status === businessStatus;
          if (identityMatches && businessMatches) {
            alert('Die Aktion wurde trotz fehlerhafter Rückmeldung erfolgreich abgeschlossen. Der aktuelle Status wurde neu geladen.');
            return refreshed;
          }
        } catch (refreshError) {
          console.error('Status recovery after unsuccessful response failed:', refreshError);
        }
        const nextcloudDetails = data.nextcloud_onboarding?.error
          ? `\n\nNextcloud-Fehler: ${data.nextcloud_onboarding.error}`
          : '';

        alert(normalizeUtfText(`${data.error || 'Die Serverrückmeldung war nicht erfolgreich. Bitte nicht erneut klicken, bevor der Status geprüft wurde.'}${nextcloudDetails}`));
        return null;
      }
    } catch (err) {
      console.error(err);
      try {
        const refreshed = await refreshVerification(id);
        const identityMatches = !identityStatus || refreshed?.identity_status === identityStatus;
        const businessMatches = !businessStatus || refreshed?.business_status === businessStatus;
        if (identityMatches && businessMatches) {
          alert('Die Aktion wurde trotz unterbrochener Rückmeldung erfolgreich abgeschlossen. Der aktuelle Status wurde neu geladen.');
          return refreshed;
        }
      } catch (refreshError) {
        console.error('Status recovery after update failure failed:', refreshError);
      }
      alert('Die Rückmeldung des Servers wurde unterbrochen. Bitte nicht erneut klicken; der aktuelle Status wird geprüft.');
      return null;
    } finally {
      updatingVerificationIdsRef.current.delete(actionKey);
      setUpdatingVerificationIds((current) => {
        const next = new Set(current);
        next.delete(actionKey);
        return next;
      });
    }
  };

  const approveIdentityAndBusiness = async (verification) => {
    if (!verification.id) return;
    if (updatingVerificationIdsRef.current.has(String(verification.id))) return;

    const retryNextcloudOnboarding =
      isIdentityLocked(verification.identity_status) &&
      isBusinessLocked(verification.business_status) &&
      !normalizeUtfText(verification.customers?.nextcloud_user_id);

    const confirmed = window.confirm(
      retryNextcloudOnboarding
        ? 'Die fachliche Freigabe ist bereits abgeschlossen. Jetzt ausschließlich das fehlgeschlagene Nextcloud-Onboarding erneut versuchen?'
        : 'Identität und Unternehmen gleichzeitig freigeben Es wird nur die Abschlussmail gesendet, keine separate Mail zu offenen Unternehmensunterlagen.'
    );

    if (!confirmed) return;

    await updateStatus(verification.id, 'approved', 'approved', {
      approval_mode: 'combined',
    });
  };

  const createStripeCheckout = async (verification) => {
    const customerId = verification.customers?.id;
    if (!customerId) return;
    try {
      const response = await fetch(`${API_URL}/admin/customers/${customerId}/stripe-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ legal_entity_id: verification.legal_entity_id || null }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Stripe-Buchungslink konnte nicht erstellt werden.');
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      alert(normalizeUtfText(error.message));
    }
  };

  const confirmReversal = async (verification) => {
    if (!verification.id) return;

    setWorkingReversalId(verification.id);

    try {
      let confirmationText =
        'Rückabwicklung erledigen? Das Seal-Abonnement wird beendet, die Shopify-Erstattung ausgelöst, Nextcloud bereinigt, der Fall final markiert und die Kundenmail versendet.';

      if (verification.stripe_billing) {
        const previewResponse = await fetch(
          `${API_URL}/admin/verifications/${verification.id}/reversal/preview`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const previewData = await previewResponse.json().catch(() => ({}));
        if (!previewResponse.ok) {
          throw new Error(
            previewData.error
            || 'Die ursprüngliche Stripe-Zahlung konnte nicht sicher zugeordnet werden. Es wurde nichts rückabgewickelt.'
          );
        }
        const preview = previewData.preview || {};
        const planAmount = formatRefundPreviewAmount({
          amount: preview.plan_amount,
          currency: preview.currency,
        });
        const expressAmount = formatRefundPreviewAmount({
          amount: preview.express_amount,
          currency: preview.currency,
        });
        confirmationText = [
          'Letzte Sicherheitsprüfung',
          '',
          `Tarif-/Grundbetrag: ${planAmount}`,
          `Express-Prüfung: ${preview.includes_express ? expressAmount : 'nicht enthalten'}`,
          `Gesamte Rückerstattung: ${formatRefundPreviewAmount(preview)}`,
          '',
          'Jetzt genau diese ursprüngliche Stripe-Zahlung erstatten, das Abonnement beenden, Nextcloud bereinigen und den Fall final rückabwickeln?',
        ].join('\n');
      }

      if (!window.confirm(confirmationText)) return;

      const response = await fetch(`${API_URL}/admin/verifications/${verification.id}/reversal/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Rückabwicklung konnte nicht erledigt werden.');
      }

      if (data.verification_deleted) {
        setVerifications((prev) => prev.filter((item) => String(item.id) !== String(verification.id)));
        setFollowupDrafts((prev) => {
          const next = { ...prev };
          delete next[verification.id];
          return next;
        });
      } else {
        await refreshVerification(verification.id);
      }
      alert(normalizeUtfText(data.message || 'Rückabwicklung wurde erledigt.'));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Rückabwicklung konnte nicht erledigt werden.'));
    } finally {
      setWorkingReversalId(null);
    }
  };

  const extendReversalDeadline = async (verification) => {
    if (!verification.id) return;

    const currentDeadline = getVerificationReversalDeadline(verification);
    const defaultValue = Number.isNaN(currentDeadline.getTime())
      ? '7'
      : currentDeadline.toISOString().slice(0, 10);
    const choice = window.prompt(
      'Frist verlängern: 7 für +7 Tage, 14 für +14 Tage oder eigenes Datum im Format YYYY-MM-DD:',
      '7'
    );

    if (!choice) return;

    const normalizedChoice = choice.trim();
    const daysToAdd = normalizedChoice === '7' || normalizedChoice === '+7'
      ? 7
      : normalizedChoice === '14' || normalizedChoice === '+14'
        ? 14
        : null;
    let nextDate = normalizedChoice;

    if (daysToAdd) {
      const now = new Date();
      const base =
        !Number.isNaN(currentDeadline.getTime()) && currentDeadline > now
          ? currentDeadline
          : now;
      nextDate = new Date(base.getTime() + daysToAdd * 24 * 60 * 60 * 1000).toISOString();
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedChoice)) {
      alert(`Bitte 7, 14 oder ein Datum wie ${defaultValue || '2026-07-31'} eingeben.`);
      return;
    }

    setWorkingReversalId(verification.id);

    try {
      const response = await fetch(`${API_URL}/admin/verifications/${verification.id}/reversal/extend-deadline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ extended_until: nextDate }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Frist konnte nicht verlängert werden.');
      }

      await refreshVerification(verification.id);
      alert(normalizeUtfText(data.message || 'Frist wurde verlängert.'));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Frist konnte nicht verlängert werden.'));
    } finally {
      setWorkingReversalId(null);
    }
  };

  const toggleDocuments = async (verificationId) => {
    const isOpen = !!openRows[verificationId];

    if (isOpen) {
      setOpenRows((prev) => ({
        ...prev,
        [verificationId]: false,
      }));
      return;
    }

    try {
        const response = await fetch(`${API_URL}/admin/verifications/${verificationId}/documents`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (response.ok) {
          setDocumentsByVerification((prev) => ({
            ...prev,
            [verificationId]: compactArray(data).map((doc) => ({
              ...doc,
              document_type: normalizeUtfText(doc.document_type),
              file_path: normalizeUtfText(doc.file_path),
            })),
          }));
        } else {
          alert(normalizeUtfText(data.error || 'Fehler beim Laden der Dokumente'));
          return;
        }
    } catch (err) {
      console.error(err);
      alert('Fehler beim Laden der Dokumente');
      return;
    }

    setOpenRows((prev) => ({
      ...prev,
      [verificationId]: true,
    }));
  };

  const openAdminFile = async (url) => {
    if (!url) {
      alert('Datei-Link fehlt.');
      return;
    }

    if (/^https:\/\//i.test(url)) {
      const externalWindow = window.open(url, '_blank');
      if (externalWindow) {
        externalWindow.opener = null;
      }
      return;
    }

    const fileWindow = window.open('', '_blank');

    try {
      if (fileWindow) {
        fileWindow.opener = null;
        fileWindow.document.title = 'Dokument wird geladen...';
        fileWindow.document.body.innerHTML = '<p style="font-family:Arial,sans-serif;padding:24px;">Dokument wird geladen...</p>';
      }

      const apiUrl = url.startsWith('/api/')
        ? url
        : `${API_URL}${url}`;

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Datei konnte nicht geladen werden.');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      if (fileWindow) {
        fileWindow.location.href = objectUrl;
      } else {
        const fallbackWindow = window.open(objectUrl, '_blank');
        if (fallbackWindow) {
          fallbackWindow.opener = null;
        }
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60 * 1000);
    } catch (err) {
      if (fileWindow) {
        fileWindow.close();
      }
      console.error(err);
      alert(normalizeUtfText(err.message || 'Datei konnte nicht geladen werden.'));
    }
  };

  const renderDocumentDownload = (doc) => {
    const label = 'Dokument öffnen';

    return (
      <button
        type="button"
        className="link-button"
        onClick={() => openAdminFile(doc.signed_url)}
      >
        {label}
      </button>
    );
  };

  const getOrderActionMeta = (order) => {
    const actions = [];

    if (order.matched_verification_id) {
      actions.push({
        disabled: false,
        label: `Zu Fall #${order.matched_verification_id}`,
        tone: 'secondary',
        action: () => focusVerificationRow(order.matched_verification_id),
      });
    } else if (order.provider === 'stripe') {
      actions.push({ disabled: true, label: 'Kein Fall verknüpft', tone: 'secondary' });
    } else if (
      order.verification_status === 'existing_verification_reused' ||
      order.verification_status === 'already_verified'
    ) {
      actions.push({
        disabled: true,
        label: 'Fertig',
        tone: 'secondary',
      });
    } else {
      actions.push({
        disabled: false,
        label: 'Anfragen',
        tone: 'primary',
        action: () => requestVerificationForOrder(order),
      });
    }

    const allowAlreadyVerifiedButton = [
      'awaiting_manual_review',
      'pending_manual_review',
      'auto_verification_eligible',
    ].includes(order.verification_status);

    if (order.verification_status === 'company_data_pending') {
      actions.push({
        disabled: true,
        label: 'Unternehmensdaten offen',
        tone: 'secondary',
      });
    } else if (allowAlreadyVerifiedButton) {
      actions.push({
        disabled: false,
        label: 'Als erledigt',
        tone: 'success',
        action: () => markOrderAsAlreadyVerified(order),
      });
    }

    if (order.email && order.provider !== 'stripe') {
      const premiumMeta = getPremiumOfferStatusMeta(order.premium_offer_status);

      if (!premiumMeta) {
        actions.push({
          disabled: false,
          label: 'Premium anbieten',
          tone: 'success',
          action: () => sendPremiumOfferForOrder(order),
        });
      } else {
        actions.push({
          disabled: true,
          label: premiumMeta.label,
          tone: premiumMeta.tone === 'success' ? 'success' : 'secondary',
        });
      }
    }

    if (order.provider === 'shopify_seal') {
      actions.push({
        disabled: !order.email,
        label: order.email ? 'Zahlungsart-E-Mail senden' : 'Kunden-E-Mail fehlt',
        tone: 'primary',
        action: order.email ? () => sendSealPaymentEmail(order) : undefined,
      });
      if (order.matched_verification_id && order.email) {
        actions.push({
          disabled: false,
          label: 'Hub-Einladung senden',
          tone: 'secondary',
          action: () => sendOrderHubActivation(order),
        });
      }
    }

    return actions;
  };

  const sendSealPaymentEmail = async (order) => {
    const confirmed = window.confirm(
      `Soll die offizielle E-Mail zur Änderung der Zahlungsart an ${order.email || 'die hinterlegte Kundenadresse'} gesendet werden? Ein Business-Hub-Konto ist dafür nicht erforderlich.`
    );
    if (!confirmed) return;
    setSendingSealPaymentEmailId(order.id);
    try {
      const response = await fetch(`${API_URL}/admin/shopify-orders/${order.id}/send-payment-method-update`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Zahlungsart-E-Mail konnte nicht gesendet werden.');
      alert(normalizeUtfText(data.message));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Zahlungsart-E-Mail konnte nicht gesendet werden.'));
    } finally {
      setSendingSealPaymentEmailId(null);
    }
  };

  const sendOrderHubActivation = async (order) => {
    const confirmed = window.confirm(
      `Soll zusätzlich eine separate Business-Hub-Einladung an ${order.email} gesendet werden? Die Seal-Zahlungsart kann unabhängig davon geändert werden.`
    );
    if (!confirmed) return;
    setSendingOrderActivationId(order.id);
    try {
      const response = await fetch(`${API_URL}/admin/verifications/${order.matched_verification_id}/send-account-activation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Hub-Einladung konnte nicht gesendet werden.');
      alert(normalizeUtfText(data.message));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Hub-Einladung konnte nicht gesendet werden.'));
    } finally {
      setSendingOrderActivationId(null);
    }
  };

  const sendPremiumOfferForOrder = async (order) => {
    const confirmed = window.confirm(
      `Soll der Bestellung ${order.shopify_order_name || order.shopify_order_id} ein kostenloses Premium-Testangebot gesendet werden`
    );

    if (!confirmed) {
      return;
    }

    setSendingPremiumOfferId(order.id);

    try {
      const response = await fetch(`${API_URL}/admin/shopify-orders/${order.id}/premium-offer`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setShopifyOrders((prev) => prev.map((item) => (
          item.id === order.id
            ? {
                ...item,
                premium_offer_status: normalizeUtfText(data.offer.status || 'sent'),
                premium_offer_start_date: data.offer.start_date || item.premium_offer_start_date,
                premium_offer_end_date: data.offer.end_date || item.premium_offer_end_date,
              }
            : item
        )));
        alert(normalizeUtfText(data.message || 'Premium-Angebot wurde gesendet.'));
      } else {
        alert(normalizeUtfText(data.error || 'Premium-Angebot konnte nicht gesendet werden.'));
      }
    } catch (err) {
      console.error(err);
      alert('Premium-Angebot konnte nicht gesendet werden.');
    } finally {
      setSendingPremiumOfferId(null);
    }
  };

  const requestVerificationForOrder = async (order) => {
    const confirmed = window.confirm(
      `Soll für die Bestellung ${order.shopify_order_name || order.shopify_order_id} jetzt eine Verifizierung angefragt werden`
    );

    if (!confirmed) {
      return;
    }

    const note = window.prompt(
      'Optionale interne Notiz für diese Verifizierungsanfrage:',
      ''
    );

    setRequestingOrderId(order.id);

    try {
      const response = await fetch(`${API_URL}/admin/shopify-orders/${order.id}/request-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          note: note || null,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        await refreshShopifyOrders();
        if (data.verification_id) {
          await refreshVerification(data.verification_id);
        }

        const successMessage = data.reused_existing_verification
          ? (
              data.message ||
              `Bestehender Verifizierungsfall wurde wiederverwendet${data.verification_id ? ` (Fall #${data.verification_id})` : ''}.`
            )
          : (
              data.message ||
              `Verifizierungsfall wurde erstellt${data.verification_id ? ` (Fall #${data.verification_id})` : ''}.`
            );

        const fullMessage = data.warning
          ? `${successMessage}\n\nHinweis: ${data.warning}`
          : successMessage;

        alert(normalizeUtfText(fullMessage));
      } else {
        alert(normalizeUtfText(data.error || 'Fehler beim Anfragen der Verifizierung'));
      }
    } catch (err) {
      console.error(err);
      alert('Fehler beim Anfragen der Verifizierung');
    } finally {
      setRequestingOrderId(null);
    }
  };

  const markOrderAsAlreadyVerified = async (order) => {
    const confirmed = window.confirm(
      `Soll die Bestellung ${order.shopify_order_name || order.shopify_order_id} als bereits verifiziert markiert werden\n\nDabei wird keine Verifizierungs-E-Mail versendet und der Kunde gilt als vollständig erledigt.`
    );

    if (!confirmed) {
      return;
    }

    const note = window.prompt(
      'Optionale interne Notiz für "Bereits verifiziert":',
      ''
    );

    setRequestingOrderId(order.id);

    try {
      const response = await fetch(`${API_URL}/admin/shopify-orders/${order.id}/mark-already-verified`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          note: note || null,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        await refreshShopifyOrders();
        if (data.verification_id) {
          await refreshVerification(data.verification_id);
        }
        alert(normalizeUtfText(data.message || 'Kunde wurde als bereits verifiziert markiert.'));
        if (data.verification_id) {
          focusVerificationRow(data.verification_id);
        }
      } else {
        alert(normalizeUtfText(data.error || 'Fehler beim Markieren als bereits verifiziert'));
      }
    } catch (err) {
      console.error(err);
      alert('Fehler beim Markieren als bereits verifiziert');
    } finally {
      setRequestingOrderId(null);
    }
  };

  const resetVerification = async (verificationId) => {
    const confirmed = window.confirm(
      'Soll diese Verifizierung wirklich zurückgesetzt werden Der Kunde kann den bestehenden Link danach erneut ausfüllen. Alte Dokumentverknüpfungen werden aus der aktiven Ansicht entfernt und im internen Hinweis protokolliert.'
    );

    if (!confirmed) {
      return;
    }

    const note = window.prompt(
      'Optionaler interner Hinweis zum Reset, z. B. „Kunde hat versehentlich Privat statt Gewerbe ausgewählt“:',
      ''
    );

    setSavingResetId(verificationId);

    try {
      const response = await fetch(`${API_URL}/admin/verifications/${verificationId}/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          note: note || null,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setDocumentsByVerification((prev) => {
          const next = { ...prev };
          delete next[verificationId];
          return next;
        });
        await refreshVerification(verificationId);
        await refreshShopifyOrders();
        alert(normalizeUtfText(data.message || 'Verifizierung wurde zurückgesetzt.'));
        focusVerificationRow(verificationId);
      } else {
        alert(normalizeUtfText(data.error || 'Fehler beim Zurücksetzen'));
      }
    } catch (err) {
      console.error(err);
      alert('Fehler beim Zurücksetzen');
    } finally {
      setSavingResetId(null);
    }
  };

  const buildVerificationEditDraft = (ver) => {
    const fullName = ver.customers.name || [ver.usage_agreement_data.first_name, ver.usage_agreement_data.last_name]
      .map(normalizeUtfText)
      .filter(Boolean)
      .join(' ');
    const { firstName, lastName } = splitFullName(fullName);
    const companyName = ver.customers.company_name || ver.usage_agreement_data.company_name || '';

    return {
      customer: {
        name: fullName,
        email: ver.customers.email || '',
        company_name: companyName,
        customer_type: normalizeAdminCustomerType(ver.customers.customer_type),
      },
      usage_agreement_data: {
        agreement_type: ver.usage_agreement_data.agreement_type || mapCustomerTypeToAgreementType(ver.customers.customer_type) || 'privat',
        first_name: ver.usage_agreement_data.first_name || firstName,
        last_name: ver.usage_agreement_data.last_name || lastName,
        email: ver.usage_agreement_data.email || ver.customers.email || '',
        phone: ver.usage_agreement_data.phone || '',
        birth_date: ver.usage_agreement_data.birth_date || '',
        street: ver.usage_agreement_data.street || '',
        postal_code: ver.usage_agreement_data.postal_code || '',
        city: ver.usage_agreement_data.city || '',
        country: ver.usage_agreement_data.country || '',
        company_name: companyName,
        legal_form: ver.usage_agreement_data.legal_form || '',
        representative_name: ver.usage_agreement_data.representative_name || fullName,
        business_activity: ver.usage_agreement_data.business_activity || '',
        company_street: ver.usage_agreement_data.company_street || '',
        company_postal_code: ver.usage_agreement_data.company_postal_code || '',
        company_city: ver.usage_agreement_data.company_city || '',
        company_country: ver.usage_agreement_data.company_country || '',
        register_court: ver.usage_agreement_data.register_court || '',
        register_number: ver.usage_agreement_data.register_number || '',
        tax_number: ver.usage_agreement_data.tax_number || '',
        vat_id: ver.usage_agreement_data.vat_id || '',
        planned_foundation_date: ver.usage_agreement_data.planned_foundation_date || '',
      },
    };
  };

  const startVerificationEdit = (ver) => {
    setVerificationEditDrafts((prev) => ({
      ...prev,
      [ver.id]: prev[ver.id] || buildVerificationEditDraft(ver),
    }));
    setEditingVerificationId(ver.id);
    setOpenRows((prev) => ({ ...prev, [ver.id]: true }));
  };

  const updateVerificationEditDraft = (verificationId, section, field, value) => {
    setVerificationEditDrafts((prev) => {
      const current = prev[verificationId] || {};
      const currentCustomer = current.customer || {};
      const currentAgreement = current.usage_agreement_data || {};
      const nextDraft = {
        ...current,
        [section]: {
          ...(current[section] || {}),
          [field]: value,
        },
      };

      if (section === 'customer' && field === 'name') {
        const { firstName, lastName } = splitFullName(value);
        nextDraft.usage_agreement_data = {
          ...currentAgreement,
          first_name: firstName,
          last_name: lastName,
          representative_name: value,
        };
      }

      if (section === 'customer' && field === 'email') {
        nextDraft.usage_agreement_data = {
          ...currentAgreement,
          email: value,
        };
      }

      if (section === 'customer' && field === 'company_name') {
        nextDraft.usage_agreement_data = {
          ...currentAgreement,
          company_name: value,
        };
      }

      if (section === 'customer' && field === 'customer_type') {
        nextDraft.usage_agreement_data = {
          ...currentAgreement,
          agreement_type: mapCustomerTypeToAgreementType(value) || currentAgreement.agreement_type || 'privat',
        };
      }

      if (section === 'usage_agreement_data' && field === 'company_name') {
        nextDraft.customer = {
          ...currentCustomer,
          company_name: value,
        };
      }

      return {
        ...prev,
        [verificationId]: nextDraft,
      };
    });
  };

  const saveVerificationEdit = async (ver) => {
    const draft = verificationEditDrafts[ver.id] || buildVerificationEditDraft(ver);
    const confirmed = window.confirm(
      'Diese Kunden-/Vertragsdaten speichern? Bei einer Firmen- oder Vertragsänderung werden Nextcloud und eine automatisch abgeleitete Briefkastenanzeige synchronisiert, der Unternehmensstatus auf „In Prüfung“ gesetzt und die Nutzungsvereinbarung ersetzt. Individuelle Briefkastenwünsche bleiben erhalten.'
    );

    if (!confirmed) return;

    setSavingVerificationEditId(ver.id);

    try {
      const response = await fetch(`${API_URL}/admin/verifications/${ver.id}/customer-data`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer: draft.customer,
          usage_agreement_data: draft.usage_agreement_data,
          sync_nextcloud: true,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Daten konnten nicht gespeichert werden.');
      }

      let agreementUpdateMessage = '';
      if (data.agreement_regeneration_required) {
        const agreementType = normalizeUtfText(draft.usage_agreement_data?.agreement_type || draft.customer?.customer_type || 'privat').toLowerCase();
        const agreementResponse = await fetch(`${API_URL}/admin/verifications/${ver.id}/usage-agreement/regenerate`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ type: agreementType, replace_existing: true }),
        });
        const agreementData = await agreementResponse.json().catch(() => ({}));
        agreementUpdateMessage = agreementResponse.ok
          ? '\n\nNutzungsvereinbarung und Address Usage Confirmation wurden ersetzt.'
          : `\n\nWICHTIG: Daten wurden gespeichert, aber die Nutzungsvereinbarung konnte nicht automatisch ersetzt werden: ${agreementData.error || 'Unbekannter Fehler'}`;
      }

      await refreshVerification(ver.id);
      setEditingVerificationId(null);
      const nextcloudDetails = data.nextcloud_warning
        ? `\n\nNextcloud-Hinweis: ${data.nextcloud_warning}`
        : '';
      const mailboxDetails = data.mailbox_display_sync?.reason === 'custom_customer_choice_preserved'
        ? '\n\nBriefkastenanzeige: Individueller Kundenwunsch wurde beibehalten.'
        : data.mailbox_display_sync?.updated
          ? `\n\nBriefkastenanzeige wurde auf „${data.mailbox_display_sync.display_name}“ aktualisiert.`
          : '';
      const businessDetails = data.business_status === 'in_review'
        ? '\n\nUnternehmensstatus wurde auf „In Prüfung“ gesetzt.'
        : '';
      alert(normalizeUtfText(`${data.message || 'Daten wurden gespeichert.'}${nextcloudDetails}${mailboxDetails}${businessDetails}${agreementUpdateMessage}`));
      focusVerificationRow(ver.id);
    } catch (err) {
      console.error('Verification edit failed:', err);
      alert(err.message || 'Daten konnten nicht gespeichert werden.');
    } finally {
      setSavingVerificationEditId(null);
    }
  };

  const reopenVerification = async (verificationId) => {
    const confirmed = window.confirm(
      'Verifizierung wieder öffnen Der Kunde kann den bestehenden Link danach erneut bearbeiten. Bestehende Dokumente bleiben als Historie erhalten.'
    );

    if (!confirmed) return;

    const note = window.prompt(
      'Optionaler interner Hinweis, z. B. „Kunde hat falsche Einstellungen gewählt“:',
      ''
    );

    setReopeningVerificationId(verificationId);

    try {
      const response = await fetch(`${API_URL}/admin/verifications/${verificationId}/reopen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ note: note || null }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Verifizierung konnte nicht wieder geöffnet werden.');
      }

      await refreshVerification(verificationId);
      await refreshShopifyOrders();
      alert(normalizeUtfText(data.message || 'Verifizierung wurde wieder geöffnet.'));
      focusVerificationRow(verificationId);
    } catch (err) {
      console.error('Verification reopen failed:', err);
      alert(err.message || 'Verifizierung konnte nicht wieder geöffnet werden.');
    } finally {
      setReopeningVerificationId(null);
    }
  };


  const deleteVerificationCase = async (verificationId) => {
    const confirmed = window.confirm(
      'Soll dieser Verifizierungsfall wirklich gelöscht werden\n\nDabei werden der Fall und die zugehörigen Dokumente aus der App entfernt.'
    );

    if (!confirmed) {
      return;
    }

    setDeletingVerificationId(verificationId);

    try {
      const response = await fetch(`${API_URL}/admin/verifications/${verificationId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setDocumentsByVerification((prev) => {
          const next = { ...prev };
          delete next[verificationId];
          return next;
        });

        setOpenRows((prev) => {
          const next = { ...prev };
          delete next[verificationId];
          return next;
        });

        setFollowupDrafts((prev) => {
          const next = { ...prev };
          delete next[verificationId];
          return next;
        });

        setVerifications((prev) => prev.filter((item) => String(item.id) !== String(verificationId)));
        await refreshShopifyOrders();

        const fullMessage = data.warning
          ? `${data.message || 'Verifizierungsfall wurde gelöscht.'}\n\nHinweis: ${data.warning}`
          : (data.message || 'Verifizierungsfall wurde gelöscht.');

        alert(normalizeUtfText(fullMessage));
      } else {
        alert(normalizeUtfText(data.error || 'Fehler beim Löschen des Verifizierungsfalls'));
      }
    } catch (err) {
      console.error(err);
      alert('Fehler beim Löschen des Verifizierungsfalls');
    } finally {
      setDeletingVerificationId(null);
    }
  };

  const updateShopifyAutoSetting = async (enabled) => {
    setSavingAutoSetting(true);

    try {
      const response = await fetch(`${API_URL}/admin/settings/shopify-auto-verification`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Einstellung konnte nicht gespeichert werden');
      }

      setSettings((prev) => ({
        ...prev,
        shopify_auto_verification_enabled: !!data.shopify_auto_verification_enabled,
        shopify_auto_verification_updated_at: new Date().toISOString(),
      }));

      alert(`Automatische Verifizierungen sind jetzt ${enabled ? 'aktiviert' : 'deaktiviert'}.`);
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Fehler beim Speichern der Einstellung'));
    } finally {
      setSavingAutoSetting(false);
    }
  };

  const testAdminNotification = async () => {
    setTestingAdminNotification(true);
    try {
      const response = await fetch(`${API_URL}/admin/notifications/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Testbenachrichtigung fehlgeschlagen');
      const destinations = [
        data.delivery?.telegram_sent ? 'Telegram' : null,
        data.delivery?.email_sent ? 'E-Mail' : null,
      ].filter(Boolean).join(' und ');
      alert(`Testbenachrichtigung wurde über ${destinations || 'den konfigurierten Kanal'} versendet.`);
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Testbenachrichtigung fehlgeschlagen'));
    } finally {
      setTestingAdminNotification(false);
    }
  };

  const updateAdminNotificationChannel = async (channel) => {
    if (!['email', 'telegram'].includes(channel) || channel === settings.admin_notification_channel) return;
    setSavingNotificationChannel(true);
    try {
      const response = await fetch(`${API_URL}/admin/settings/admin-notification-channel`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ channel }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Benachrichtigungskanal konnte nicht geändert werden.');
      setSettings((current) => ({
        ...current,
        admin_notification_channel: data.admin_notification_channel,
      }));
      alert(normalizeUtfText(data.message || 'Benachrichtigungskanal wurde geändert.'));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Benachrichtigungskanal konnte nicht geändert werden.'));
    } finally {
      setSavingNotificationChannel(false);
    }
  };

  const saveAdminDashboardCardOrder = async (nextOrder, previousOrder) => {
    setCardOrderSaveError('');
    setSavingAdminCardOrder(true);
    try {
      const response = await fetch(`${API_URL}/admin/settings/admin-dashboard-card-order`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order: nextOrder }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Kartenreihenfolge konnte nicht gespeichert werden.');
      const savedOrder = normalizeAdminDashboardCardOrder(data.admin_dashboard_card_order);
      setAdminDashboardCardOrder(savedOrder);
    } catch (err) {
      console.error('[admin-dashboard] card order save failed:', err);
      setAdminDashboardCardOrder(previousOrder);
      setCardOrderSaveError(normalizeUtfText(err.message || 'Kartenreihenfolge konnte nicht gespeichert werden.'));
    } finally {
      setSavingAdminCardOrder(false);
    }
  };

  const applyAdminDashboardCardOrder = (nextOrder) => {
    if (savingAdminCardOrder) return;
    const normalizedNextOrder = normalizeAdminDashboardCardOrder(nextOrder);
    const previousOrder = adminDashboardCardOrder;
    if (normalizedNextOrder.every((cardId, index) => cardId === previousOrder[index])) return;
    setAdminDashboardCardOrder(normalizedNextOrder);
    saveAdminDashboardCardOrder(normalizedNextOrder, previousOrder);
  };

  const handleAdminCardDrop = (targetCardId) => {
    if (!draggedAdminCardId) return;
    suppressAdminCardClickRef.current = true;
    applyAdminDashboardCardOrder(
      moveAdminDashboardCard(adminDashboardCardOrder, draggedAdminCardId, targetCardId)
    );
    setDraggedAdminCardId('');
    setDragOverAdminCardId('');
    window.setTimeout(() => {
      suppressAdminCardClickRef.current = false;
    }, 0);
  };

  const handleAdminCardKeyboardMove = (event, cardId) => {
    if (savingAdminCardOrder || !event.altKey || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const offset = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
    applyAdminDashboardCardOrder(
      moveAdminDashboardCardByOffset(adminDashboardCardOrder, cardId, offset)
    );
  };

  const toggleFollowupForm = (verificationId) => {
    const currentVerification = verifications.find((item) => item.id === verificationId);

    if (currentVerification.identity_status === 'already_verified') {
      return;
    }

    setFollowupDrafts((prev) => ({
      ...prev,
      [verificationId]: {
        ...(prev[verificationId] || {
          requestedDocuments: [],
          requestedMessage: '',
        }),
        open: !prev[verificationId].open,
      },
    }));
  };

  const handleFollowupCheckboxChange = (verificationId, label) => {
    setFollowupDrafts((prev) => {
      const current = prev[verificationId] || {
        open: true,
        requestedDocuments: [],
        requestedMessage: '',
      };

      const exists = current.requestedDocuments.includes(label);

      return {
        ...prev,
        [verificationId]: {
          ...current,
          requestedDocuments: exists
            ? current.requestedDocuments.filter((item) => item !== label)
            : [...current.requestedDocuments, label],
        },
      };
    });
  };

  const handleFollowupMessageChange = (verificationId, value) => {
    setFollowupDrafts((prev) => ({
      ...prev,
      [verificationId]: {
        ...(prev[verificationId] || {
          open: true,
          requestedDocuments: [],
          requestedMessage: '',
        }),
        requestedMessage: value,
      },
    }));
  };

  const submitFollowupRequest = async (verificationId) => {
    const draft = followupDrafts[verificationId] || {
      requestedDocuments: [],
      requestedMessage: '',
    };

    if (
      draft.requestedDocuments.length === 0 &&
      !String(draft.requestedMessage || '').trim()
    ) {
      alert('Bitte wählen Sie mindestens ein Dokument oder geben Sie einen Hinweis ein.');
      return;
    }

    setSavingFollowupId(verificationId);

    const result = await updateStatus(
      verificationId,
      'additional_requested',
      'pending',
      {
        requested_documents: draft.requestedDocuments,
        requested_message: draft.requestedMessage,
      }
    );

    if (result) {
      setFollowupDrafts((prev) => ({
        ...prev,
        [verificationId]: {
          ...draft,
          open: false,
        },
      }));
    }

    setSavingFollowupId(null);
  };

  const renderUsageAgreementBlock = useCallback(
    (ver, compact = false) => {
      const data = ver.usage_agreement_data;
      const status = getUsageAgreementStatusView(ver.usage_agreement_status, hasUsageAgreementData(data));
      const statusTone = status.tone;
      const statusEvidence = (
        <div className="admin-usage-agreement__rows" aria-label="NV-Statusnachweise">
          <div className="admin-usage-agreement__row"><span className="admin-usage-agreement__label">Status</span><span className="admin-usage-agreement__value"><StatusBadge label={status.label} tone={statusTone} /></span></div>
          <div className="admin-usage-agreement__row"><span className="admin-usage-agreement__label">Erstellt</span><span className="admin-usage-agreement__value">{status.created_at ? formatDateTime(status.created_at) : '-'}</span></div>
          <div className="admin-usage-agreement__row"><span className="admin-usage-agreement__label">PDF-Hash</span><span className="admin-usage-agreement__value">{status.pdf_hash_present ? 'Vorhanden' : 'Nicht vorhanden'}</span></div>
          <div className="admin-usage-agreement__row"><span className="admin-usage-agreement__label">Nextcloud</span><span className="admin-usage-agreement__value">{status.nextcloud_storage_confirmed ? 'Speicherung bestätigt' : 'Nicht bestätigt'}</span></div>
          <div className="admin-usage-agreement__row"><span className="admin-usage-agreement__label">Audit</span><span className="admin-usage-agreement__value">{status.audit_evidence_present ? 'Nachweis vorhanden' : 'Nicht vorhanden'}</span></div>
        </div>
      );

      if (status.fail_closed) {
        return (
          <div className="admin-usage-agreement admin-usage-agreement--empty">
            <div className="admin-usage-agreement__header"><strong>Nutzungsvereinbarung</strong><StatusBadge label={status.label} tone="danger" /></div>
            <p>Der Unternehmensprofil-Scope ist fremd oder mehrdeutig. NV-Daten werden nicht angezeigt.</p>
          </div>
        );
      }

      if (!hasUsageAgreementData(data)) {
        return (
          <div className="admin-usage-agreement admin-usage-agreement--empty">
            <div className="admin-usage-agreement__header">
              <strong>Nutzungsvereinbarung</strong>
              <StatusBadge label={status.label} tone={statusTone} />
            </div>
            {statusEvidence}
            <p>Noch keine profilgebundenen Vertragsstammdaten gespeichert.</p>
          </div>
        );
      }

      const fullName = [data.first_name, data.last_name].map(normalizeUtfText).filter(Boolean).join(' ');
      const privateAddress = formatAgreementAddress(data);
      const companyAddress = formatAgreementAddress(data, 'company_');
      const copyTextValue = buildUsageAgreementCopyText(data, ver.customers || {});
      const agreementType = normalizeUtfText(data.agreement_type) || mapCustomerTypeToAgreementType(ver.customers.customer_type) || 'privat';
      const draft = verificationEditDrafts[ver.id] || buildVerificationEditDraft(ver);
      const editedAgreementType = draft.usage_agreement_data?.agreement_type;
      const selectedAgreementType = editedAgreementType || agreementType;
      const normalizedAgreementType = normalizeUtfText(selectedAgreementType).toLowerCase();
      const isPlannedFoundation = normalizedAgreementType === 'gruendung';
      const missingPersonalFields = getMissingPersonalAgreementFields(data);
      const isAgreementComplete = missingPersonalFields.length === 0;
      const isPrivateAgreement = normalizedAgreementType === 'privat';
      const isFreelancerAgreement = normalizedAgreementType === 'freelancer';
      const isSoleProprietorAgreement = normalizedAgreementType === 'einzelunternehmen';
      const isCompanyAgreement = ['kapitalgesellschaft', 'verein', 'ausland'].includes(normalizedAgreementType);
      const hasExistingAgreement = hasGeneratedUsageAgreement(ver);
      const agreementActionVerb = hasExistingAgreement ? 'ersetzen' : 'erstellen';
      const agreementPdfType = normalizedAgreementType || 'privat';
      const agreementPdfLabel = `Nutzungsvereinbarung ${agreementActionVerb}`;

      const rows = [
        { label: 'Art', value: getAgreementTypeLabel(selectedAgreementType) },
        { label: 'Name', value: fullName || ver.customers.name || '-' },
        { label: 'Geburtsdatum', value: formatDateOnly(data.birth_date) },
        { label: 'Adresse', value: privateAddress, copyKey: `usage-${ver.id}-address` },
        { label: isSoleProprietorAgreement ? 'Gewerbebezeichnung' : isPlannedFoundation ? 'Geplante Firma' : 'Firma', value: isPrivateAgreement ? '' : data.company_name, copyKey: `usage-${ver.id}-company` },
        { label: 'Rechtsform', value: isCompanyAgreement || isPlannedFoundation ? data.legal_form : '' },
        { label: 'Vertreter', value: isCompanyAgreement || isPlannedFoundation ? data.representative_name : '' },
        { label: isFreelancerAgreement ? 'Tätigkeit' : 'Branche', value: data.business_activity },
        { label: 'Unternehmensadresse', value: isCompanyAgreement ? companyAddress : '' },
        { label: 'Registergericht', value: isCompanyAgreement ? data.register_court : '' },
        { label: 'Registernummer', value: isCompanyAgreement ? data.register_number : '' },
        { label: 'Steuernummer', value: data.tax_number },
        { label: 'USt-ID', value: data.vat_id },
        { label: 'Gepl. Gründung', value: formatDateOnly(data.planned_foundation_date) },
      ].filter((row) => normalizeUtfText(row.value));

      return (
        <div className={`admin-usage-agreement ${compact ? 'admin-usage-agreement--compact' : ''}`}>
          <div className="admin-usage-agreement__header">
            <strong>Nutzungsvereinbarung</strong>
            <StatusBadge
              label={!isAgreementComplete ? 'Unvollständig' : isPlannedFoundation ? 'Vorläufig' : 'Daten vorhanden'}
              tone={!isAgreementComplete || isPlannedFoundation ? 'warning' : 'success'}
            />
          </div>

          {statusEvidence}

          <div className="admin-usage-agreement__rows">
            {rows.map((row) => (
              <div key={`${ver.id}-${row.label}`} className="admin-usage-agreement__row">
                <span className="admin-usage-agreement__label">{row.label}</span>
                <span className="admin-usage-agreement__value">{row.value}</span>
                {row.copyKey ? renderCopyButton(row.value, row.copyKey) : null}
              </div>
            ))}
          </div>

          {!isAgreementComplete ? (
            <p className="admin-usage-agreement__notice">
              Fehlende Pflichtfelder: {missingPersonalFields.map(({ label }) => label).join(', ')}.
            </p>
          ) : null}

          {isPlannedFoundation ? (
            <p className="admin-usage-agreement__notice">
              Gründung geplant: Diese Daten können später über den Nachreichungslink korrigiert und finalisiert werden.
            </p>
          ) : null}

          <div className="admin-usage-agreement__actions">
            <label className="admin-usage-agreement__type-select">
              <span>NV-Typ</span>
              <select
                value={agreementPdfType}
                onChange={(event) => {
                  setVerificationEditDrafts((prev) => ({
                    ...prev,
                    [ver.id]: {
                      ...draft,
                      usage_agreement_data: {
                        ...(draft.usage_agreement_data || {}),
                        agreement_type: event.target.value,
                      },
                    },
                  }));
                }}
              >
                {AGREEMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="action-button action-button--secondary"
              onClick={() => copyText(copyTextValue, `usage-${ver.id}-all`)}
            >
              Daten kopieren
            </button>
            <button
              type="button"
              className="action-button action-button--primary"
              onClick={() => regenerateUsageAgreementPdf(ver.id, agreementPdfType, hasExistingAgreement)}
              disabled={downloadingAgreementKey === `${ver.id}-regenerate-${agreementPdfType}`}
              title={`${hasExistingAgreement ? 'Ersetzt die bestehende' : 'Erstellt eine'} Nutzungsvereinbarung aus den aktuell gespeicherten Daten.`}
            >
              {downloadingAgreementKey === `${ver.id}-regenerate-${agreementPdfType}` ? 'Speichere...' : agreementPdfLabel}
            </button>
          </div>
        </div>
      );
    },
    [copyText, downloadingAgreementKey, regenerateUsageAgreementPdf, renderCopyButton, verificationEditDrafts]
  );

  const renderVerificationEditPanel = (ver) => {
    if (editingVerificationId !== ver.id) return null;

    const draft = verificationEditDrafts[ver.id] || buildVerificationEditDraft(ver);
    const customerFields = [
      { section: 'customer', field: 'name', label: 'Name / Vertreter' },
      { section: 'customer', field: 'email', label: 'E-Mail' },
      { section: 'customer', field: 'company_name', label: 'Firma' },
    ];
    const agreementFields = [
      { section: 'usage_agreement_data', field: 'birth_date', label: 'Geburtsdatum', type: 'date' },
      { section: 'usage_agreement_data', field: 'street', label: 'Privat: Straße' },
      { section: 'usage_agreement_data', field: 'postal_code', label: 'Privat: PLZ' },
      { section: 'usage_agreement_data', field: 'city', label: 'Privat: Ort' },
      { section: 'usage_agreement_data', field: 'country', label: 'Privat: Land' },
      { section: 'usage_agreement_data', field: 'legal_form', label: 'Rechtsform' },
      { section: 'usage_agreement_data', field: 'business_activity', label: 'Branche/Tätigkeit' },
      { section: 'usage_agreement_data', field: 'company_street', label: 'Firma: Straße' },
      { section: 'usage_agreement_data', field: 'company_postal_code', label: 'Firma: PLZ' },
      { section: 'usage_agreement_data', field: 'company_city', label: 'Firma: Ort' },
      { section: 'usage_agreement_data', field: 'company_country', label: 'Firma: Land' },
      { section: 'usage_agreement_data', field: 'register_court', label: 'Registergericht' },
      { section: 'usage_agreement_data', field: 'register_number', label: 'Registernummer' },
      { section: 'usage_agreement_data', field: 'tax_number', label: 'Steuernummer' },
      { section: 'usage_agreement_data', field: 'vat_id', label: 'USt-ID' },
      { section: 'usage_agreement_data', field: 'planned_foundation_date', label: 'Gepl. Gründung', type: 'date' },
    ];

    const renderField = ({ section, field, label, type = 'text' }) => (
      <label key={`${section}-${field}`}>
        <span>{label}</span>
        <input
          type={type}
          value={(draft[section] || {})[field] || ''}
          onChange={(event) =>
            updateVerificationEditDraft(ver.id, section, field, event.target.value)
          }
        />
      </label>
    );

    return (
      <div className="admin-verification-edit">
        <div className="admin-verification-edit__header">
          <strong>Kunden- und Vertragsdaten bearbeiten</strong>
          <StatusBadge label="Admin" tone="warning" />
        </div>

        <div className="admin-verification-edit__grid">
          {customerFields.map(renderField)}
          <label>
            <span>Kundentyp</span>
            <select
              value={draft.customer.customer_type || 'privat'}
              onChange={(event) =>
                updateVerificationEditDraft(ver.id, 'customer', 'customer_type', event.target.value)
              }
            >
              <option value="privat">Privatperson</option>
              <option value="freelancer">Freelancer / Freiberufler</option>
              <option value="einzelunternehmen">Einzelunternehmen / Gewerbe</option>
              <option value="kapitalgesellschaft">Deutsche Kapitalgesellschaft</option>
              <option value="verein">Verein / eingetragener Verein (e. V.)</option>
              <option value="ausland">Ausländisches / nicht-deutsches Unternehmen</option>
              <option value="gruendung">Gründung geplant</option>
            </select>
          </label>
          <label>
            <span>NV-Art</span>
            <select
              value={draft.usage_agreement_data.agreement_type || 'privat'}
              onChange={(event) =>
                updateVerificationEditDraft(ver.id, 'usage_agreement_data', 'agreement_type', event.target.value)
              }
            >
              {AGREEMENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {agreementFields.map(renderField)}
        </div>

        <p className="admin-usage-agreement__notice">
          Nach dem Speichern werden nur Kunden- und Vertragsdaten aktualisiert. Wenn die Nutzungsvereinbarung betroffen ist, danach im NV-Block die Nutzungsvereinbarung ersetzen.
        </p>

        <div className="admin-action-grid admin-action-grid--responsive">
          <button
            type="button"
            className="action-button action-button--primary"
            onClick={() => saveVerificationEdit(ver)}
            disabled={savingVerificationEditId === ver.id}
          >
            {savingVerificationEditId === ver.id ? 'Speichert...' : 'Daten speichern'}
          </button>
          <button
            type="button"
            className="action-button action-button--secondary"
            onClick={() => setEditingVerificationId(null)}
            disabled={savingVerificationEditId === ver.id}
          >
            Abbrechen
          </button>
        </div>
      </div>
    );
  };

  const renderOrderActions = (order) => {
    const orderActions = getOrderActionMeta(order);
    const isLoading = requestingOrderId === order.id
      || sendingPremiumOfferId === order.id
      || sendingSealPaymentEmailId === order.id
      || sendingOrderActivationId === order.id;
    const premiumMeta = getPremiumOfferStatusMeta(order.premium_offer_status);

    return (
      <div>
        <div className="admin-action-grid admin-action-grid--responsive">
          {orderActions.map((actionMeta, index) => (
            <button
              key={`${order.id}-${index}-${actionMeta.label}`}
              onClick={actionMeta.action}
              disabled={isLoading || actionMeta.disabled}
              className={`action-button ${
                actionMeta.tone === 'secondary'
                  ? 'action-button--secondary'
                  : actionMeta.tone === 'success'
                    ? 'action-button--success'
                  : 'action-button--primary'
              }`}
            >
              {isLoading ? 'Wird verarbeitet...' : actionMeta.label}
            </button>
          ))}
          {['stripe', 'shopify_seal'].includes(order.provider) ? (
            <button
              type="button"
              className="action-button action-button--secondary"
              disabled={isLoading || refundLoading}
              onClick={() => openRefundDialog(order)}
            >
              Rückerstattung
            </button>
          ) : null}
        </div>
        {premiumMeta ? (
          <div className="admin-order-secondary" style={{ marginTop: '8px' }}>
            {premiumMeta.label}
            {order.premium_offer_start_date ? ` · ${formatDateOnly(order.premium_offer_start_date)}` : ''}
            {order.premium_offer_end_date ? ` bis ${formatDateOnly(order.premium_offer_end_date)}` : ''}
          </div>
        ) : null}
      </div>
    );
  };

  const closeRefundDialog = () => {
    if (refundLoading) return;
    setRefundDialog(null);
    setRefundDetails(null);
    setRefundError('');
  };

  const openRefundDialog = async (order) => {
    setRefundDialog(order);
    setRefundDetails(null);
    setRefundDraft({ mode: 'partial', amount: '', reason: '', sepaAcknowledged: false });
    setRefundError('');
    setRefundLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/orders/${encodeURIComponent(order.order_kind)}/${encodeURIComponent(order.source_id)}/refund`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Rückerstattung konnte nicht geprüft werden.');
      setRefundDetails(data);
      setRefundDraft((current) => ({ ...current, mode: data.full_only ? 'full' : current.mode,
        amount: (Number(data.remaining_amount_cents || 0) / 100).toFixed(2) }));
    } catch (err) {
      setRefundError(normalizeUtfText(err.message));
    } finally {
      setRefundLoading(false);
    }
  };

  const submitRefund = async () => {
    if (!refundDialog || !refundDetails) return;
    const amountCents = refundDraft.mode === 'full'
      ? Number(refundDetails.remaining_amount_cents)
      : Math.round(Number(String(refundDraft.amount).replace(',', '.')) * 100);
    if (!Number.isInteger(amountCents) || amountCents <= 0 || amountCents > Number(refundDetails.remaining_amount_cents)) {
      setRefundError('Bitte einen gültigen Betrag bis zum verbleibenden Erstattungsbetrag eingeben.');
      return;
    }
    const customerLabel = refundDialog.full_name || refundDialog.email || refundDialog.order_reference;
    const providerLabel = refundDialog.order_kind === 'shopify_order' ? 'Shopify/PayPal' : 'Stripe';
    const warning = `Rückerstattung verbindlich auslösen?\n\nKunde: ${customerLabel}\nBestellung: ${refundDialog.order_reference}\nBetrag: ${formatRevenue(amountCents, refundDetails.currency)}\n\n${providerLabel} wird belastet. Vertrag, Seal und Zugang bleiben unverändert.`;
    if (!window.confirm(warning)) return;
    setRefundLoading(true);
    setRefundError('');
    try {
      const idempotencyKey = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${refundDialog.source_id}`;
      const response = await fetch(`${API_URL}/admin/orders/${encodeURIComponent(refundDialog.order_kind)}/${encodeURIComponent(refundDialog.source_id)}/refund`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: refundDraft.mode,
          amount_cents: amountCents,
          reason: refundDraft.reason,
          sepa_risk_acknowledged: refundDraft.sepaAcknowledged,
          idempotency_key: idempotencyKey,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Rückerstattung konnte nicht ausgeführt werden.');
      alert(`Rückerstattung erfasst. Status: ${data.operation?.status || 'wird verarbeitet'}`);
      closeRefundDialog();
    } catch (err) {
      setRefundError(normalizeUtfText(err.message));
    } finally {
      setRefundLoading(false);
    }
  };

  const retryRefundEmail = async (operationId) => {
    setRefundLoading(true);
    setRefundError('');
    try {
      const response = await fetch(`${API_URL}/admin/refunds/${encodeURIComponent(operationId)}/retry-email`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'E-Mail konnte nicht gesendet werden.');
      await openRefundDialog(refundDialog);
    } catch (err) {
      setRefundError(normalizeUtfText(err.message));
      setRefundLoading(false);
    }
  };

  const resendVerificationRequest = async (ver) => {
    const customerLabel = ver.customers.name || ver.customers.email || `Fall #${ver.id}`;
    const confirmed = window.confirm(
      `Soll die Verifizierungsanfrage für ${customerLabel} erneut per E-Mail gesendet werden`
    );

    if (!confirmed) {
      return;
    }

    setResendingVerificationId(ver.id);

    try {
      const response = await fetch(`${API_URL}/admin/verifications/${ver.id}/resend-request`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        await refreshVerification(ver.id);
        alert(normalizeUtfText(data.message || 'Verifizierungsanfrage wurde erneut gesendet.'));
      } else {
        alert(normalizeUtfText(data.error || 'Fehler beim erneuten Senden der Verifizierungsanfrage'));
      }
    } catch (err) {
      console.error(err);
      alert('Fehler beim erneuten Senden der Verifizierungsanfrage');
    } finally {
      setResendingVerificationId(null);
    }
  };

  const sendAccountActivation = async (ver) => {
    const customerLabel = ver.customers.name || ver.customers.email || `Fall #${ver.id}`;
    const confirmed = window.confirm(
      `Soll die Kontoaktivierung für ${customerLabel} gesendet werden? Bereits vorhandene Verifizierungsdaten bleiben erhalten.`
    );

    if (!confirmed) return;
    setSendingAccountActivationId(ver.id);

    try {
      const response = await fetch(`${API_URL}/admin/verifications/${ver.id}/send-account-activation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        await refreshVerification(ver.id);
        alert(normalizeUtfText(data.message || 'Kontoaktivierung wurde gesendet.'));
      } else {
        alert(normalizeUtfText(data.error || 'Kontoaktivierung konnte nicht gesendet werden.'));
      }
    } catch (err) {
      console.error(err);
      alert('Kontoaktivierung konnte nicht gesendet werden.');
    } finally {
      setSendingAccountActivationId(null);
    }
  };

  const resendCompletionEmail = async (ver) => {
    const customerLabel = ver.customers.name || ver.customers.email || `Fall #${ver.id}`;
    const confirmed = window.confirm(
      `Soll die Verifizierungsbestätigung für ${customerLabel} erneut per E-Mail gesendet werden`
    );

    if (!confirmed) {
      return;
    }

    setResendingCompletionId(ver.id);

    try {
      const response = await fetch(`${API_URL}/admin/verifications/${ver.id}/resend-completion-email`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        await refreshVerification(ver.id);
        const infoNote = data.info_pdf_attached === false
          ? '\n\nHinweis: Das Info-PDF wurde nicht angehängt, weil es im Backend nicht gefunden wurde.'
          : '';
        alert(normalizeUtfText(data.message || 'Verifizierungsbestätigung wurde gesendet.') + infoNote);
      } else {
        alert(normalizeUtfText(data.error || 'Fehler beim Senden der Verifizierungsbestätigung'));
      }
    } catch (err) {
      console.error(err);
      alert('Fehler beim Senden der Verifizierungsbestätigung');
    } finally {
      setResendingCompletionId(null);
    }
  };

  const handleLogisticsDraftChange = (requestId, field, value) => {
    setLogisticsDrafts((prev) => ({
      ...prev,
      [requestId]: {
        ...(prev[requestId] || {}),
        [field]: value,
      },
    }));
  };

  const saveLogisticsRequest = async (requestId) => {
    const draft = logisticsDrafts[requestId] || {};

    setSavingLogisticsId(requestId);

    try {
      const response = await fetch(`${API_URL}/admin/logistics-requests/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(draft),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Logistik-Auftrag konnte nicht gespeichert werden.');
      }

      if (data.request) {
        const normalized = normalizeLogisticsRequestItem(data.request);
        setLogisticsRequests((prev) => prev.map((item) => (
      item.id === normalized.id ? normalized : item
        )));
        setLogisticsDrafts((prev) => ({
          ...prev,
          [normalized.id]: {
            status: normalized.status || 'new',
            dhl_tracking_number_outbound: normalized.dhl_tracking_number_outbound || '',
            dhl_label_url: normalized.dhl_label_url || '',
            shipping_cost_amount: normalized.shipping_cost_amount || '',
            customer_notes: normalized.customer_notes || '',
            admin_note: normalized.admin_note || '',
          },
        }));
      }
      alert(normalizeUtfText(data.message || 'Logistik-Auftrag wurde gespeichert.'));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Logistik-Auftrag konnte nicht gespeichert werden.'));
    } finally {
      setSavingLogisticsId(null);
    }
  };

  const applyUpdatedLogisticsRequest = (updatedRequest) => {
    const normalized = normalizeLogisticsRequestItem(updatedRequest);
    setLogisticsRequests((prev) => prev.map((request) => (
      request.id === normalized.id ? normalized : request
    )));
    setLogisticsDrafts((prev) => ({
      ...prev,
      [normalized.id]: {
        ...(prev[normalized.id] || {}),
        status: normalized.status || 'new',
        dhl_tracking_number_outbound: normalized.dhl_tracking_number_outbound || '',
        dhl_label_url: normalized.dhl_label_url || '',
        shipping_cost_amount: normalized.shipping_cost_amount || '',
        customer_notes: normalized.customer_notes || '',
        admin_note: normalized.admin_note || '',
      },
    }));
  };

  const createShippingInvoicePreview = async (item) => {
    const draft = logisticsDrafts[item.id] || {};
    const amount = draft.shipping_cost_amount || item.shipping_cost_amount || '';

    if (!String(amount).trim()) {
      alert('Bitte zuerst die 1:1 Versandkosten eintragen.');
      return;
    }

    const label = formatMoneyValue(amount, item.shipping_cost_currency || 'EUR');
    const confirmed = window.confirm(
      `Soll jetzt ein Shopify-Draft über ${label} erstellt werden? Es wird noch keine E-Mail an ${item.customer_email} gesendet.`
    );

    if (!confirmed) return;

    setCreatingShippingInvoiceId(item.id);

    try {
      const response = await fetch(`${API_URL}/admin/logistics-requests/${item.id}/shipping-invoice/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shipping_cost_amount: amount }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Shopify-Draft konnte nicht erstellt werden.');
      }

      if (data.request) {
        applyUpdatedLogisticsRequest(data.request);
      }

      alert(normalizeUtfText(data.message || 'Shopify-Draft wurde erstellt.'));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Shopify-Draft konnte nicht erstellt werden.'));
    } finally {
      setCreatingShippingInvoiceId(null);
    }
  };

  const createStripeShippingCheckout = async (item) => {
    const draft = logisticsDrafts[item.id] || {};
    const amount = draft.shipping_cost_amount || item.shipping_cost_amount || '';
    if (!String(amount).trim()) {
      alert('Bitte zuerst die tatsächlichen Versandkosten eintragen.');
      return;
    }
    if (!window.confirm(`Stripe-Zahlungslink über ${formatMoneyValue(amount, 'EUR')} erstellen?`)) return;
    setCreatingShippingInvoiceId(item.id);
    try {
      const response = await fetch(`${API_URL}/admin/logistics-requests/${item.id}/stripe-shipping-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shipping_cost_amount: amount }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Stripe-Zahlungslink konnte nicht erstellt werden.');
      if (data.checkout_url) {
        window.prompt('Stripe-Zahlungslink für den Kunden:', data.checkout_url);
      } else {
        alert(normalizeUtfText(data.message || 'Stripe-Zahlungslink wurde erstellt.'));
      }
      await loadData();
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Stripe-Zahlungslink konnte nicht erstellt werden.'));
    } finally {
      setCreatingShippingInvoiceId(null);
    }
  };

  const sendShippingInvoice = async (item) => {
    if (!item.shopify_shipping_invoice_draft_order_id) {
      alert('Bitte zuerst einen Shopify-Draft erstellen.');
      return;
    }

    const draft = logisticsDrafts[item.id] || {};
    const amount = draft.shipping_cost_amount || item.shipping_cost_amount || '';
    const label = formatMoneyValue(amount, item.shipping_cost_currency || 'EUR');
    const confirmed = window.confirm(
      `Soll die Shopify-Zahlungsaufforderung über ${label} jetzt wirklich an ${item.customer_email} gesendet werden? Der Kunde erhält einen sicheren Checkout-Link.`
    );

    if (!confirmed) return;

    setSendingShippingInvoiceId(item.id);

    try {
      const response = await fetch(`${API_URL}/admin/logistics-requests/${item.id}/shipping-invoice/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shipping_cost_amount: amount }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Shopify-Zahlungsaufforderung konnte nicht gesendet werden.');
      }

      if (data.request) {
        applyUpdatedLogisticsRequest(data.request);
      }

      alert(normalizeUtfText(data.message || 'Shopify-Zahlungsaufforderung wurde gesendet.'));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Shopify-Zahlungsaufforderung konnte nicht gesendet werden.'));
    } finally {
      setSendingShippingInvoiceId(null);
    }
  };

  const deleteShippingInvoiceDraft = async (item) => {
    if (!item.shopify_shipping_invoice_draft_order_id) {
      alert('Es gibt keinen Shopify-Draft zum Löschen.');
      return;
    }

    const confirmed = window.confirm(
      'Soll der Shopify-Draft gelöscht werden? Es wird keine Zahlungsaufforderung an den Kunden gesendet.'
    );

    if (!confirmed) return;

    setDeletingShippingInvoiceId(item.id);

    try {
      const response = await fetch(`${API_URL}/admin/logistics-requests/${item.id}/shipping-invoice`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Shopify-Draft konnte nicht gelöscht werden.');
      }

      if (data.request) {
        applyUpdatedLogisticsRequest(data.request);
      }

      alert(normalizeUtfText(data.message || 'Shopify-Draft wurde gelöscht.'));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Shopify-Draft konnte nicht gelöscht werden.'));
    } finally {
      setDeletingShippingInvoiceId(null);
    }
  };

  const notifyLogisticsCustomer = async (item) => {
    const confirmed = window.confirm(
      `Soll ${item.customer_email} jetzt per E-Mail über Tracking/Label/Hinweis informiert werden`
    );

    if (!confirmed) return;

    setNotifyingLogisticsId(item.id);

    try {
      const response = await fetch(`${API_URL}/admin/logistics-requests/${item.id}/notify-customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(logisticsDrafts[item.id] || {}),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kundenbenachrichtigung konnte nicht gesendet werden.');
      }

      if (data.request) {
        setLogisticsRequests((prev) => prev.map((request) => (
          request.id === item.id ? normalizeLogisticsRequestItem(data.request) : request
        )));
        setLogisticsDrafts((prev) => ({
          ...prev,
          [item.id]: {
            status: data.request.status || 'new',
            dhl_tracking_number_outbound: data.request.dhl_tracking_number_outbound || '',
            dhl_label_url: data.request.dhl_label_url || '',
            shipping_cost_amount: data.request.shipping_cost_amount || '',
            customer_notes: data.request.customer_notes || '',
            admin_note: data.request.admin_note || '',
          },
        }));
      }

      alert(normalizeUtfText(data.message || 'Kunde wurde benachrichtigt.'));
    } catch (err) {
      console.error(err);
      alert(normalizeUtfText(err.message || 'Kundenbenachrichtigung konnte nicht gesendet werden.'));
    } finally {
      setNotifyingLogisticsId(null);
    }
  };

  const renderLogisticsAdminFields = (item) => {
    const draft = logisticsDrafts[item.id] || {
      status: item.status || 'new',
      dhl_tracking_number_outbound: item.dhl_tracking_number_outbound || '',
      dhl_label_url: item.dhl_label_url || '',
      shipping_cost_amount: item.shipping_cost_amount || '',
      customer_notes: item.customer_notes || '',
      admin_note: item.admin_note || '',
    };

    return (
      <div className="admin-logistics-edit-grid">
        <label>
          Status
          <select
            value={draft.status}
            onChange={(e) => handleLogisticsDraftChange(item.id, 'status', e.target.value)}
          >
            <option value="new">Neu</option>
            <option value="form_submitted">Formular eingereicht</option>
            <option value="expected">Erwartet</option>
            <option value="received">Eingegangen</option>
            <option value="shipping_costs_open">Versandkosten offen</option>
            <option value="paid">Bezahlt</option>
            <option value="shipped">Versendet</option>
            <option value="completed">Abgeschlossen</option>
            <option value="cancelled">Storniert</option>
          </select>
        </label>

        <label>
          Ausgehende DHL-Trackingnummer
          <input
            value={draft.dhl_tracking_number_outbound}
            onChange={(e) => handleLogisticsDraftChange(item.id, 'dhl_tracking_number_outbound', e.target.value)}
          />
        </label>

        <label>
          DHL Label URL
          <input
            value={draft.dhl_label_url}
            onChange={(e) => handleLogisticsDraftChange(item.id, 'dhl_label_url', e.target.value)}
          />
        </label>

        <label>
          1:1 Versandkosten
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="z. B. 6.99"
            value={draft.shipping_cost_amount}
            disabled={Boolean(item.shopify_shipping_invoice_draft_order_id)}
            title={item.shopify_shipping_invoice_draft_order_id ? 'Zum Ändern bitte den Shopify-Draft löschen und neu erstellen.' : ''}
            onChange={(e) => handleLogisticsDraftChange(item.id, 'shipping_cost_amount', e.target.value)}
          />
        </label>

        <label>
          Kundenhinweis
          <textarea
            rows={3}
            value={draft.customer_notes}
            onChange={(e) => handleLogisticsDraftChange(item.id, 'customer_notes', e.target.value)}
          />
        </label>

        <label className="full-width">
          Interne Notiz
          <textarea
            rows={3}
            value={draft.admin_note}
            onChange={(e) => handleLogisticsDraftChange(item.id, 'admin_note', e.target.value)}
          />
        </label>

        {item.logistics_request_files.length ? (
          <div className="full-width">
            <strong>Upload-Dateien</strong>
            <ul className="document-list">
              {item.logistics_request_files.map((file) => (
                <li key={file.id}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() =>
                      openAdminFile(`/admin/files/logistics/${file.id}`)
                    }
                  >
                    {file.original_filename || `Datei #${file.id}`}
                  </button>
                  {file.size ? <small> ({formatFileSize(file.size)})</small> : null}
                  {file.expires_at ? <small> - Ablauf: {formatDateOnly(file.expires_at)}</small> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="full-width logistics-detail-panel">
          <strong>Empfängeradresse</strong>
          <small>{formatLogisticsRecipientAddress(item)}</small>
          {item.recipient_phone ? <small>Telefon: {item.recipient_phone}</small> : null}
        </div>

        <div className="full-width logistics-detail-panel">
          <strong>Paketdetails</strong>
          <small>Absender: {item.sender_name || '-'}</small>
          <small>Inhalt: {item.package_content_notes || '-'}</small>
          <small>Eingang: {item.inbound_tracking_number || '-'} / {item.inbound_carrier || '-'}</small>
          <small>Erwartet: {formatDateOnly(item.expected_arrival_date) || '-'}</small>
        </div>

        <div className="full-width logistics-invoice-panel">
          {String(item.shopify_order_id || '').startsWith('stripe:') ? (
            <>
              <div>
                <strong>Stripe-Zahlungslink für tatsächliche Versandkosten</strong>
                <small>
                  Betrag: {formatMoneyValue(draft.shipping_cost_amount || item.shipping_cost_amount, item.shipping_cost_currency || 'EUR')}
                </small>
              </div>
              <div className="logistics-invoice-actions">
                <button
                  type="button"
                  className="action-button action-button--success"
                  onClick={() => createStripeShippingCheckout(item)}
                  disabled={creatingShippingInvoiceId === item.id}
                >
                  {creatingShippingInvoiceId === item.id ? 'Link wird erstellt...' : 'Stripe-Zahlungslink erstellen'}
                </button>
              </div>
            </>
          ) : (
          <>
          <div>
            <strong>Shopify-Zahlungsaufforderung für Versandkosten</strong>
            <small>
              {item.shopify_shipping_invoice_draft_order_id
                ? item.shopify_shipping_invoice_sent_at
                  ? `${item.shopify_shipping_invoice_name || 'Shopify-Draft'}: Zahlungsaufforderung gesendet am ${formatDateTime(item.shopify_shipping_invoice_sent_at)}`
                  : `${item.shopify_shipping_invoice_name || 'Shopify-Draft'} erstellt, noch keine E-Mail gesendet`
                : `Noch kein Shopify-Draft erstellt. Betrag: ${formatMoneyValue(draft.shipping_cost_amount || item.shipping_cost_amount, item.shipping_cost_currency || 'EUR')}`}
            </small>
            {item.shopify_shipping_invoice_url ? (
              <a href={item.shopify_shipping_invoice_url} target="_blank" rel="noopener noreferrer">
                Shopify-Zahlungsseite prüfen
              </a>
            ) : null}
          </div>
          <div className="logistics-invoice-actions">
            {!item.shopify_shipping_invoice_draft_order_id ? (
              <button
                type="button"
                className="action-button action-button--warning"
                onClick={() => createShippingInvoicePreview(item)}
                disabled={creatingShippingInvoiceId === item.id}
              >
                {creatingShippingInvoiceId === item.id ? 'Draft wird erstellt...' : 'Shopify-Draft erstellen'}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="action-button action-button--success"
                  onClick={() => sendShippingInvoice(item)}
                  disabled={sendingShippingInvoiceId === item.id}
                >
                  {sendingShippingInvoiceId === item.id
                    ? 'Sendet...'
                    : item.shopify_shipping_invoice_sent_at
                      ? 'Zahlungsaufforderung erneut senden'
                    : 'Zahlungsaufforderung senden'}
                </button>
                {!item.shopify_shipping_invoice_sent_at ? (
                  <button
                    type="button"
                    className="action-button action-button--danger"
                    onClick={() => deleteShippingInvoiceDraft(item)}
                    disabled={deletingShippingInvoiceId === item.id}
                  >
                    {deletingShippingInvoiceId === item.id ? 'Löscht...' : 'Nicht senden / löschen'}
                  </button>
                ) : null}
              </>
            )}
          </div>
          </>
          )}
        </div>

        <div className="full-width logistics-action-row">
          <button
            type="button"
            className="action-button action-button--primary"
            onClick={() => saveLogisticsRequest(item.id)}
            disabled={savingLogisticsId === item.id}
          >
            {savingLogisticsId === item.id ? 'Speichert...' : 'Logistik speichern'}
          </button>
          <button
            type="button"
            className="action-button action-button--secondary"
            onClick={() => notifyLogisticsCustomer(item)}
            disabled={notifyingLogisticsId === item.id}
          >
            {notifyingLogisticsId === item.id ? 'Sendet...' : 'Kunde benachrichtigen'}
          </button>
        </div>
      </div>
    );
  };

  const renderVerificationActions = (ver) => {
    const businessRelevant = isBusinessCustomer(ver.customers.customer_type);
    const identityLocked = isIdentityLocked(ver.identity_status);
    const businessLocked = businessRelevant ? isBusinessLocked(ver.business_status) : true;
    const isAlreadyVerified = ver.identity_status === 'already_verified';
    const reversalRequired = isReversalRequiredStatus(ver.identity_status);
    const reversalDeadlinePassed = isVerificationPastReversalDeadline(ver);
    const reversalDeadline = getVerificationReversalDeadline(ver);
    const reversalFinal = isReversedStatus(ver.identity_status);
    const lifecycle = ver.subscription_lifecycle || {};
    const stripeBilling = ver.stripe_billing || null;
    const refundMeta = getRefundStatusMeta(lifecycle.refund_status);
    const refundReference = lifecycle.refund_id || lifecycle.refund_reference || lifecycle.refundReference;
    const caseCompleted = identityLocked && businessLocked;
    const needsNextcloudOnboarding = caseCompleted && !normalizeUtfText(ver.customers?.nextcloud_user_id);
    const statusUpdateRunning = updatingVerificationIds.has(String(ver.id));

    const disableIdentityApprove = statusUpdateRunning || identityLocked || reversalRequired;
    const disableIdentityReject = statusUpdateRunning || identityLocked || reversalRequired;
    const disableFollowup =
      isAlreadyVerified ||
      reversalRequired ||
      reversalFinal ||
      (!businessRelevant && identityLocked) ||
      caseCompleted;
    const disableBusinessApprove =
      statusUpdateRunning || !businessRelevant || isAlreadyVerified || businessLocked || reversalRequired;
    const disableFullApprove =
      statusUpdateRunning || !businessRelevant || isAlreadyVerified || (caseCompleted && !needsNextcloudOnboarding) ||
      (businessLocked && !needsNextcloudOnboarding) || reversalRequired;

    return (
      <div className="admin-action-grid admin-action-grid--responsive">
        {ver.stripe_billing_available ? <div className="admin-muted-box" style={{ gridColumn: '1 / -1' }}>
          <strong>Zahlungsanbieter:</strong> {stripeBilling ? 'Stripe' : 'Shopify / Seal'}
          {stripeBilling ? (
            <>
              <br /><small>Status: {stripeBilling.status || '-'} · Tarif: {stripeBilling.product || 'Pro'} · Zahlungsart: {stripePaymentMethodLabel(stripeBilling.payment_method_type)}</small>
              <br /><small>Letzte Zahlung: {formatDateTime(stripeBilling.last_payment_at)} · Periode bis: {formatDateTime(stripeBilling.current_period_end)}</small>
              <br /><small>Customer: {stripeBilling.stripe_customer_id || '-'} · Subscription: {stripeBilling.stripe_subscription_id || '-'}</small>
            </>
          ) : ver.stripe_checkout_available ? (
            <button type="button" onClick={() => createStripeCheckout(ver)} style={{ marginLeft: '1rem' }}>
              Stripe-Pro-Buchungslink erstellen
            </button>
          ) : <small> · Stripe Checkout ist deaktiviert.</small>}
        </div> : null}
        {(reversalRequired || reversalDeadlinePassed) && !reversalFinal ? (
          <div className="admin-warning-box" style={{ gridColumn: '1 / -1' }}>
            <strong>Verifizierung seit mehr als 14 Tagen offen – Rückabwicklung prüfen.</strong>
            <br />
            <small>
              Frist: {formatDateTime(reversalDeadline)}. Beim Erledigen wird die technische Rückabwicklung ausgeführt.
              {' '}
              <StatusBadge label={refundMeta.label} tone={refundMeta.tone} />
            </small>
          </div>
        ) : null}

        {reversalFinal ? (
          <div className="admin-muted-box" style={{ gridColumn: '1 / -1' }}>
            Rückabgewickelt. <StatusBadge label={refundMeta.label} tone={refundMeta.tone} />
            {refundReference ? <small> Referenz: {refundReference}</small> : null}
          </div>
        ) : null}

        {(reversalRequired || reversalDeadlinePassed) && !reversalFinal ? (
          <button
            onClick={() => confirmReversal(ver)}
            disabled={workingReversalId === ver.id}
            className="action-button action-button--danger"
            title="Beendet soweit möglich Abo, Erstattung und Nextcloud-Bereinigung und setzt den Fall final auf rückabgewickelt."
          >
            {workingReversalId === ver.id ? 'Läuft...' : 'Rückabwicklung erledigen'}
          </button>
        ) : null}

        {!reversalFinal && !isAlreadyVerified && !identityLocked ? (
          <button
            onClick={() => extendReversalDeadline(ver)}
            disabled={workingReversalId === ver.id}
            className="action-button action-button--secondary"
            title="Verlängert die Verifizierungsfrist und verschiebt Reminder/Rückabwicklung."
          >
            Frist verlängern
          </button>
        ) : null}

        <button
          onClick={() => updateStatus(ver.id, 'approved', undefined)}
          disabled={disableIdentityApprove}
          className="action-button action-button--primary"
          title={disableIdentityApprove ? 'Die Identität ist bereits final bearbeitet.' : ''}
        >
          Identität freigeben
        </button>

        <button
          onClick={() => approveIdentityAndBusiness(ver)}
          disabled={disableFullApprove}
          className="action-button action-button--success"
          title={
            !businessRelevant
              ? 'Für Privatpersonen nicht relevant.'
              : isAlreadyVerified
                ? 'Dieser Fall ist bereits final abgeschlossen.'
              : needsNextcloudOnboarding
                ? 'Die fachliche Freigabe ist abgeschlossen; versucht nur das fehlgeschlagene Nextcloud-Onboarding erneut.'
              : caseCompleted
                ? 'Dieser Fall ist bereits vollständig abgeschlossen.'
              : businessLocked
                ? 'Der Unternehmensstatus ist bereits final bearbeitet.'
              : 'Gibt Identität und Unternehmen in einem Schritt frei und sendet nur die Abschlussmail.'
          }
        >
          {statusUpdateRunning ? 'Wird verarbeitet …' : needsNextcloudOnboarding ? 'Nextcloud-Onboarding erneut versuchen' : 'Alles freigeben'}
        </button>

        <button
          onClick={() => updateStatus(ver.id, 'rejected', undefined)}
          disabled={disableIdentityReject}
          className="action-button action-button--danger"
          title={disableIdentityReject ? 'Die Identität ist bereits final bearbeitet.' : ''}
        >
          Identität ablehnen
        </button>

        <button
          onClick={() => toggleFollowupForm(ver.id)}
          disabled={disableFollowup}
          className="action-button action-button--warning"
          title={
            isAlreadyVerified
              ? 'Bei bereits verifizierten Fällen sind keine Nachforderungen mehr vorgesehen.'
              : !businessRelevant && identityLocked
                ? 'Bei privaten, bereits freigegebenen Fällen sind keine weiteren Schritte nötig.'
              : caseCompleted
                ? 'Dieser Fall ist bereits vollständig abgeschlossen.'
              : ''
          }
        >
          Nachforderung
        </button>

        <button
          onClick={() => resendVerificationRequest(ver)}
          disabled={resendingVerificationId === ver.id || isAlreadyVerified || caseCompleted}
          className="action-button action-button--secondary"
          title={
            isAlreadyVerified
              ? "Bei bereits verifizierten Fällen wird keine neue Anfrage gesendet."
              : caseCompleted
                ? "Dieser Fall ist bereits vollständig abgeschlossen."
              : "Sendet die ursprüngliche Verifizierungsanfrage mit dem bestehenden Link erneut."
          }
        >
          {resendingVerificationId === ver.id ? "Sendet..." : "Erneut anfragen"}
        </button>

        <button
          onClick={() => sendAccountActivation(ver)}
          disabled={sendingAccountActivationId === ver.id || !ver.customers?.email}
          className="action-button action-button--secondary"
          title="Sendet ausschließlich die Aktivierung des Kundenkontos. Bestehende Verifizierungsdaten und Status bleiben erhalten."
        >
          {sendingAccountActivationId === ver.id ? 'Sendet...' : 'Kontoaktivierung senden'}
        </button>

        {caseCompleted && !isAlreadyVerified ? (
          <button
            onClick={() => resendCompletionEmail(ver)}
            disabled={resendingCompletionId === ver.id}
            className="action-button action-button--primary"
            title="Sendet die zweisprachige Abschlussmail mit Bewertungslink und Info-PDF erneut."
          >
            {resendingCompletionId === ver.id ? "Sendet..." : "Bestätigung senden"}
          </button>
        ) : null}

        <button
          onClick={() => updateStatus(ver.id, undefined, 'approved')}
          disabled={disableBusinessApprove}
          className="action-button action-button--success"
          title={
            !businessRelevant
              ? 'Für Privatpersonen nicht relevant.'
              : isAlreadyVerified
                ? 'Dieser Fall ist bereits final abgeschlossen.'
              : businessLocked
                ? 'Der Unternehmensstatus ist bereits final bearbeitet.'
              : ''
          }
        >
          Unternehmen freigeben
        </button>

        <button
          onClick={() => resetVerification(ver.id)}
          disabled={savingResetId === ver.id}
          className="action-button action-button--secondary"
          title="Setzt den Fall zurück, damit der Kunde den bestehenden Link erneut vollständig ausfüllen kann."
        >
          {savingResetId === ver.id ? 'Läuft...' : 'Zurücksetzen'}
        </button>

        <button
          onClick={() => reopenVerification(ver.id)}
          disabled={reopeningVerificationId === ver.id}
          className="action-button action-button--secondary"
          title="Öffnet den bestehenden Link erneut zur Bearbeitung, ohne die Dokument-Historie zu löschen."
        >
          {reopeningVerificationId === ver.id ? 'Öffnet...' : 'Wieder öffnen'}
        </button>

        <button
          onClick={() => startVerificationEdit(ver)}
          disabled={savingVerificationEditId === ver.id}
          className="action-button action-button--secondary"
          title="Kunden- und Nutzungsvereinbarungsdaten korrigieren."
        >
          Daten bearbeiten
        </button>

        {!isAlreadyVerified && (
          <button
            onClick={() => updateStatus(ver.id, 'already_verified', undefined)}
            disabled={caseCompleted}
            className="action-button action-button--success"
            title={caseCompleted ? 'Dieser Fall ist bereits vollständig erledigt.' : ''}
          >
            Bereits verifiziert
          </button>
        )}

        <button
          onClick={() => toggleDocuments(ver.id)}
          className="action-button action-button--secondary"
        >
          {openRows[ver.id] ? 'Dok. zu' : 'Dokumente'}
        </button>

        <button
          onClick={() => deleteVerificationCase(ver.id)}
          disabled={deletingVerificationId === ver.id}
          className="action-button action-button--danger"
          title="Löscht den Verifizierungsfall inklusive Dokumentverknüpfungen aus der App."
        >
          {deletingVerificationId === ver.id ? 'Löscht...' : 'Löschen'}
        </button>
      </div>
    );
  };

  const adminAppCards = {
    'open-operations': {
      icon: '!', count: openOperations.length, label: 'Offene Vorgänge',
      description: 'Priorisierte Aufgaben und Störungen aus allen Arbeitsbereichen an einer Stelle.',
      statusClass: openOperations.some((item) => item.priority === 'critical')
        ? 'admin-app-tile--status-critical'
        : openOperations.length ? 'admin-app-tile--status-warning' : 'admin-app-tile--status-ok',
    },
    'system-status': {
      icon: '●',
      count: systemStatus ? systemStatus.counts.error : '–',
      label: 'Systemstatus',
      description: 'Erreichbarkeit und sichere Funktionsprüfungen aller angebundenen Dienste.',
      statusClass: `admin-app-tile--status-${systemStatus?.overall || 'unknown'}`,
    },
    notifications: {
      icon: '◆', count: notificationOverview?.counts?.critical || 0, label: 'Benachrichtigungen',
      description: 'Telegram, E-Mail, System, Zahlungen, Backups, Wartung und Tests zentral überblicken.',
      statusClass: notificationOverview?.counts?.critical
        ? 'admin-app-tile--status-critical'
        : notificationOverview ? 'admin-app-tile--status-ok' : 'admin-app-tile--status-unknown',
    },
    metrics: {
      icon: '↗', count: null, label: 'Kennzahlen',
      description: 'Umsatz, Entwicklung, Abos, Kündigungen und Zahlungsausfälle auswerten.',
    },
    'web-analytics': {
      icon: '⌁', count: websiteAnalytics?.configured ? websiteAnalytics.online : null, label: 'Web Analytics',
      description: 'Live-Aktivität, Besucher und Seitenaufrufe der Website auswerten.',
    },
    scans: {
      icon: '▣', count: unassignedScanCount, label: 'Scans zuordnen',
      description: 'Neue Postscans prüfen und Kunden zuweisen.',
    },
    'portal-requests': {
      icon: '↔', count: portalRequests.length, label: 'Portal-Anfragen',
      description: 'Änderungen, Widerrufe und Briefkastenwünsche prüfen.',
    },
    logistics: {
      icon: '⌂', count: logisticsRequests.length, label: 'Logistik',
      description: 'Weiterleitungen, Versandkosten und Sendungen verwalten.',
    },
    'mailbox-display': {
      icon: '▤', count: mailboxCandidateSources.total, label: 'Briefkastenanzeige',
      description: 'Seal- und Stripe-Kunden sowie Anzeigenamen verwalten.',
    },
    'chat-automation': {
      icon: '●', count: null, label: 'Live-Chat',
      description: 'Servicezeiten und zweisprachige Eingangsbestätigungen sicher konfigurieren.',
      statusClass: settings.chat_auto_acknowledgement?.enabled
        ? 'admin-app-tile--status-ok'
        : 'admin-app-tile--status-unknown',
    },
    'google-reviews': {
      icon: '★', count: settings.google_reviews?.preview?.candidates ?? null, label: 'Google-Bewertungen',
      description: 'Bewertungsanzeige und freiwillige Anfragen getrennt steuern und sicher vorschauen.',
      statusClass: settings.google_reviews?.display_enabled || settings.google_reviews?.requests_enabled
        ? 'admin-app-tile--status-ok'
        : 'admin-app-tile--status-unknown',
    },
    'access-reviews': {
      icon: '⊘', count: accessReviews.filter((item) => item.status === 'pending_confirmation').length + (nextcloudInventory?.unassigned_accounts?.length || 0),
      label: 'Zugangsprüfungen',
      description: 'Abgelaufene Abos prüfen und Nextcloud-, Post- und Vertragszugang bewusst deaktivieren.',
    },
    'dashboard-focus': {
      icon: '◎', count: dashboardStats.followupCases, label: 'Bestellungen',
      description: 'Bestellungen und Verifizierungen gemeinsam bearbeiten.',
    },
    activity: {
      icon: 'i', count: null, label: 'Aktivität',
      description: 'Letzte Vorgänge und Systembewegungen ansehen.',
    },
  };

  if (!token) {
    return (
      <div className="page-shell">
        <div className="card">
          <p>Weiterleitung zum Admin-Login...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <main className="page-shell page-shell--wide">
        <div className="card">
          <p>Lade...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="page-shell page-shell--wide">
        <div className="card">
          <p>{error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className={`admin-hub-shell ${activeAdminArea === 'scans' ? 'admin-hub-shell--scan-focus' : ''}`}>
      <style>{`
        [hidden] { display: none !important; }
        .admin-dashboard-mobile-only {
          display: none;
        }

        .admin-dashboard-desktop-only {
          display: block;
        }

        .admin-section-anchor {
          scroll-margin-top: 18px;
        }

        .admin-app-launcher {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
        }

        .admin-app-tile {
          appearance: none;
          border: 1px solid #e4e7ec;
          border-radius: 18px;
          background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
          color: #101828;
          padding: 18px;
          text-align: left;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(16, 24, 40, 0.05);
          transition: transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease;
          min-height: 142px;
        }

        .admin-app-tile:hover,
        .admin-app-tile:focus-visible {
          transform: translateY(-2px);
          border-color: #84adcf;
          box-shadow: 0 8px 20px rgba(15, 76, 129, 0.12);
          outline: none;
        }

        .admin-app-tile.is-dragging {
          opacity: 0.45;
          transform: scale(0.98);
          cursor: grabbing;
        }

        .admin-app-tile.is-drop-target {
          border-color: #2f6cff;
          box-shadow: 0 0 0 3px rgba(47, 108, 255, 0.18), 0 8px 20px rgba(15, 76, 129, 0.12);
        }

        .admin-app-tile__top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }

        .admin-app-tile__icon {
          width: 46px;
          height: 46px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          background: #eaf3fa;
          color: #0f4c81;
          font-size: 1.45rem;
        }

        .admin-app-tile__count {
          min-width: 30px;
          height: 30px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          padding: 0 8px;
          background: #0f4c81;
          color: #fff;
          font-size: 0.78rem;
          font-weight: 800;
        }
        .admin-app-tile__meta {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .admin-app-tile__drag {
          color: #7c8da1;
          font-size: 1.25rem;
          font-weight: 900;
          letter-spacing: -0.18em;
          cursor: grab;
        }
        .admin-app-order-error {
          margin: 10px 0 0;
          color: #b42318;
          font-size: 0.82rem;
          font-weight: 700;
        }
        .admin-app-tile--status-ok {
          border-color: #86efac;
          background: #f0fdf4;
        }
        .admin-app-tile--status-warning {
          border-color: #facc15;
          background: #fefce8;
        }
        .admin-app-tile--status-critical {
          border-color: #f87171;
          background: #fef2f2;
        }
        .admin-app-tile--status-ok .admin-app-tile__count { background: #15803d; }
        .admin-app-tile--status-warning .admin-app-tile__count { background: #ca8a04; }
        .admin-app-tile--status-critical .admin-app-tile__count { background: #b91c1c; }

        .admin-app-tile strong {
          display: block;
          font-size: 1rem;
          margin-bottom: 5px;
        }

        .admin-app-tile small {
          color: #667085;
          line-height: 1.35;
        }
        .admin-system-status {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
          margin: 4px 0 18px;
        }
        .admin-system-status__item {
          border: 1px solid #dbe3ed;
          border-radius: 999px;
          background: #f8fafc;
          color: #475569;
          font-size: 12px;
          font-weight: 700;
          padding: 7px 11px;
        }
        .admin-system-status__item--ok { background: #ecfdf3; border-color: #bbf7d0; color: #166534; }
        .admin-system-status__item--warning { background: #fff7ed; border-color: #fed7aa; color: #9a3412; }
        .admin-system-status__link { text-decoration: none; }
        .admin-system-status__link:hover { background: #dcfce7; border-color: #86efac; }
        .admin-notification-switch {
          display: inline-flex;
          gap: 3px;
          padding: 3px;
          border: 1px solid #dbe3ed;
          border-radius: 999px;
          background: #f8fafc;
        }
        .admin-notification-switch button {
          min-height: 30px;
          border-radius: 999px;
          padding: 6px 11px;
          background: transparent;
          color: #475569;
          font-size: 12px;
          font-weight: 800;
        }
        .admin-notification-switch button.is-active {
          background: #0f4c81;
          color: #fff;
        }
        .admin-system-status__test { margin-left: auto; }
        .admin-notification-counts {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
          margin: 16px 0;
        }
        .admin-notification-counts article,
        .admin-notification-source,
        .admin-notification-entry {
          border: 1px solid #dbe3ed;
          border-radius: 14px;
          background: #fff;
          padding: 14px;
        }
        .admin-notification-counts span {
          display: block;
          color: #667085;
          font-size: 12px;
          font-weight: 750;
        }
        .admin-notification-counts strong {
          display: block;
          margin-top: 5px;
          color: #05284a;
          font-size: 25px;
        }
        .admin-notification-sources {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 12px;
          margin: 16px 0;
        }
        .admin-notification-source__header,
        .admin-notification-entry {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }
        .admin-notification-source p,
        .admin-notification-entry p { margin: 6px 0 0; color: #667085; }
        .admin-notification-state {
          display: inline-block;
          flex: 0 0 auto;
          border-radius: 999px;
          padding: 5px 9px;
          font-size: 11px;
          font-weight: 800;
        }
        .admin-notification-state--critical { background: #fee2e2; color: #991b1b; }
        .admin-notification-state--open { background: #ffedd5; color: #9a3412; }
        .admin-notification-state--confirmed { background: #dcfce7; color: #166534; }
        .admin-notification-state--historical,
        .admin-notification-state--external { background: #e2e8f0; color: #475569; }
        .admin-notification-state--unavailable { background: #fef3c7; color: #92400e; }
        .admin-notification-list { display: grid; gap: 10px; margin-top: 14px; }
        .admin-notification-entry--critical { border-color: #fca5a5; background: #fff7f7; }
        .admin-notification-entry--open { border-color: #fdba74; background: #fffaf5; }
        .admin-notification-entry__meta { color: #667085; font-size: 12px; margin-top: 6px; }
        .admin-notification-privacy {
          border-left: 4px solid #0f4c81;
          background: #eff6ff;
          color: #1e3a5f;
          padding: 12px 14px;
          margin: 14px 0;
        }
        @media (max-width: 760px) {
          .admin-notification-counts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .admin-notification-entry { flex-direction: column; }
        }
        .admin-operations-summary { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 12px; margin: 16px 0; }
        .admin-operations-summary article, .admin-release-panel { border: 1px solid #dbe3ed; border-radius: 14px; padding: 15px; background: #f8fafc; }
        .admin-operations-summary span, .admin-release-panel span { display: block; color: #667085; font-size: 12px; font-weight: 750; }
        .admin-operations-summary strong { display: block; margin-top: 5px; color: #05284a; font-size: 25px; }
        .admin-release-panel { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; margin: 16px 0; }
        .admin-release-panel strong { display: block; margin-top: 4px; color: #05284a; }
        .admin-release-card { border: 1px solid #dbe3ed; border-radius: 12px; padding: 14px; background: #fff; }
        .admin-release-card__header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
        .admin-release-card__header h3 { margin: 0; font-size: 17px; color: #05284a; }
        .admin-release-state { border-radius: 999px; padding: 4px 9px; font-size: 12px; font-weight: 800; }
        .admin-release-state--confirmed { background: #dcfce7; color: #166534; }
        .admin-release-state--partial, .admin-release-state--unknown { background: #f1f5f9; color: #475569; }
        .admin-release-state--inconsistent { background: #fee2e2; color: #991b1b; }
        .admin-release-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 18px; }
        .admin-release-note { margin: 10px 0 0; color: #667085; font-size: 12px; }
        .admin-release-config { grid-column: 1 / -1; margin-top: 4px; }
        .admin-release-config ul { columns: 2; margin: 6px 0 0; padding-left: 18px; color: #344054; font-size: 12px; }
        @media (max-width: 640px) { .admin-release-grid { grid-template-columns: 1fr; } .admin-release-config { grid-column: auto; } .admin-release-config ul { columns: 1; } }
        .admin-operations-list { display: grid; gap: 10px; }
        .admin-operation { display: grid; grid-template-columns: 110px minmax(0,1fr) auto; gap: 12px; align-items: center; border: 1px solid #dbe3ed; border-left-width: 5px; border-radius: 12px; padding: 13px; background: #fff; }
        .admin-operation--critical { border-left-color: #b42318; }
        .admin-operation--high { border-left-color: #d97706; }
        .admin-operation--normal { border-left-color: #2563eb; }
        .admin-operation__category { color: #475569; font-size: 12px; font-weight: 800; text-transform: uppercase; }
        .admin-operation p { margin: 3px 0 0; color: #667085; }
        @media (max-width: 760px) { .admin-operations-summary { grid-template-columns: 1fr; } .admin-operation { grid-template-columns: 1fr; } }
        .admin-status-summary {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: center;
        }
        .admin-status-overall {
          width: 100%;
          border-radius: 14px;
          padding: 13px 15px;
          font-weight: 800;
        }
        .admin-status-overall--ok { background: #dcfce7; color: #166534; }
        .admin-status-overall--warning { background: #fef9c3; color: #854d0e; }
        .admin-status-overall--critical { background: #fee2e2; color: #991b1b; }
        .admin-status-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
          margin-top: 16px;
        }
        .admin-status-card {
          display: grid;
          grid-template-columns: 14px minmax(0, 1fr);
          gap: 10px;
          align-items: start;
          border: 1px solid #dbe3ed;
          border-radius: 14px;
          padding: 15px;
          background: #fff;
        }
        .admin-status-card__lamp {
          width: 12px;
          height: 12px;
          margin-top: 4px;
          border-radius: 999px;
          background: #94a3b8;
          box-shadow: 0 0 0 4px #f1f5f9;
        }
        .admin-status-card--ok .admin-status-card__lamp {
          background: #16a34a;
          box-shadow: 0 0 0 4px #dcfce7;
        }
        .admin-status-card--error .admin-status-card__lamp {
          background: #dc2626;
          box-shadow: 0 0 0 4px #fee2e2;
        }
        .admin-status-card strong { display: block; color: #172033; }
        .admin-status-card small { display: block; color: #667085; margin-top: 4px; }
        .admin-status-card__latency { color: #94a3b8; font-size: 0.72rem; margin-top: 6px; }
        .admin-status-card__details {
          grid-column: 1 / -1;
          margin-top: 3px;
          border-top: 1px solid #e5eaf1;
          padding-top: 10px;
        }
        .admin-status-card__details summary {
          cursor: pointer;
          color: #0b5794;
          font-size: 0.78rem;
          font-weight: 800;
          list-style-position: outside;
          margin-left: 16px;
        }
        .admin-status-card__detail-list {
          display: grid;
          gap: 7px;
          margin-top: 10px;
        }
        .admin-status-card__detail {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #475569;
          font-size: 0.76rem;
        }
        .admin-status-card__detail-lamp {
          width: 9px;
          height: 9px;
          flex: 0 0 auto;
          border-radius: 999px;
          background: #94a3b8;
        }
        .admin-status-card__detail--ok .admin-status-card__detail-lamp { background: #16a34a; }
        .admin-status-card__detail--error .admin-status-card__detail-lamp { background: #dc2626; }
        .admin-app-navigation {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 18px;
          padding: 12px 14px;
          border: 1px solid #dbe3ed;
          border-radius: 14px;
          background: #ffffff;
          box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
        }
        .admin-app-navigation__title {
          min-width: 0;
          font-size: 1rem;
          font-weight: 800;
          color: #172033;
        }
        .admin-app-navigation__overview { margin-left: auto; }

        .admin-source-status {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin: 12px 0;
        }

        .admin-inline-warning {
          border: 1px solid #fedf89;
          border-radius: 10px;
          background: #fffaeb;
          color: #93370d;
          padding: 10px 12px;
          margin: 10px 0;
          font-size: 0.9rem;
        }

        @media (max-width: 1100px) {
          .admin-app-launcher { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .admin-status-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }

        @media (max-width: 620px) {
          .admin-app-launcher { grid-template-columns: 1fr; }
          .admin-app-tile { min-height: 118px; }
          .admin-status-grid { grid-template-columns: 1fr; }
        }

        .admin-shopify-table th,
        .admin-shopify-table td,
        .admin-verifications-table th,
        .admin-verifications-table td {
          vertical-align: top;
        }

        .admin-shopify-table th:nth-child(2),
        .admin-shopify-table td:nth-child(2) {
          width: 280px;
          min-width: 280px;
        }

        .admin-shopify-table td,
        .admin-verifications-table td {
          padding-top: 14px;
          padding-bottom: 14px;
        }

        .admin-limited-list {
          max-height: 620px;
          overflow-y: auto;
          overscroll-behavior: contain;
          border-radius: 12px;
        }

        .admin-limited-list table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f8fafc;
        }

        .admin-logistics-table {
          min-width: 1320px;
          table-layout: auto;
        }

        .admin-logistics-table th:nth-child(2),
        .admin-logistics-table td:nth-child(2) {
          width: auto;
          min-width: 180px;
        }

        .admin-logistics-table td:last-child {
          width: 360px;
          min-width: 360px;
        }

        .admin-order-primary {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .admin-order-secondary,
        .admin-muted-text {
          color: #667085;
          font-size: 0.92rem;
          line-height: 1.4;
        }

        .admin-order-stack,
        .admin-status-stack {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .admin-order-stack strong,
        .admin-status-stack strong {
          color: #101828;
        }

        .logistics-detail-panel,
        .logistics-invoice-panel {
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          background: #f8fafc;
          padding: 10px 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .logistics-detail-panel small,
        .logistics-invoice-panel small {
          color: #475467;
          line-height: 1.35;
        }

        .logistics-invoice-panel {
          flex-direction: row;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }

        .logistics-invoice-panel > div {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }

        .logistics-invoice-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 8px;
        }

        .logistics-invoice-actions .action-button {
          white-space: nowrap;
        }

        .logistics-action-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .admin-quick-stats {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 18px;
        }

        .admin-quick-stat-card {
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          padding: 14px 16px;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
          min-width: 0;
        }

        .admin-quick-stat-card span {
          display: block;
          color: #667085;
          font-size: 0.84rem;
          margin-bottom: 6px;
        }

        .admin-quick-stat-card strong {
          color: #101828;
          font-size: 1.3rem;
          line-height: 1;
        }

        .admin-activity-layout {
          display: grid;
          grid-template-columns: minmax(0, 1.4fr) minmax(300px, 0.9fr);
          gap: 16px;
          align-items: stretch;
        }

        .admin-activity-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .admin-activity-card {
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          padding: 14px 16px;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
          min-width: 0;
        }

        .admin-activity-card span {
          display: block;
          color: #667085;
          font-size: 0.84rem;
          margin-bottom: 6px;
        }

        .admin-activity-card strong {
          color: #101828;
          font-size: 1.45rem;
          line-height: 1;
          display: block;
          margin-bottom: 8px;
        }

        .admin-activity-card small {
          color: #667085;
          font-size: 0.82rem;
          line-height: 1.35;
          display: block;
        }

        .admin-activity-feed {
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          background: #fff;
          padding: 14px 16px;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
          min-width: 0;
        }

        .admin-activity-feed__title {
          margin: 0 0 10px;
          color: #101828;
          font-size: 1rem;
        }

        .admin-activity-feed__list {
          display: grid;
          gap: 10px;
        }

        .admin-activity-feed__item {
          border: 1px solid #eef2f6;
          border-radius: 12px;
          padding: 10px 12px;
          background: #fcfcfd;
        }

        .admin-activity-feed__top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 4px;
        }

        .admin-activity-feed__name {
          font-weight: 700;
          color: #101828;
          line-height: 1.3;
        }

        .admin-activity-feed__meta {
          color: #667085;
          font-size: 0.86rem;
          line-height: 1.4;
        }

        .admin-activity-feed__empty {
          color: #667085;
          font-size: 0.92rem;
        }

        .admin-activity-open-button {
          border: 1px solid #d0d5dd;
          background: #fff;
          color: #344054;
          border-radius: 8px;
          padding: 6px 10px;
          font-size: 0.78rem;
          font-weight: 700;
          line-height: 1;
          white-space: nowrap;
        }

        .admin-activity-open-button:hover {
          background: #f9fafb;
        }

        .admin-filter-toolbar {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
          flex-wrap: wrap;
        }

        .admin-filter-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .admin-filter-pill {
          background: #fff;
          border: 1px solid #d0d5dd;
          color: #344054;
          padding: 9px 12px;
          border-radius: 999px;
          font-size: 0.88rem;
          font-weight: 700;
          line-height: 1;
        }

        .admin-filter-pill--active {
          background: #0f4c81;
          border-color: #0f4c81;
          color: #fff;
        }

        .admin-search-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1 1 360px;
          justify-content: flex-end;
        }

        .admin-search-input {
          min-width: 0;
          width: 100%;
          max-width: 420px;
          height: 42px;
          padding: 10px 14px;
        }

        .admin-search-clear {
          background: #f8fafc;
          border: 1px solid #d0d5dd;
          color: #344054;
          padding: 9px 12px;
          border-radius: 10px;
          font-weight: 700;
          white-space: nowrap;
        }

        .admin-search-clear:hover {
          background: #eef2f6;
        }

        .admin-mobile-list {
          display: grid;
          gap: 14px;
        }

        .admin-mobile-card {
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 14px;
          background: #fff;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
        }

        .admin-mobile-card__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 10px;
        }

        .admin-mobile-card__title {
          font-weight: 700;
          color: #101828;
          line-height: 1.3;
        }

        .admin-mobile-card__subtitle {
          font-size: 0.92rem;
          color: #667085;
          margin-top: 2px;
        }

        .admin-mobile-card__section {
          border-top: 1px solid #eef2f6;
          padding-top: 10px;
          margin-top: 10px;
        }

        .mobile-info-row {
          display: grid;
          grid-template-columns: 110px 1fr;
          gap: 10px;
          align-items: start;
          margin-bottom: 8px;
        }

        .mobile-info-label {
          font-size: 0.85rem;
          font-weight: 700;
          color: #667085;
        }

        .mobile-info-value {
          min-width: 0;
          word-break: break-word;
          color: #101828;
        }

        .admin-mobile-actions {
          display: grid;
          gap: 8px;
          margin-top: 12px;
        }

        .admin-mobile-actions .action-button,
        .admin-action-grid--responsive .action-button {
          width: 100%;
          justify-content: center;
        }

        .admin-action-grid--responsive {
          display: grid;
          gap: 7px;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          align-items: stretch;
        }

        .admin-action-grid--responsive .action-button {
          min-height: 34px;
          padding: 7px 10px;
          font-size: 0.79rem;
          font-weight: 700;
          border-radius: 10px;
          line-height: 1.15;
          box-shadow: none;
        }

        .admin-action-grid--responsive .action-button--primary,
        .admin-action-grid--responsive .action-button--success,
        .admin-action-grid--responsive .action-button--warning,
        .admin-action-grid--responsive .action-button--danger,
        .admin-action-grid--responsive .action-button--secondary {
          white-space: nowrap;
        }

        .admin-warning-box,
        .admin-muted-box {
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 0.83rem;
          line-height: 1.35;
        }

        .admin-warning-box {
          border: 1px solid #fed7aa;
          background: #fff7ed;
          color: #9a3412;
        }

        .admin-muted-box {
          border: 1px solid #d0d5dd;
          background: #f9fafb;
          color: #475467;
        }

        .admin-order-customer-compact {
          display: grid;
          gap: 6px;
          min-width: 0;
        }

        .admin-order-customer-compact__row {
          display: grid;
          grid-template-columns: 44px minmax(0, 1fr) auto;
          align-items: center;
          gap: 8px;
          padding: 0;
          font-size: 0.92rem;
          line-height: 1.35;
          min-width: 0;
        }

        .admin-order-customer-compact__label {
          font-size: 0.78rem;
          font-weight: 700;
          color: #667085;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          padding-top: 0;
          white-space: nowrap;
        }

        .admin-order-customer-compact__value {
          color: #101828;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          word-break: normal;
          overflow-wrap: normal;
        }

        .admin-order-customer-compact__row.is-email .admin-order-customer-compact__value {
          font-size: 0.89rem;
        }

        .admin-order-customer-compact__row.is-address {
          align-items: start;
        }

        .admin-order-customer-compact__row.is-address .admin-order-customer-compact__value {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          white-space: normal;
          text-overflow: initial;
          word-break: normal;
          overflow-wrap: anywhere;
          line-height: 1.35;
        }

        .admin-inline-copy-button {
          border: 1px solid #d0d5dd;
          background: #ffffff;
          color: #475467;
          border-radius: 8px;
          padding: 4px 8px;
          font-size: 0.72rem;
          font-weight: 700;
          line-height: 1;
          min-width: 46px;
          height: 26px;
          cursor: pointer;
          transition: all 0.15s ease;
          flex-shrink: 0;
        }

        .admin-inline-copy-button:hover:not(:disabled) {
          background: #f9fafb;
          border-color: #98a2b3;
        }

        .admin-inline-copy-button:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .admin-inline-copy-button--copied {
          background: #ecfdf3;
          border-color: #12b76a;
          color: #027a48;
        }

        .admin-usage-agreement {
          border: 1px solid #dbe4ef;
          border-radius: 14px;
          background: #f8fafc;
          padding: 12px;
          margin-top: 12px;
          display: grid;
          gap: 10px;
        }

        .admin-usage-agreement--empty {
          background: #fcfcfd;
        }

        .admin-usage-agreement__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }

        .admin-usage-agreement__header strong {
          color: #101828;
        }

        .admin-usage-agreement__rows {
          display: grid;
          gap: 7px;
        }

        .admin-usage-agreement__row {
          display: grid;
          grid-template-columns: 118px minmax(0, 1fr) auto;
          align-items: start;
          gap: 8px;
          font-size: 0.88rem;
          line-height: 1.35;
        }

        .admin-usage-agreement__label {
          color: #667085;
          font-size: 0.78rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        .admin-usage-agreement__value {
          color: #101828;
          min-width: 0;
          overflow-wrap: anywhere;
        }

        .admin-usage-agreement__notice {
          margin: 0;
          color: #92400e;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 10px;
          padding: 9px 10px;
          font-size: 0.86rem;
          line-height: 1.4;
        }

        .admin-usage-agreement__actions {
          display: grid;
          grid-template-columns: minmax(0, 1fr) repeat(2, minmax(0, 0.7fr));
          gap: 8px;
        }

        .admin-usage-agreement__type-select {
          display: grid;
          gap: 4px;
          font-size: 0.78rem;
          color: #475467;
          font-weight: 700;
        }

        .admin-usage-agreement__type-select select {
          min-height: 34px;
          border: 1px solid #cfd8e3;
          border-radius: 10px;
          padding: 7px 9px;
          background: #ffffff;
          color: #101828;
          font-weight: 700;
          min-width: 0;
        }

        .admin-usage-agreement__actions .action-button {
          min-height: 34px;
          padding: 7px 10px;
          font-size: 0.79rem;
          border-radius: 10px;
        }

        .admin-verification-edit {
          border: 1px solid #dbe4ef;
          border-radius: 14px;
          background: #ffffff;
          padding: 14px;
          display: grid;
          gap: 12px;
        }

        .admin-verification-edit__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }

        .admin-verification-edit__grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }

        .admin-verification-edit__grid label {
          display: grid;
          gap: 5px;
          color: #344054;
          font-size: 0.82rem;
          font-weight: 700;
        }

        .admin-verification-edit__grid input,
        .admin-verification-edit__grid select {
          width: 100%;
          border: 1px solid #d0d5dd;
          border-radius: 9px;
          padding: 8px 9px;
          color: #101828;
          font: inherit;
          font-weight: 500;
          background: #ffffff;
        }

        .admin-logistics-edit-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          min-width: 300px;
        }

        .admin-logistics-edit-grid label {
          font-size: 0.84rem;
          gap: 5px;
        }

        .admin-logistics-edit-grid input,
        .admin-logistics-edit-grid select,
        .admin-logistics-edit-grid textarea {
          width: 100%;
          padding: 9px 10px;
          font-size: 0.88rem;
          border-radius: 8px;
        }

        @media (min-width: 1024px) {
          .admin-action-grid--responsive {
            min-width: 236px;
          }
        }

        .admin-shopify-table td:last-child,
        .admin-verifications-table td:last-child {
          width: 248px;
        }

        .admin-action-grid--responsive .action-button:disabled {
          opacity: 0.7;
        }

        @media (max-width: 980px) {
          .admin-dashboard-desktop-only {
            display: none;
          }

          .admin-dashboard-mobile-only {
            display: block;
          }

          .admin-section-header {
            gap: 12px;
          }

          .admin-quick-stats {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .admin-verification-edit__grid {
            grid-template-columns: 1fr;
          }
          .admin-activity-layout {
            grid-template-columns: 1fr;
          }

          .admin-activity-grid {
            grid-template-columns: 1fr;
          }


          .admin-filter-toolbar {
            flex-direction: column;
            align-items: stretch;
          }

          .admin-search-wrap {
            width: 100%;
            justify-content: stretch;
          }

          .admin-search-input {
            max-width: none;
          }

          .admin-action-grid--responsive {
            grid-template-columns: 1fr;
          }

          .admin-toolbar--compact {
            width: 100%;
            align-items: flex-start;
          }

          .mobile-info-row {
            grid-template-columns: 1fr;
            gap: 4px;
          }

          .mobile-info-label {
            font-size: 0.8rem;
          }

          .admin-order-customer-compact__row {
            grid-template-columns: 40px minmax(0, 1fr) auto;
            gap: 6px;
          }

          .admin-order-customer-compact__value {
            white-space: normal;
            text-overflow: initial;
            overflow-wrap: anywhere;
          }

          .admin-inline-copy-button {
            min-width: 42px;
            padding: 4px 6px;
            font-size: 0.68rem;
          }

          .admin-mobile-card {
            padding: 12px;
          }

          .admin-mobile-card__header {
            flex-direction: column;
            align-items: flex-start;
          }

          .checkbox-inline {
            flex-wrap: wrap;
          }

          .admin-logistics-edit-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <aside className="admin-hub-sidebar">
        <a className="admin-hub-brand" href="https://yourgermanaddress.com">
          <span className="admin-hub-brand__mark" aria-hidden="true">Y</span>
          <span>YourGermanAddress</span>
        </a>
        <p className="admin-hub-sidebar__label">ADMINISTRATION</p>
        <nav className="admin-hub-navigation" aria-label="Admin-Navigation">
          {['overview', ...VALID_ADMIN_AREAS].map((area) => (
            <button
              key={area}
              type="button"
              className={`admin-hub-navigation__item ${activeAdminArea === area ? 'is-active' : ''}`}
              onClick={() => openAdminArea(area)}
            >
              <span className="admin-hub-navigation__icon" aria-hidden="true">
                {area === 'system-status' ? '●' : area === 'overview' ? '⌂' : '□'}
              </span>
              <span>{ADMIN_AREA_LABELS[area]}</span>
              {area === 'system-status' && systemStatus?.counts?.error > 0 ? (
                <span className="admin-hub-navigation__count">{systemStatus.counts.error}</span>
              ) : null}
              {area === 'notifications' && notificationOverview?.counts?.critical > 0 ? (
                <span className="admin-hub-navigation__count">{notificationOverview.counts.critical}</span>
              ) : null}
              {area === 'scans' && unassignedScanCount > 0 ? (
                <span className="admin-hub-navigation__count">{unassignedScanCount}</span>
              ) : null}
              {area === 'access-reviews' && (accessReviews.filter((item) => item.status === 'pending_confirmation').length + (nextcloudInventory?.unassigned_accounts?.length || 0)) > 0 ? (
                <span className="admin-hub-navigation__count">{accessReviews.filter((item) => item.status === 'pending_confirmation').length + (nextcloudInventory?.unassigned_accounts?.length || 0)}</span>
              ) : null}
            </button>
          ))}
        </nav>
        <div className="admin-hub-sidebar__footer">Geschützter Adminbereich</div>
      </aside>

      <section className="admin-hub-workspace">
      <header className="admin-hub-topbar">
        <div>
          <p className="eyebrow">YourGermanAddress</p>
          <h1>{ADMIN_AREA_LABELS[activeAdminArea] || 'Admin Dashboard'}</h1>
          <p className="subtitle">
            Sichere Verwaltung von Verifizierungen, Kundenanfragen, Logistik und Systemzustand.
          </p>
        </div>
        <div className="admin-hub-topbar__actions">
          <a className="secondary-link-button" href="https://yourgermanaddress.com">Zur Website</a>
          <button type="button" className="secondary-button" onClick={() => {
            localStorage.removeItem('adminToken');
            navigate(buildAdminLoginPath(), { replace: true });
          }}>Ausloggen</button>
        </div>
      </header>
      <div className={`admin-hub-content ${activeAdminArea === 'logistics' ? 'admin-hub-content--logistics' : ''}`}>

      {startupWarnings.length ? (
        <div className="warning-box" role="alert">
          <strong>Dashboard teilweise geladen</strong>
          <p>Einzelne Bereiche waren vorübergehend nicht erreichbar. Die verfügbaren Funktionen können weiter genutzt werden.</p>
          <ul>
            {startupWarnings.map((warning) => <li key={warning}>{warning}</li>)}
          </ul>
          <button type="button" className="secondary-button" onClick={fetchData}>Erneut versuchen</button>
        </div>
      ) : null}

      {activeAdminArea !== 'overview' && (
        <nav className="admin-app-navigation" aria-label="Admin-Arbeitsbereich">
          <span className="admin-app-navigation__title">{ADMIN_AREA_LABELS[activeAdminArea]}</span>
          <button
            type="button"
            className="secondary-button admin-app-navigation__overview"
            onClick={() => openAdminArea('overview')}
          >
            ← Zur Übersicht
          </button>
        </nav>
      )}

      <MarketingLeadOverview token={token} active={activeAdminArea === 'marketing-leads'} />

      <section hidden={activeAdminArea !== 'access-reviews'} className="card admin-section-card admin-section-anchor" aria-labelledby="access-reviews-title">
        <div className="admin-section-header">
          <div>
            <h2 id="access-reviews-title">Zugangsprüfungen</h2>
            <p className="subtitle">Erkennung allein sperrt niemanden. Erst nach 30 Tagen und deiner ausdrücklichen Bestätigung werden Nextcloud-, Post- und Vertragszugang deaktiviert. Dateien und Ordner bleiben erhalten.</p>
          </div>
          <button type="button" className="secondary-button" onClick={() => loadAccessReviews({ discover: true })} disabled={loadingAccessReviews}>
            {loadingAccessReviews ? 'Prüfung läuft …' : 'Alle Kunden rückwirkend prüfen'}
          </button>
        </div>
        {accessReviewError ? <div className="error-message" role="alert">{accessReviewError}</div> : null}
        {!accessReviews.length ? <p>Noch keine Zugangsprüfungen vorhanden.</p> : (
          <div className="admin-mobile-list">
            {accessReviews.map((review) => {
              const customer = review.customers || {};
              const eligible = new Date(review.eligible_at).getTime() <= Date.now();
              const statusLabels = {
                observing: '30-Tage-Frist läuft',
                pending_confirmation: 'Bestätigung erforderlich',
                deactivating: 'Deaktivierung läuft',
                deactivated: 'Zugang deaktiviert',
                reactivating: 'Reaktivierung läuft',
                reactivated: 'Aktives Abo erkannt',
                failed: 'Technischer Fehler',
              };
              return (
                <article className="admin-mobile-card" key={review.id || review.customer_id}>
                  <div className="admin-mobile-card__header">
                    <div>
                      <strong>{customer.company_name || customer.name || customer.email || `Kunde ${review.customer_id}`}</strong>
                      <div>{customer.email || '-'}</div>
                    </div>
                    <StatusBadge
                      label={statusLabels[review.status] || review.status}
                      tone={review.status === 'deactivated' ? 'danger' : review.status === 'pending_confirmation' ? 'warning' : review.status === 'reactivated' ? 'success' : 'neutral'}
                    />
                  </div>
                  <p>
                    Grund: {review.reason === 'no_subscription' ? 'Kein Abonnement gefunden' : 'Abonnement beendet'}<br />
                    Fristende: {new Date(review.eligible_at).toLocaleString('de-DE')}<br />
                    Nextcloud: {customer.nextcloud_user_id ? 'Konto vorhanden; wird nur deaktiviert' : 'Kein Benutzerkonto verknüpft'}
                  </p>
                  {review.processing_error ? <div className="error-message">{review.processing_error}</div> : null}
                  {review.status === 'pending_confirmation' && eligible ? (
                    <button
                      type="button"
                      className="action-button action-button--danger"
                      disabled={confirmingAccessCustomerId === review.customer_id}
                      onClick={() => confirmAccessDeactivation(review)}
                    >
                      {confirmingAccessCustomerId === review.customer_id ? 'Prüft erneut …' : 'Zugang nach erneuter Anbieterprüfung deaktivieren'}
                    </button>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
        <div className="admin-section-header" style={{ marginTop: '2rem' }}>
          <div>
            <h3>Nicht zugeordnete Nextcloud-Konten</h3>
            <p className="subtitle">Live-Abgleich des Nextcloud-Benutzerbestands mit den im Business Hub verknüpften Kundenkonten. Diese Liste löst niemals automatisch eine Sperre oder Löschung aus.</p>
          </div>
          <button type="button" className="secondary-button" onClick={loadNextcloudInventory} disabled={loadingNextcloudInventory}>
            {loadingNextcloudInventory ? 'Abgleich läuft …' : 'Nextcloud-Bestand abgleichen'}
          </button>
        </div>
        {nextcloudInventoryError ? <div className="error-message" role="alert">{nextcloudInventoryError}</div> : null}
        {nextcloudInventory ? (
          <>
            <p className="subtitle">
              {nextcloudInventory.linked_count} verknüpft · {nextcloudInventory.unassigned_accounts?.length || 0} manuell zu prüfen
            </p>
            {!nextcloudInventory.unassigned_accounts?.length ? (
              <div className="success-message">Alle erfassten Nextcloud-Kundenkonten sind im Business Hub zugeordnet.</div>
            ) : (
              <div className="admin-mobile-list">
                {nextcloudInventory.unassigned_accounts.map((account) => (
                  <article className="admin-mobile-card" key={account.nextcloud_user_id}>
                    <div className="admin-mobile-card__header">
                      <div>
                        <strong>{account.nextcloud_user_id}</strong>
                        <div>Keine Verknüpfung zu einem Business-Hub-Kunden gefunden</div>
                      </div>
                      <StatusBadge label="Manuell prüfen" tone="warning" />
                    </div>
                    <p>Bitte Kundenzugehörigkeit und Abostatus manuell klären. Dieses Konto wird durch den Inventarabgleich nicht verändert.</p>
                  </article>
                ))}
              </div>
            )}
          </>
        ) : null}
      </section>

      <section hidden={activeAdminArea !== 'metrics'} className="card admin-section-card admin-section-anchor admin-metrics" aria-labelledby="admin-metrics-title">
        <div className="admin-section-header">
          <div>
            <p className="admin-revenue__eyebrow">Geschäftsentwicklung</p>
            <h2 id="admin-metrics-title">Kennzahlen</h2>
            <p className="subtitle">Tatsächliche Zahlungseingänge, Rückerstattungen, Abobewegungen und Kundenbindung.</p>
          </div>
          <button type="button" className="secondary-button" onClick={loadBusinessMetrics} disabled={loadingBusinessMetrics}>
            {loadingBusinessMetrics ? 'Aktualisiert …' : 'Neu berechnen'}
          </button>
        </div>
        <div className="admin-metrics__ranges" aria-label="Auswertungszeitraum">
          {[
            ['7d', '7 Tage'], ['30d', '30 Tage'], ['month', 'Dieser Monat'], ['year', 'Dieses Jahr'],
          ].map(([value, label]) => (
            <button key={value} type="button" className={businessMetricsRange === value ? 'is-active' : ''} onClick={() => setBusinessMetricsRange(value)}>{label}</button>
          ))}
        </div>
        {businessMetrics ? (
          <>
            <div className="admin-metrics__grid">
              <article className="is-primary">
                <span>Zahlungseingänge netto</span>
                <strong>{formatRevenue(businessMetrics.payments?.current?.net_minor, businessMetrics.currency)}</strong>
                <small>{businessMetrics.payments?.change_percent === null ? 'Keine vergleichbare Vorperiode' : `${businessMetrics.payments?.change_percent >= 0 ? '+' : ''}${businessMetrics.payments?.change_percent || 0} % zur Vorperiode`}</small>
              </article>
              <article>
                <span>Bruttozahlungen</span>
                <strong>{formatRevenue(businessMetrics.payments?.current?.gross_minor, businessMetrics.currency)}</strong>
                <small>{businessMetrics.payments?.current?.payments || 0} erfolgreiche Zahlungen</small>
              </article>
              <article>
                <span>Rückerstattungen</span>
                <strong>{formatRevenue(businessMetrics.payments?.current?.refunds_minor, businessMetrics.currency)}</strong>
                <small>Im ausgewählten Zeitraum</small>
              </article>
              <article>
                <span>Zusatzleistungen</span>
                <strong>{formatRevenue(businessMetrics.additional_services_minor, businessMetrics.currency)}</strong>
                <small>Servicegebühren und tatsächliches Porto</small>
              </article>
              <article>
                <span>Zahlungserfolgsquote</span>
                <strong>{(() => { const paid = businessMetrics.payments?.current?.payments || 0; const failed = businessMetrics.failed_payments || 0; return paid + failed ? `${Math.round((paid / (paid + failed)) * 1000) / 10} %` : '–'; })()}</strong>
                <small>{businessMetrics.failed_payments || 0} fehlgeschlagene Versuche</small>
              </article>
              <article>
                <span>Neue Abos</span>
                <strong>{businessMetrics.subscriptions?.new_subscriptions || 0}</strong>
                <small>{businessMetrics.subscriptions?.cancellations || 0} Kündigungen</small>
              </article>
              <article>
                <span>Kunden-Churn</span>
                <strong>{businessMetrics.subscriptions?.churn_percent || 0} %</strong>
                <small>Basis: {businessMetrics.subscriptions?.active_at_period_start || 0} Abos zum Periodenstart</small>
              </article>
              <article>
                <span>Ø Monatsumsatz pro Abo</span>
                <strong>{formatRevenue((subscriptionRevenue?.totals?.monthly_minor || 0) / Math.max(1, subscriptionRevenue?.totals?.active_subscriptions || 0), subscriptionRevenue?.currency)}</strong>
                <small>ARPU auf Basis des aktuellen MRR</small>
              </article>
              <article>
                <span>Geschätzter Kundenwert</span>
                <strong>{['30d', 'month'].includes(businessMetricsRange) && businessMetrics.subscriptions?.churn_percent > 0 ? formatRevenue(((subscriptionRevenue?.totals?.monthly_minor || 0) / Math.max(1, subscriptionRevenue?.totals?.active_subscriptions || 0)) / (businessMetrics.subscriptions.churn_percent / 100), subscriptionRevenue?.currency) : '–'}</strong>
                <small>{['30d', 'month'].includes(businessMetricsRange) ? 'Vereinfachter LTV: ARPU ÷ Monats-Churn' : 'Nur für einen Monatszeitraum sinnvoll'}</small>
              </article>
            </div>
            <div className="admin-metrics__split">
              <span><strong>Stripe netto:</strong> {formatRevenue(businessMetrics.payments?.providers?.stripe, businessMetrics.currency)}</span>
              <span><strong>Shopify / Seal netto:</strong> {formatRevenue(businessMetrics.payments?.providers?.seal, businessMetrics.currency)}</span>
              <span><strong>Vorperiode:</strong> {formatRevenue(businessMetrics.payments?.previous?.net_minor, businessMetrics.currency)}</span>
            </div>
            <div className="admin-metrics__chart">
              <h3>Zahlungseingänge im Zeitverlauf</h3>
              {(businessMetrics.payments?.daily || []).length ? (
                <div className="admin-metrics__bars">
                  {businessMetrics.payments.daily.map((point) => {
                    const maximum = Math.max(1, ...businessMetrics.payments.daily.map((item) => Math.abs(item.net_minor)));
                    return (
                      <div key={point.date} className={`admin-metrics__bar-column${point.net_minor < 0 ? ' is-negative' : ''}`} title={`${new Date(`${point.date}T12:00:00`).toLocaleDateString('de-DE')}: ${formatRevenue(point.net_minor, businessMetrics.currency)}`}>
                        <span style={{ height: `${Math.max(3, (Math.abs(point.net_minor) / maximum) * 100)}%` }} />
                        <small>{new Date(`${point.date}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</small>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="subtitle">In diesem Zeitraum wurden keine erfolgreichen Zahlungen gefunden.</p>}
            </div>
            {(businessMetrics.warnings || []).length ? <div className="admin-revenue__warning" role="status">{businessMetrics.warnings.map((warning, index) => <span key={`${warning.source}-${index}`}>{warning.message}</span>)}</div> : null}
            <p className="admin-revenue__meta">Netto bedeutet hier erfolgreiche Zahlungen abzüglich erkannter Rückerstattungen; keine steuerliche Gewinn- oder Buchhaltungskennzahl. Stand {new Date(businessMetrics.generated_at).toLocaleString('de-DE')}.</p>
          </>
        ) : <p className="subtitle">{businessMetricsError || 'Kennzahlen werden geladen …'}</p>}
        {businessMetricsError && businessMetrics ? <p className="admin-revenue__warning" role="alert">{businessMetricsError}</p> : null}
      </section>

      <section hidden={activeAdminArea !== 'web-analytics'} className="card admin-section-card admin-section-anchor admin-revenue" aria-labelledby="website-analytics-title">
        <div className="admin-section-header">
          <div>
            <p className="admin-revenue__eyebrow">Vercel Web Analytics</p>
            <h2 id="website-analytics-title">Website-Besucher</h2>
            <p className="subtitle">Anonyme, aggregierte Besucherzahlen der öffentlichen Website.</p>
          </div>
          {websiteAnalytics?.configured ? <span className="admin-revenue__status is-complete">Live verbunden</span> : <span className="admin-revenue__status has-warning">Nicht eingerichtet</span>}
        </div>
        <div className="admin-metrics__ranges" aria-label="Analytics-Zeitraum">
          {[["24h", "24 Stunden"], ["7d", "7 Tage"], ["30d", "30 Tage"]].map(([value, label]) => (
            <button key={value} type="button" className={websiteAnalyticsRange === value ? 'is-active' : ''} onClick={() => setWebsiteAnalyticsRange(value)}>{label}</button>
          ))}
        </div>
        {websiteAnalytics?.configured ? (
          <>
            <div className="admin-metrics__grid admin-website-analytics__grid">
              <article className="is-primary"><span>Gerade aktiv</span><strong>{websiteAnalytics.online || 0}</strong><small>Besucher mit Aktivität in den letzten {websiteAnalytics.live_window_minutes || 5} Minuten</small></article>
              <article><span>Visitors</span><strong>{websiteAnalytics.visitors || 0}</strong><small>Einmalige Besucher im ausgewählten Zeitraum</small></article>
              <article><span>Page Views</span><strong>{websiteAnalytics.pageviews || 0}</strong><small>Seitenaufrufe im ausgewählten Zeitraum</small></article>
            </div>
            <div className="admin-metrics__chart">
              <h3>Visitors im Zeitverlauf</h3>
              {(websiteAnalytics.points || []).length ? (
                <div className="admin-metrics__bars">
                  {websiteAnalytics.points.map((point) => {
                    const maximum = Math.max(1, ...websiteAnalytics.points.map((item) => item.visitors || 0));
                    const date = new Date(point.timestamp);
                    const label = websiteAnalyticsRange === '24h' ? date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
                    return <div key={point.timestamp} className="admin-metrics__bar-column" title={`${label}: ${point.visitors} Visitors`}><span style={{ height: `${Math.max(3, ((point.visitors || 0) / maximum) * 100)}%` }} /><small>{label}</small></div>;
                  })}
                </div>
              ) : <p className="subtitle">Im ausgewählten Zeitraum wurden keine Besucher erfasst.</p>}
            </div>
            <p className="admin-revenue__meta">Stand {new Date(websiteAnalytics.generated_at).toLocaleString('de-DE')} · Datenschutzfreundliche Vercel-Aggregate ohne individuelle Besucherprofile.</p>
          </>
        ) : <p className="subtitle">Für diese Anzeige muss einmalig ein geschütztes Vercel-API-Token auf dem VPS hinterlegt werden.</p>}
        {websiteAnalyticsError ? <p className="admin-revenue__warning" role="alert">{websiteAnalyticsError}</p> : null}
        <div className="admin-conversion-funnel">
          <div className="admin-section-header">
            <div>
              <p className="admin-revenue__eyebrow">Eigener Conversion-Funnel</p>
              <h3>Vom Preisvergleich bis zum Abschluss</h3>
              <p className="subtitle">Datensparsame Ereignisse aus Website und Business Hub. Erfolgreiche Abschlüsse werden ausschließlich durch den signierten Stripe-Webhook bestätigt.</p>
            </div>
          </div>
          {conversionAnalytics ? (
            <div className="admin-conversion-funnel__steps">
              {[
                ['pricing_viewed', 'Preise angesehen'],
                ['plan_viewed', 'Paketdetail angesehen'],
                ['plan_selected', 'Tarif ausgewählt'],
                ['checkout_started', 'Checkout gestartet'],
                ['checkout_completed', 'Zahlung abgeschlossen'],
                ['verification_started', 'Verifizierung begonnen'],
                ['verification_submitted', 'Verifizierung eingereicht'],
              ].map(([key, label]) => {
                const value = conversionAnalytics.steps?.unique?.[key] || 0;
                const rate = conversionAnalytics.steps?.rates?.[key] || 0;
                const previous = conversionAnalytics.previous?.unique?.[key] || 0;
                const change = previous ? Math.round(((value - previous) / previous) * 100) : null;
                return <article key={key}><span>{label}</span><strong>{value}</strong><small>{rate.toLocaleString('de-DE')} % der Preisbesucher{change === null ? '' : ` · ${change >= 0 ? '+' : ''}${change} % zur Vorperiode`}</small></article>;
              })}
            </div>
          ) : <p className="subtitle">{conversionAnalyticsError || 'Conversion-Funnel wird geladen …'}</p>}
          {conversionAnalytics ? (
            <>
              <div className="admin-section-header">
                <div>
                  <h3>Express-Entscheidung</h3>
                  <p className="subtitle">Angebot angezeigt, gewählt, übersprungen oder ohne Checkout geschlossen.</p>
                </div>
              </div>
              <div className="admin-conversion-funnel__steps">
                {[
                  ['express_offer_shown', 'Angezeigt'],
                  ['express_offer_accepted', 'Hinzugefügt'],
                  ['express_offer_skipped', 'Ohne Express weiter'],
                  ['express_offer_dismissed', 'Geschlossen'],
                ].map(([key, label]) => <article key={key}><span>{label}</span><strong>{conversionAnalytics.steps?.unique?.[key] || 0}</strong></article>)}
              </div>
              {(conversionAnalytics.steps?.experiments || []).length ? (
                <div className="admin-conversion-experiments">
                  <h3>A/B-Varianten</h3>
                  <div className="admin-conversion-funnel__steps">
                    {conversionAnalytics.steps.experiments.map((experiment) => {
                      const exposed = experiment.unique?.experiment_exposed || 0;
                      const started = experiment.unique?.checkout_started || 0;
                      const completed = experiment.unique?.checkout_completed || 0;
                      return <article key={`${experiment.experiment_key}-${experiment.variant}`}>
                        <span>{experiment.experiment_key} · {experiment.variant}</span>
                        <strong>{completed}</strong>
                        <small>{exposed} Exposures · {started} Checkout-Starts · {exposed ? ((completed / exposed) * 100).toFixed(1) : '0.0'} % Abschlussrate</small>
                      </article>;
                    })}
                  </div>
                </div>
              ) : <p className="subtitle">Noch keine aktive A/B-Testvariante im gewählten Zeitraum.</p>}
            </>
          ) : null}
          {conversionAnalyticsError && conversionAnalytics ? <p className="admin-revenue__warning" role="alert">{conversionAnalyticsError}</p> : null}
        </div>
      </section>

      <section hidden={activeAdminArea !== 'metrics'} className="card admin-section-card admin-section-anchor admin-revenue" aria-labelledby="admin-revenue-title">
        <div className="admin-section-header">
          <div>
            <p className="admin-revenue__eyebrow">Wiederkehrender Umsatz</p>
            <h2 id="admin-revenue-title">Erwarteter Abo-Umsatz</h2>
            <p className="subtitle">Aktive, verlängerbare Abos aus Stripe und Shopify / Seal. Einmalige Zusatzleistungen sind nicht enthalten.</p>
          </div>
          {subscriptionRevenue ? (
            <span className={`admin-revenue__status ${subscriptionRevenue.complete ? 'is-complete' : 'has-warning'}`}>
              {subscriptionRevenue.complete
                ? (subscriptionRevenue.estimated ? 'Vollständig · EZB-Näherungswert' : 'Daten vollständig')
                : 'Teilweise verfügbar'}
            </span>
          ) : null}
        </div>
        {subscriptionRevenue ? (
          <>
            <div className="admin-revenue__calendar-controls">
              <label>
                Prognosemonat
                <select value={subscriptionForecastMonth} onChange={(event) => {
                  setSubscriptionForecastMonth(event.target.value);
                  setSubscriptionForecastWeek('');
                }}>
                  {subscriptionForecastMonths.map((month) => <option key={month.key} value={month.key}>{month.label}</option>)}
                </select>
              </label>
              <label>
                Monatswoche
                <select value={subscriptionForecastWeek} onChange={(event) => setSubscriptionForecastWeek(event.target.value)}>
                  {(subscriptionRevenue.forecast?.weeks || []).map((week) => (
                    <option key={week.key} value={week.key}>{formatForecastDate(week.start)}–{formatForecastDate(week.end)}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="admin-revenue__kpis">
              <article>
                <span>Heute tatsächlich erwartet · {formatForecastDate(subscriptionRevenue.forecast?.today?.date)}</span>
                <strong>{formatRevenue(subscriptionRevenue.forecast?.today?.amount_minor, subscriptionRevenue.currency)}</strong>
                <small>{formatRevenue(subscriptionRevenue.forecast?.today?.renewals_minor || 0, subscriptionRevenue.currency)} Verlängerungen + {formatRevenue(subscriptionRevenue.forecast?.today?.new_subscriptions_minor || 0, subscriptionRevenue.currency)} Neuabschlüsse</small>
                <small>{formatSubscriptionCounts(subscriptionRevenue.forecast?.today?.renewal_count, subscriptionRevenue.forecast?.today?.new_subscription_count)}</small>
              </article>
              <article>
                <span>{(() => { const week = subscriptionRevenue.forecast?.weeks?.find((item) => item.key === subscriptionForecastWeek); return week ? `Erwartet ${formatForecastDate(week.start)}–${formatForecastDate(week.end)}` : 'Ausgewählte Monatswoche'; })()}</span>
                <strong>{formatRevenue(subscriptionRevenue.forecast?.weeks?.find((item) => item.key === subscriptionForecastWeek)?.amount_minor || 0, subscriptionRevenue.currency)}</strong>
                <small>{(() => { const week = subscriptionRevenue.forecast?.weeks?.find((item) => item.key === subscriptionForecastWeek); return `${formatRevenue(week?.renewals_minor || 0, subscriptionRevenue.currency)} Verlängerungen + ${formatRevenue(week?.new_subscriptions_minor || 0, subscriptionRevenue.currency)} Neuabschlüsse`; })()}</small>
                <small>{(() => { const week = subscriptionRevenue.forecast?.weeks?.find((item) => item.key === subscriptionForecastWeek); return formatSubscriptionCounts(week?.renewal_count, week?.new_subscription_count); })()}</small>
              </article>
              <article className="is-primary">
                <span>Im ausgewählten Monat erwartet</span>
                <strong>{formatRevenue(subscriptionRevenue.forecast?.month_minor, subscriptionRevenue.currency)}</strong>
                <small>{formatRevenue(subscriptionRevenue.forecast?.month_renewals_minor || 0, subscriptionRevenue.currency)} Verlängerungen + {formatRevenue(subscriptionRevenue.forecast?.month_new_subscriptions_minor || 0, subscriptionRevenue.currency)} Neuabschlüsse</small>
                <small>{formatSubscriptionCounts(subscriptionRevenue.forecast?.month_renewal_count, subscriptionRevenue.forecast?.month_new_subscription_count)}</small>
              </article>
            </div>
            <div className="admin-revenue__sources">
              <span><strong>Stripe</strong> {formatRevenue(subscriptionRevenue.providers?.stripe?.monthly_minor, subscriptionRevenue.currency)} / Monat · {subscriptionRevenue.providers?.stripe?.active_subscriptions || 0} Abos</span>
              <span><strong>Shopify / Seal</strong> {formatRevenue(subscriptionRevenue.providers?.seal?.monthly_minor, subscriptionRevenue.currency)} / Monat · {subscriptionRevenue.providers?.seal?.active_subscriptions || 0} Abos</span>
              <span><strong>Gesamt</strong> {subscriptionRevenue.totals?.active_subscriptions || 0} aktive Abos</span>
              <span><strong>Monatliche Run-Rate</strong> {formatRevenue(subscriptionRevenue.totals?.monthly_minor, subscriptionRevenue.currency)}</span>
            </div>
            {Object.entries(subscriptionRevenue.conversion?.native_monthly || {})
              .filter(([currency]) => currency !== 'EUR')
              .length ? (
                <div className="admin-revenue__currencies" aria-label="Originalbeträge der Fremdwährungs-Abos">
                  <strong>Originalwährungen:</strong>
                  {Object.entries(subscriptionRevenue.conversion.native_monthly)
                    .filter(([currency]) => currency !== 'EUR')
                    .sort(([left], [right]) => left.localeCompare(right))
                    .map(([currency, amountMinor]) => (
                      <span key={currency}>{formatRevenue(amountMinor, currency)} / Monat</span>
                    ))}
                </div>
              ) : null}
            {(subscriptionRevenue.warnings || []).length ? (
              <div className="admin-revenue__warning" role="status">
                {subscriptionRevenue.warnings.map((warning, index) => <span key={`${warning.source}-${index}`}>{warning.message}</span>)}
              </div>
            ) : null}
            <p className="admin-revenue__meta">
              Stand {new Date(subscriptionRevenue.generated_at).toLocaleString('de-DE')}
              {subscriptionRevenue.conversion?.reference_date ? ` · EZB-Referenzkurs vom ${new Date(`${subscriptionRevenue.conversion.reference_date}T12:00:00`).toLocaleDateString('de-DE')}` : ''}
              {' · '}Fälligkeiten werden jede Minute aktualisiert · Kündigungen werden sofort herausgerechnet.
            </p>
          </>
        ) : (
          <p className="subtitle">{subscriptionRevenueError || 'Umsatzprognose wird geladen …'}</p>
        )}
        {subscriptionRevenueError && subscriptionRevenue ? <p className="admin-revenue__warning" role="alert">{subscriptionRevenueError}</p> : null}
      </section>

      <section hidden={activeAdminArea !== 'overview'} className="card admin-section-card admin-section-anchor" aria-labelledby="admin-apps-title">
        <div className="admin-section-header">
          <div>
            <h2 id="admin-apps-title">Arbeitsbereiche</h2>
            <p className="subtitle">Direkt zum gewünschten Bereich springen. Alle bisherigen Funktionen bleiben unverändert verfügbar.</p>
          </div>
        </div>
        <div className="admin-app-launcher">
          {adminDashboardCardOrder.map((cardId) => {
            const card = adminAppCards[cardId];
            return (
              <button
                key={cardId}
                type="button"
                draggable={!savingAdminCardOrder}
                aria-busy={savingAdminCardOrder}
                className={`admin-app-tile ${card.statusClass || ''} ${draggedAdminCardId === cardId ? 'is-dragging' : ''} ${dragOverAdminCardId === cardId && draggedAdminCardId !== cardId ? 'is-drop-target' : ''}`}
                aria-label={`${card.label}. Ziehen zum Sortieren; Alt und Pfeiltaste verschiebt die Karte.`}
                onClick={() => {
                  if (!suppressAdminCardClickRef.current) openAdminArea(cardId);
                }}
                onDragStart={(event) => {
                  setDraggedAdminCardId(cardId);
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', cardId);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  if (draggedAdminCardId && draggedAdminCardId !== cardId) setDragOverAdminCardId(cardId);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  handleAdminCardDrop(cardId);
                }}
                onDragEnd={() => {
                  setDraggedAdminCardId('');
                  setDragOverAdminCardId('');
                }}
                onKeyDown={(event) => handleAdminCardKeyboardMove(event, cardId)}
              >
                <span className="admin-app-tile__top">
                  <span className="admin-app-tile__icon">{card.icon}</span>
                  <span className="admin-app-tile__meta">
                    {card.count !== null ? <span className="admin-app-tile__count">{card.count}</span> : null}
                    <span className="admin-app-tile__drag" aria-hidden="true">⋮⋮</span>
                  </span>
                </span>
                <strong>{card.label}</strong>
                <small>{card.description}</small>
              </button>
            );
          })}
        </div>
        {cardOrderSaveError ? <p className="admin-app-order-error" role="alert">{cardOrderSaveError}</p> : null}
        <div className="admin-system-status" aria-label="Systemstatus">
          <span className={`admin-system-status__item ${notificationStatus.ok ? 'admin-system-status__item--ok' : 'admin-system-status__item--warning'}`}>
            {notificationStatus.label}
          </span>
          <div className="admin-notification-switch" aria-label="Benachrichtigungskanal">
            <button
              type="button"
              className={settings.admin_notification_channel === 'telegram' ? 'is-active' : ''}
              disabled={savingNotificationChannel || !settings.telegram_admin_configured}
              onClick={() => updateAdminNotificationChannel('telegram')}
              title={!settings.telegram_admin_configured ? 'Telegram ist noch nicht vollständig eingerichtet.' : 'Telegram mit E-Mail-Fallback verwenden'}
            >
              Telegram
            </button>
            <button
              type="button"
              className={settings.admin_notification_channel === 'email' ? 'is-active' : ''}
              disabled={savingNotificationChannel}
              onClick={() => updateAdminNotificationChannel('email')}
            >
              E-Mail
            </button>
          </div>
          {settings.mailbox_display_url ? (
            <a
              className="admin-system-status__item admin-system-status__item--ok admin-system-status__link"
              href={settings.mailbox_display_url}
              target="_blank"
              rel="noreferrer"
            >
              Briefkastenanzeige öffnen ↗
            </a>
          ) : (
            <span className="admin-system-status__item admin-system-status__item--warning">
              Briefkasten-Token fehlt
            </span>
          )}
          <button
            type="button"
            className="secondary-button admin-system-status__test"
            onClick={testAdminNotification}
            disabled={testingAdminNotification}
          >
            {testingAdminNotification ? 'Test läuft…' : 'Benachrichtigung testen'}
          </button>
        </div>
      </section>

      <div hidden={activeAdminArea !== 'chat-automation'}>
        <ChatAutomationSettingsCard
          settings={settings.chat_auto_acknowledgement}
          token={token}
          onSaved={(chatSettings) => setSettings((current) => ({
            ...current,
            chat_auto_acknowledgement: chatSettings,
          }))}
        />
      </div>

      <div hidden={activeAdminArea !== 'google-reviews'}>
        <GoogleReviewAdminSettingsCard
          settings={settings.google_reviews}
          token={token}
          onSaved={(reviewSettings) => setSettings((current) => ({
            ...current,
            google_reviews: reviewSettings,
          }))}
        />
      </div>

      <section hidden={activeAdminArea !== 'open-operations'} id="open-operations" className="card admin-section-card admin-section-anchor">
        <div className="admin-section-header">
          <div>
            <h2>Offene Vorgänge</h2>
            <p className="subtitle">Priorisierte Arbeitsliste aus Systemstatus, Verifizierungen, Kundenanfragen, Versand, Zugangsprüfungen und Nextcloud-Zuordnungen.</p>
          </div>
          <button type="button" className="secondary-button" onClick={() => { fetchData(); loadSystemStatus({ force: true }); loadAccessReviews(); loadNextcloudInventory(); }} disabled={loading || loadingSystemStatus}>
            {loading || loadingSystemStatus ? 'Aktualisierung läuft…' : 'Alles aktualisieren'}
          </button>
        </div>

        <div className="admin-release-panel" aria-label="Zentrale Produktionsstände">
          {[productionStatus?.business_hub, productionStatus?.website].filter(Boolean).map((application) => (
            <article className="admin-release-card" key={application.application}>
              <div className="admin-release-card__header">
                <h3>{application.label}</h3>
                <span className={`admin-release-state admin-release-state--${application.status}`}>{productionStatusLabel(application.status)}</span>
              </div>
              <div className="admin-release-grid">
                <div><span>Umgebung</span><strong>{application.release?.environment === 'production' ? 'Produktion' : application.release?.environment || 'Unbekannt'}</strong></div>
                <div><span>Commit</span><strong>{application.release?.short_commit || 'Unbekannt'}</strong></div>
                <div><span>Deployment</span><strong>{formatReleaseTimestamp(application.release?.deployed_at)}</strong></div>
                {application.migrations && <div><span>Migrationen</span><strong>{application.migrations.status === 'confirmed' ? application.migrations.applied_head : application.migrations.status === 'inconsistent' ? 'Widerspruch' : `${application.migrations.expected_count} erwartet · DB-Stand unbekannt`}</strong></div>}
                {application.configuration?.flags && (
                  <div className="admin-release-config">
                    <span>Unkritische Konfigurationsstände · {application.configuration.fingerprint}</span>
                    <ul>{Object.entries(application.configuration.flags).map(([key, value]) => <li key={key}>{key.replaceAll('_', ' ')}: {configurationStateLabel(value)}</li>)}</ul>
                  </div>
                )}
              </div>
              {application.notes?.map((note) => <p className="admin-release-note" key={note}>{note}</p>)}
            </article>
          ))}
          {!productionStatus && <div className="admin-release-card">Produktionsstände werden geladen.</div>}
        </div>

        <section className="admin-legal-entity-overview" aria-labelledby="admin-legal-entity-overview-heading">
          <div className="admin-section-header">
            <div>
              <h3 id="admin-legal-entity-overview-heading">Unternehmensprofile</h3>
              <p className="subtitle">Datensparsame Zuordnung über unveränderliche interne IDs; Namen und E-Mail-Adressen werden nicht als Schlüssel verwendet.</p>
            </div>
            <span className={`status-pill ${legalEntityOverview.migration_pending ? 'status-pill--warning' : 'status-pill--success'}`}>
              {legalEntityOverview.migration_pending ? 'Migration ausstehend' : `${legalEntityOverview.entities.length} Profile`}
            </span>
          </div>
          {legalEntityOverview.entities.length ? (
            <div className="admin-legal-entity-accounts">
              {Object.entries(legalEntityOverview.entities.reduce((groups, entity) => {
                const key = entity.account_ref || 'nicht-verknüpft';
                groups[key] = [...(groups[key] || []), entity];
                return groups;
              }, {})).map(([accountRef, entities]) => (
                <section className="admin-legal-entity-account" key={accountRef}>
                  <h4>Kundenkonto {accountRef}</h4>
                  <div className="admin-legal-entity-grid">
                    {entities.map((entity) => (
                      <article key={entity.id}>
                        <strong>{getLegalEntityDisplayName(entity)}</strong>
                        {entity.profile_label && entity.legal_name ? <span>Rechtlicher Name: {entity.legal_name}</span> : null}
                        <span>{entity.legal_form || entity.entity_type} · {entity.country_code}</span>
                        <span>{entity.register_court && entity.registration_number ? `${entity.register_court} · ${entity.registration_number}` : 'Keine Registeridentität'}</span>
                        <span>Profil: {entity.lifecycle?.profile_type || entity.entity_type} · {entity.lifecycle?.profile_status || entity.status}</span>
                        <span>Verifizierung: {entity.lifecycle?.verification_status || 'ungeprüft'}</span>
                        <span>Abo/Tarif: {entity.lifecycle?.subscription_plan || 'kein eigenes Abo'} · {entity.lifecycle?.subscription_status || '—'}</span>
                        <span>Postfach: {entity.lifecycle?.mailbox_status || 'ungeprüft'} · Nutzungsvereinbarung: {entity.lifecycle?.usage_agreement_status || 'ungeprüft'}</span>
                        <strong>Nächster Schritt: {companyProfileNextStepLabel(entity.lifecycle?.next_step)}</strong>
                        {entity.lifecycle?.blocked ? <span className="status-pill status-pill--warning">Fail-closed: {entity.lifecycle.conflict_codes.join(', ')}</span> : null}
                        <small>ID {entity.id.slice(0, 8)}…</small>
                        <details>
                          <summary>Rechtliche Angaben sicher korrigieren</summary>
                          <form className="admin-legal-entity-form" onSubmit={(event) => saveLegalEntityCorrection(event, entity)}>
                            <label>Rechtlicher Name<input name="legal_name" defaultValue={entity.legal_name || ''} /></label>
                            <label>Rechtsform<input name="legal_form" defaultValue={entity.legal_form || ''} /></label>
                            <label>Sitz<input name="registered_office" defaultValue={entity.registered_office || ''} /></label>
                            <label>Registergericht<input name="register_court" defaultValue={entity.register_court || ''} /></label>
                            <label>Registernummer<input name="registration_number" defaultValue={entity.registration_number || ''} /></label>
                            <label>Land (ISO)<input name="country_code" maxLength="2" defaultValue={entity.country_code || 'DE'} /></label>
                            <button type="submit" className="secondary-button" disabled={legalEntitySavingId === entity.id}>Korrektur speichern</button>
                          </form>
                        </details>
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : <p>{legalEntityOverview.migration_pending ? 'Die geschützte Bestandsvorschau muss vor der Migration vollständig grün sein.' : 'Noch keine Unternehmensprofile vorhanden.'}</p>}
          {legalEntityCorrectionMessage ? <p role="status">{legalEntityCorrectionMessage}</p> : null}
        </section>

        <div className="admin-operations-summary">
          <article><span>Gesamt offen</span><strong>{openOperations.length}</strong></article>
          <article><span>Kritisch</span><strong>{openOperations.filter((item) => item.priority === 'critical').length}</strong></article>
          <article><span>Hohe Priorität</span><strong>{openOperations.filter((item) => item.priority === 'high').length}</strong></article>
        </div>

        <section className="verification-review-worklist" aria-labelledby="verification-review-worklist-title">
          <div className="verification-review-worklist__header">
            <div>
              <h3 id="verification-review-worklist-title">Verifizierungen prüfen</h3>
              <p className="subtitle">Separate Arbeitsliste ohne zweite Bearbeitungslogik. „Fall öffnen“ führt direkt zur bestehenden Verifizierungsansicht.</p>
            </div>
            <StatusBadge label={`${verificationReviewWorklist.length} offen`} tone={verificationReviewWorklist.length ? 'warning' : 'success'} />
          </div>

          <div className="admin-filter-pills" aria-label="Offene Verifizierungen filtern">
            {VERIFICATION_REVIEW_FILTERS.map((option) => {
              const count = option.key === 'all'
                ? verificationReviewWorklist.length
                : verificationReviewWorklist.filter((item) => item.filters.includes(option.key)).length;
              return (
                <button
                  key={option.key}
                  type="button"
                  className={`admin-filter-pill ${verificationReviewFilter === option.key ? 'admin-filter-pill--active' : ''}`}
                  aria-pressed={verificationReviewFilter === option.key}
                  onClick={() => setVerificationReviewFilter(option.key)}
                >
                  {option.label} ({count})
                </button>
              );
            })}
          </div>

          {visibleVerificationReviewWorklist.length ? (
            <div className="verification-review-worklist__items" aria-live="polite">
              {visibleVerificationReviewWorklist.map((item) => (
                <article className="verification-review-item" key={item.id}>
                  <div className="verification-review-item__main">
                    <div className="verification-review-item__title">
                      <strong>{item.customerLabel}</strong>
                      <span>Fall #{item.verificationId}</span>
                    </div>
                    <div className="verification-review-item__badges">
                      {item.identityReview ? <StatusBadge label="Identität prüfen" tone="warning" /> : null}
                      {item.businessReview ? <StatusBadge label="Unternehmen prüfen" tone="warning" /> : null}
                      {item.followupAnswered ? <StatusBadge label="Nachforderung beantwortet" tone="info" /> : null}
                      {!item.hasUpload ? <StatusBadge label="Noch kein Upload" tone="neutral" /> : null}
                      {item.filters.includes('stale') ? <StatusBadge label="Lange unbearbeitet" tone="danger" /> : null}
                    </div>
                  </div>
                  <dl className="verification-review-item__details">
                    <div><dt>Letzter Upload</dt><dd>{item.latestUploadAt ? formatDateTime(item.latestUploadAt) : 'Noch keiner'}</dd></div>
                    <div><dt>Wartezeit</dt><dd>{formatWaitingTime(item.waitingMs)}</dd></div>
                    <div><dt>Nächste Aktion</dt><dd>{item.nextAction}</dd></div>
                  </dl>
                  <button
                    type="button"
                    className="action-button action-button--primary"
                    onClick={() => {
                      openAdminArea('dashboard-focus');
                      focusVerificationRow(item.verificationId);
                    }}
                  >
                    Fall öffnen
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="success-box">Für diesen Filter gibt es aktuell keine offenen Verifizierungen.</div>
          )}
        </section>

        {openOperations.length ? (
          <div className="admin-operations-list">
            {openOperations.map((item) => (
              <article key={item.id} className={`admin-operation admin-operation--${item.priority}`}>
                <span className="admin-operation__category">{item.category}</span>
                <div><strong>{item.title}</strong><p>{item.detail}</p></div>
                <button type="button" className="secondary-button" onClick={() => openAdminArea(item.area)}>Öffnen</button>
              </article>
            ))}
          </div>
        ) : <div className="success-box">Aktuell sind keine offenen Vorgänge aus den überwachten Bereichen vorhanden.</div>}
      </section>

      <section hidden={activeAdminArea !== 'system-status'} id="system-status" className="card admin-section-card admin-section-anchor">
        <div className="admin-section-header">
          <div>
            <h2>Systemstatus</h2>
            <p className="subtitle">
              Grün bedeutet erfolgreich geprüft, Rot bedeutet Fehler und Grau bedeutet bewusst deaktiviert oder nicht eingerichtet.
            </p>
          </div>
          <button
            type="button"
            className="secondary-button"
            onClick={() => loadSystemStatus({ force: true })}
            disabled={loadingSystemStatus}
          >
            {loadingSystemStatus ? 'Prüfung läuft…' : 'Jetzt neu prüfen'}
          </button>
        </div>

        {systemStatusError && <div className="admin-inline-warning">{systemStatusError}</div>}

        {systemStatus && (
          <>
            <div className="admin-status-summary">
              <div className={`admin-status-overall admin-status-overall--${systemStatus.overall}`}>
                {systemStatus.overall === 'critical'
                  ? 'Rot: Alle aktiven Dienste sind ausgefallen'
                  : systemStatus.overall === 'warning'
                    ? `Gelb: ${systemStatus.counts.error} ${systemStatus.counts.error === 1 ? 'Dienst ist' : 'Dienste sind'} gestört`
                    : 'Grün: Alle aktiven Systeme sind erreichbar'}
              </div>
              <span className="subtitle">
                Live-Anzeige · automatische Aktualisierung alle 30 Sekunden · letzte Prüfung: {formatDateTime(systemStatus.checked_at)}
              </span>
            </div>
            <div className="admin-status-grid">
              {systemStatus.checks.map((check) => (
                <article key={check.id} className={`admin-status-card admin-status-card--${check.status}`}>
                  <span className="admin-status-card__lamp" aria-hidden="true" />
                  <div>
                    <strong>{check.label}</strong>
                    <small>{check.message}</small>
                    {check.latency_ms !== null && (
                      <div className="admin-status-card__latency">{check.latency_ms} ms</div>
                    )}
                  </div>
                  {Array.isArray(check.details) && check.details.length > 0 ? (
                    <details className="admin-status-card__details">
                      <summary>{check.details.length} Tarife einzeln anzeigen</summary>
                      <div className="admin-status-card__detail-list">
                        {check.details.map((detail) => (
                          <div key={detail.label} className={`admin-status-card__detail admin-status-card__detail--${detail.status}`}>
                            <span className="admin-status-card__detail-lamp" aria-hidden="true" />
                            <span>{detail.label}: {detail.message}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  ) : null}
                </article>
              ))}
            </div>
          </>
        )}

        {!systemStatus && !systemStatusError && (
          <p className="subtitle">{loadingSystemStatus ? 'Systeme werden geprüft…' : 'Noch keine Prüfung vorhanden.'}</p>
        )}
      </section>

      <section hidden={activeAdminArea !== 'notifications'} id="notifications" className="card admin-section-card admin-section-anchor">
        <div className="admin-section-header">
          <div>
            <h2>Zentrale Benachrichtigungsübersicht</h2>
            <p className="subtitle">
              Kritische Meldungen, offene Maßnahmen, bestätigte Zustände und historische Hinweise aus den geschützten Betriebsbereichen.
            </p>
          </div>
          <button type="button" className="secondary-button" onClick={fetchData}>
            Aktualisieren
          </button>
        </div>

        {!notificationOverview ? (
          <p className="subtitle">Benachrichtigungen werden geladen…</p>
        ) : (
          <>
            <div className="admin-notification-privacy" role="note">
              <strong>Datenschutz:</strong> {notificationOverview.privacy}
            </div>

            <div className="admin-notification-counts" aria-label="Benachrichtigungsstatus">
              {['critical', 'open', 'confirmed', 'historical'].map((state) => (
                <article key={state}>
                  <span>{NOTIFICATION_STATE_LABELS[state]}</span>
                  <strong>{notificationOverview.counts?.[state] || 0}</strong>
                </article>
              ))}
            </div>

            <h3>Überwachte Quellen</h3>
            <div className="admin-notification-sources">
              {(notificationOverview.sources || []).map((source) => (
                <article key={source.id} className="admin-notification-source">
                  <div className="admin-notification-source__header">
                    <strong>{source.label}</strong>
                    <span className={`admin-notification-state admin-notification-state--${source.status}`}>
                      {NOTIFICATION_SOURCE_LABELS[source.status] || source.status}
                    </span>
                  </div>
                  <p>{source.detail}</p>
                </article>
              ))}
            </div>

            <div className="admin-section-header">
              <div>
                <h3>Zentraler Verlauf</h3>
                <p className="subtitle">Neueste geschützte Hinweise, maximal 100 Einträge.</p>
              </div>
              <small className="subtitle">Stand: {formatDateTime(notificationOverview.generated_at)}</small>
            </div>

            {(notificationOverview.items || []).length ? (
              <div className="admin-notification-list">
                {notificationOverview.items.map((item) => (
                  <article key={item.id} className={`admin-notification-entry admin-notification-entry--${item.state}`}>
                    <div>
                      <span className={`admin-notification-state admin-notification-state--${item.state}`}>
                        {NOTIFICATION_STATE_LABELS[item.state] || item.state}
                      </span>
                      <h4>{item.title}</h4>
                      <p>{item.detail}</p>
                      <div className="admin-notification-entry__meta">
                        {item.category_label} · {formatDateTime(item.created_at)}
                      </div>
                    </div>
                    <button type="button" className="secondary-button" onClick={() => openAdminArea(item.target_area)}>
                      Details öffnen
                    </button>
                  </article>
                ))}
              </div>
            ) : (
              <div className="success-box">Im zentralen Verlauf liegen aktuell keine Meldungen vor.</div>
            )}
          </>
        )}
      </section>

      <div hidden={activeAdminArea !== 'scans'} id="scans" className="admin-section-anchor">
        <ScanInboxSection
          token={token}
          onRefreshVerification={refreshVerification}
          onScanCountChange={setUnassignedScanCount}
          onBack={() => openAdminArea('overview')}
        />
      </div>

      <section hidden={activeAdminArea !== 'overview'} id="verification-automation" className="card admin-section-card admin-section-anchor">
        <div className="admin-section-header">
          <div>
            <h2>Verifizierungsautomatik</h2>
            <p className="subtitle">
              Dieser zentrale Schalter steuert, ob geeignete eingehende Bestellungen automatisch
              einen echten Verifizierungsfall anlegen dürfen.
            </p>
          </div>
          <StatusBadge
            label={settings.shopify_auto_verification_enabled
              ? 'Verifizierungsautomatik aktiv'
              : 'Verifizierungsautomatik aus'}
            tone={settings.shopify_auto_verification_enabled ? 'success' : 'neutral'}
          />
        </div>

        <div className="admin-toolbar">
          <button
            onClick={() => updateShopifyAutoSetting(!settings.shopify_auto_verification_enabled)}
            disabled={savingAutoSetting}
            className={`action-button ${settings.shopify_auto_verification_enabled ? 'action-button--danger' : 'action-button--success'}`}
          >
            {savingAutoSetting
              ? 'Wird gespeichert...'
              : settings.shopify_auto_verification_enabled
                ? 'Verifizierungsautomatik deaktivieren'
              : 'Verifizierungsautomatik aktivieren'}
          </button>
        </div>

        <p className="meta-text">
          Letzte Änderung:{' '}
          {settings.shopify_auto_verification_updated_at
            ? new Date(settings.shopify_auto_verification_updated_at).toLocaleString('de-DE')
            : '-'}
        </p>
      </section>


      <section hidden={activeAdminArea !== 'activity'} id="activity" className="card admin-section-card admin-section-anchor">
        <div className="admin-section-header">
          <div>
            <h2>Aktivität</h2>
            <p className="subtitle">
              Schneller Überblick über frische Vorgänge und die letzten relevanten Bewegungen im System.
            </p>
          </div>
          <StatusBadge
            label={`${activityOverview.recentActivity.length} letzte Aktionen`}
            tone={activityOverview.recentActivity.length > 0 ? 'info' : 'neutral'}
          />
        </div>

        <div className="admin-activity-layout">
          <div className="admin-activity-grid">
            <div className="admin-activity-card">
              <span>Neue Shopify Orders</span>
              <strong>{activityOverview.newOrders24h}</strong>
              <small>Bestellungen der letzten 24 Stunden</small>
            </div>
            <div className="admin-activity-card">
              <span>Neue Uploads</span>
              <strong>{activityOverview.newUploads24h}</strong>
              <small>Neue Dokumente oder Nachreichungen der letzten 24 Stunden</small>
            </div>
            <div className="admin-activity-card">
              <span>Offene Prüfungen</span>
              <strong>{activityOverview.openReviews}</strong>
              <small>Fälle mit Status „eingereicht“ oder „in Prüfung“</small>
            </div>
            <div className="admin-activity-card">
              <span>Nachforderungen offen</span>
              <strong>{activityOverview.followupsOpen}</strong>
              <small>Fälle mit offener Nachforderung oder angeforderten Dokumenten</small>
            </div>
          </div>

          <div className="admin-activity-feed">
            <h3 className="admin-activity-feed__title">Letzte Aktivitäten</h3>

            {activityOverview.recentActivity.length === 0 ? (
              <div className="admin-activity-feed__empty">
                Keine frischen Aktivitäten in den letzten 24 Stunden.
              </div>
            ) : (
              <div className="admin-activity-feed__list">
                {activityOverview.recentActivity.map((item) => (
                  <div key={item.key} className="admin-activity-feed__item">
                    <div className="admin-activity-feed__top">
                      <div>
                        <div className="admin-activity-feed__name">{item.title}</div>
                        <div className="admin-activity-feed__meta">{item.subtitle}</div>
                        <div className="admin-activity-feed__meta">
                          {formatDateTime(item.date)}
                        </div>
                      </div>

                      {item.verificationId ? (
                        <button
                          type="button"
                          className="admin-activity-open-button"
                          onClick={() => focusVerificationRow(item.verificationId)}
                        >
                          Fall öffnen
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section hidden={activeAdminArea !== 'mailbox-display'} id="mailbox-display" className="card admin-section-card admin-section-anchor">
        <div className="admin-section-header">
          <div>
            <h2>Briefkastenanzeige</h2>
            <p className="subtitle">
              Nur Kunden mit aktivem Seal-/Stripe-Abo und freigegebener Identität anzeigen und verwalten.
            </p>
          </div>
          <div className="admin-action-grid admin-action-grid--responsive">
            <button
              type="button"
              className="action-button action-button--secondary"
              disabled={mailboxCandidates.length === 0}
              onClick={fillMailboxSuggestions}
            >
              Vorschläge übernehmen
            </button>
            <button
              type="button"
              className="action-button action-button--primary"
              disabled={mailboxCandidates.length === 0}
              onClick={copyMailboxList}
            >
              Liste kopieren
            </button>
            <StatusBadge label={`${mailboxCandidates.length} berechtigte Anzeigen`} tone={mailboxCandidates.length ? 'info' : 'neutral'} />
          </div>
        </div>

        <div className="admin-source-status">
          <StatusBadge label={`Seal: ${mailboxCandidateSources.seal || 0}`} tone="neutral" />
          <StatusBadge label={`Stripe berechtigt: ${mailboxCandidateSources.stripe || 0}`} tone="info" />
          {mailboxReconciliation.stripe ? (
            <StatusBadge
              label={`Stripe aktiv: ${mailboxReconciliation.stripe.active_subscriptions || 0}`}
              tone="neutral"
            />
          ) : null}
          {mailboxReconciliation.stripe ? (
            <StatusBadge
              label={`Stripe stabil zugeordnet: ${mailboxReconciliation.stripe.stable_identity_customers || 0}/${mailboxReconciliation.stripe.unique_customers || 0}`}
              tone={mailboxReconciliation.stripe.stable_identity_customers === mailboxReconciliation.stripe.unique_customers ? 'success' : 'warning'}
            />
          ) : null}
        </div>

        {mailboxReconciliation.stripe?.excluded_identity_not_approved ? (
          <div className="admin-inline-warning">
            <strong>Stripe-Abgleich:</strong>{' '}
            {mailboxReconciliation.stripe.excluded_identity_not_approved} aktiv zugeordnete Kundschaft wird bewusst noch nicht angezeigt, weil die Identitätsfreigabe fehlt.
          </div>
        ) : null}
        {mailboxReconciliation.stripe?.excluded_missing_customer ? (
          <div className="admin-inline-warning">
            <strong>Stripe-Abweichung:</strong>{' '}
            {mailboxReconciliation.stripe.excluded_missing_customer} aktives Abonnement besitzt keine interne Kundenbindung.
          </div>
        ) : null}

        {mailboxCandidateWarnings.map((warning, index) => (
          <div className="admin-inline-warning" key={`${warning.source || 'source'}-${index}`}>
            <strong>{normalizeUtfText(warning.source || 'Quelle')}:</strong>{' '}
            {normalizeUtfText(warning.message || 'Quelle konnte nicht geladen werden.')}
          </div>
        ))}

        {mailboxCandidatesLoading ? (
          <p>Briefkasten-Kandidaten werden im Hintergrund geladen. Die übrigen Arbeitsbereiche bleiben währenddessen vollständig nutzbar.</p>
        ) : mailboxCandidates.length === 0 ? (
          <p>Keine Kunden mit aktivem Seal-/Stripe-Abo und freigegebener Identität gefunden.</p>
        ) : (
          <div className="table-wrap admin-limited-list" tabIndex="0" aria-label="Kunden für die Briefkastenanzeige">
            <table className="data-table">
              <caption className="visually-hidden">Kunden für die Briefkastenanzeige</caption>
              <thead>
                <tr>
                  <th>Kunde</th>
                  <th>Leistung</th>
                  <th>Firma / Name</th>
                  <th>Anzeige</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {compactArray(mailboxCandidates).map((candidate) => {
                  if (!candidate) return null;
                  const customerId = candidate.customer?.id;
                  const draftValue = customerId ? mailboxDrafts[customerId] || '' : '';
                  const hasDisplayProfile = Boolean(candidate.display_profile?.display_name);

                  return (
                    <tr key={`${candidate.subscription?.id || 'subscription'}-${customerId || 'unmatched'}`}>
                      <td>
                        {candidate.customer ? (
                          <>
                            <strong>{candidate.customer.name || candidate.customer.company_name || candidate.customer.email || '-'}</strong>
                            <br />
                            <small>{candidate.customer.email || '-'}</small>
                            <br />
                            <small>Kunde #{customerId}</small>
                          </>
                        ) : (
                          <>
                            <StatusBadge label="Kein Match" tone="warning" />
                            <br />
                            <small>{candidate.subscription?.email || '-'}</small>
                          </>
                        )}
                      </td>
                      <td>
                        <strong>{candidate.subscription?.product || '-'}</strong>
                        <br />
                        <small>Zahlungsanbieter: {candidate.subscription?.billing_provider === 'stripe' ? 'Stripe' : 'Seal'}</small>
                        <br />
                        <small>Bestell-ID: {candidate.subscription?.order_id || '-'}</small>
                      </td>
                      <td>
                        <strong>{candidate.customer?.company_name || '-'}</strong>
                        <br />
                        <small>{candidate.customer?.name || '-'}</small>
                      </td>
                      <td>
                        {customerId ? (
                          <>
                            <input
                              type="text"
                              value={draftValue}
                              placeholder="Anzeigename eintragen"
                              onChange={(event) => setMailboxDrafts((prev) => ({
                                ...prev,
                                [customerId]: event.target.value,
                              }))}
                            />
                            <div style={{ marginTop: '6px' }}>
                              {hasDisplayProfile ? (
                                <StatusBadge label="Bereits aktiv" tone="success" />
                              ) : candidate.suggested_display_name ? (
                                <small>Vorschlag aus Kundendaten</small>
                              ) : (
                                <small>Kein Vorschlag vorhanden.</small>
                              )}
                            </div>
                          </>
                        ) : (
                          <small>Bitte Kunde zuerst manuell zuordnen.</small>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="action-button action-button--primary"
                          disabled={!customerId || savingMailboxCustomerId === customerId}
                          onClick={() => saveMailboxDisplayName(candidate)}
                        >
            {savingMailboxCustomerId === customerId ? 'Speichert...' : 'Speichern'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section hidden={activeAdminArea !== 'dashboard-focus'} id="dashboard-focus" className="card admin-section-card admin-section-anchor">
        <div className="admin-section-header">
          <div>
            <h2>Bestellungen</h2>
            <p className="subtitle">
              Gemeinsame Filter und Suche für Bestellungen und die zugehörigen Verifizierungen.
            </p>
          </div>
          <StatusBadge
            label={
              quickFilter === 'all'
                ? 'Alle Bereiche'
                : QUICK_FILTER_OPTIONS.find((item) => item.key === quickFilter).label ||
                  'Filter aktiv'
            }
            tone={quickFilter === 'all' ? 'neutral' : 'info'}
          />
        </div>

        <div className="admin-quick-stats">
          <div className="admin-quick-stat-card">
            <span>Offene Fälle</span>
            <strong>{dashboardStats.openCases}</strong>
          </div>
          <div className="admin-quick-stat-card">
            <span>Neue Fälle</span>
            <strong>{dashboardStats.newCases}</strong>
          </div>
          <div className="admin-quick-stat-card">
            <span>Verifiziert</span>
            <strong>{dashboardStats.verifiedCases}</strong>
          </div>
          <div className="admin-quick-stat-card">
            <span>Nachforderung offen</span>
            <strong>{dashboardStats.followupCases}</strong>
          </div>
          <div className="admin-quick-stat-card">
            <span>Rückabwicklung</span>
            <strong>{dashboardStats.reversalCases}</strong>
          </div>
          <div className="admin-quick-stat-card">
            <span>Manuell prüfen</span>
            <strong>{dashboardStats.manualReviewOrders}</strong>
          </div>
        </div>

        <div className="admin-filter-toolbar">
          <div className="admin-filter-pills">
            {QUICK_FILTER_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`admin-filter-pill ${
                  quickFilter === option.key ? 'admin-filter-pill--active' : ''
                }`}
                onClick={() => setQuickFilter(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="admin-search-wrap">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Suchen nach Name, E-Mail, Firma oder Order ID"
              aria-label="Bestellungen durchsuchen"
              className="admin-search-input"
            />
            {searchTerm ? (
              <button
                type="button"
                className="admin-search-clear"
                onClick={() => setSearchTerm('')}
              >
                Zurücksetzen
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <section hidden={activeAdminArea !== 'dashboard-focus'} id="shopify-orders" className="card admin-section-card admin-section-anchor">
        <div className="admin-section-header">
          <div>
            <h2>Bestellungen</h2>
            <p className="subtitle">Shopify-/Seal- und Stripe-Bestellungen in einer gemeinsamen Übersicht.</p>
          </div>
          <StatusBadge label={`${visibleShopifyOrders.length} sichtbar`} tone="info" />
        </div>

        <div className="admin-filter-pills" aria-label="Bestellungen nach Zahlungsanbieter filtern">
          {[
            { key: 'all', label: 'Alle Anbieter' },
            { key: 'shopify_seal', label: 'Shopify / Seal' },
            { key: 'stripe', label: 'Stripe' },
          ].map((provider) => (
            <button
              key={provider.key}
              type="button"
              className={`admin-filter-pill ${orderProviderFilter === provider.key ? 'admin-filter-pill--active' : ''}`}
              onClick={() => setOrderProviderFilter(provider.key)}
            >
              {provider.label}
            </button>
          ))}
        </div>

        {visibleShopifyOrders.length === 0 ? (
          <p>Keine Bestellungen für die aktuelle Filterung gefunden.</p>
        ) : (
          <>
            <div className="admin-dashboard-desktop-only">
              <div className="table-wrap admin-limited-list" tabIndex="0" aria-label="Bestellungen und Zuordnungsstatus">
                <table className="data-table admin-shopify-table">
                  <caption className="visually-hidden">Bestellungen und Zuordnungsstatus</caption>
                  <thead>
                    <tr>
                      <th>Bestellung</th>
                      <th>Kunde</th>
                      <th>Produkt / Firma</th>
                      <th>Status / Match</th>
                      <th>Anbieter / Datum</th>
                      <th>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleShopifyOrders.map((order) => {
                      const verificationMeta = getOrderVerificationStatusMeta(order.verification_status);

                      return (
                        <tr key={order.id}>
                          <td>
                            <div className="admin-order-primary">
                              <strong>{order.order_reference || '-'}</strong>
                              {order.external_order_id && order.external_order_id !== order.order_reference ? (
                                <span className="admin-order-secondary">{order.external_order_id}</span>
                              ) : null}
                              {order.customer_order_index ? (
                                <span className="admin-order-secondary">Bestellung Nr. {order.customer_order_index}</span>
                              ) : null}
                            </div>
                          </td>
                          <td>{renderOrderCustomerBlock(order)}</td>
                          <td>
                            <div className="admin-order-stack">
                              <div>
                                <strong>{order.main_product || '-'}</strong>
                                {Array.isArray(order.products) && order.products.length > 1 && (
                                  <div className="admin-order-secondary">
                                    {order.products.length} Produkte
                                  </div>
                                )}
                              </div>
                              <div className="admin-order-secondary">
                                Firma: {order.company_name || '-'}
                              </div>
                              <div className="admin-order-secondary">
                                Zahlungsstatus: {order.financial_status || '-'}
                              </div>
                              {order.detected_customer_type || order.express_activation || order.detected_agreement_type ? (
                                <div className="admin-order-secondary">
                                  Produktlogik: {order.detected_customer_type ? getCustomerTypeLabel(order.detected_customer_type) : 'Zusatzprodukt'}
                                  {order.detected_agreement_type === 'gewerbe' ? ' · gewerbliche NV' : ''}
                                  {order.detected_agreement_type === 'none' ? ' · ohne NV' : ''}
                                  {order.express_activation ? ' · Express-Prüfung' : ''}
                                </div>
                              ) : null}
                              {Array.isArray(order.unknown_variant_ids) && order.unknown_variant_ids.length > 0 ? (
                                <div className="admin-order-secondary" style={{ color: '#b45309' }}>
                                  Unbekannte Variant-ID: {order.unknown_variant_ids.join(', ')}
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <div className="admin-status-stack">
                              <div>
                                <StatusBadge
                                  label={verificationMeta.label}
                                  tone={verificationMeta.tone}
                                />
                                {order.matched_verification_id ? (
                                  <div className="admin-order-secondary">
                                    Fall #{order.matched_verification_id}
                                  </div>
                                ) : null}
                              </div>
                              <div className="admin-order-secondary">
                                {getMatchSourceLabel(order.match_source)}
                                {order.matched_customer_id ? ` · Kunde #${order.matched_customer_id}` : ''}
                              </div>
                              {order.manual_review_required ? (
                                <div className="admin-order-secondary">
                                  Manuelle Prüfung nötig
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <div className="admin-status-stack">
                              <div>
                                <StatusBadge
                                  label={order.provider_label}
                                  tone={order.provider === 'stripe' ? 'info' : 'success'}
                                />
                              </div>
                              {order.provider === 'shopify_seal' ? (
                                <div className="admin-order-secondary">
                                  Automatik: {order.auto_verification_enabled ? 'AN' : 'AUS'}
                                </div>
                              ) : null}
                              {order.provider === 'shopify_seal' && order.auto_verification_decision ? (
                                <div className="admin-order-secondary">
                                  {getAutoDecisionLabel(order.auto_verification_decision)}
                                </div>
                              ) : null}
                              {order.provider === 'shopify_seal' && order.auto_verification_reason ? (
                                <div className="admin-order-secondary">
                                  {order.auto_verification_reason}
                                </div>
                              ) : null}
                              <div className="admin-order-secondary">
                                {order.order_date
                                  ? new Date(order.order_date).toLocaleString('de-DE')
                                  : '-'}
                              </div>
                              {order.payment_method_type ? (
                                <div className="admin-order-secondary">Zahlungsart: {order.payment_method_type}</div>
                              ) : null}
                            </div>
                          </td>
                          <td>{renderOrderActions(order)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="admin-dashboard-mobile-only">
              <div className="admin-mobile-list admin-limited-list">
                {visibleShopifyOrders.map((order) => {
                  const verificationMeta = getOrderVerificationStatusMeta(order.verification_status);

                  return (
                    <div key={order.id} className="admin-mobile-card">
                      <div className="admin-mobile-card__header">
                        <div>
                          <div className="admin-mobile-card__title">
                            {order.order_reference || '-'}
                          </div>
                          <div className="admin-mobile-card__subtitle">
                            {order.external_order_id || order.provider_label}
                          </div>
                        </div>
                        <StatusBadge
                          label={verificationMeta.label}
                          tone={verificationMeta.tone}
                        />
                      </div>

                      <MobileInfoRow label="Kunde">
                        {renderOrderCustomerBlock(order)}
                      </MobileInfoRow>

                      <MobileInfoRow label="Produkt">
                        <>
                          <div>{order.main_product || '-'}</div>
                          {Array.isArray(order.products) && order.products.length > 1 ? (
                            <div className="admin-muted-text">
                              {order.products.length} Produkte
                            </div>
                          ) : null}
                          {order.detected_customer_type || order.express_activation || order.detected_agreement_type ? (
                            <div className="admin-muted-text">
                              Produktlogik: {order.detected_customer_type ? getCustomerTypeLabel(order.detected_customer_type) : 'Zusatzprodukt'}
                              {order.detected_agreement_type === 'gewerbe' ? ' · gewerbliche NV' : ''}
                              {order.detected_agreement_type === 'none' ? ' · ohne NV' : ''}
                              {order.express_activation ? ' · Express-Prüfung' : ''}
                            </div>
                          ) : null}
                          {Array.isArray(order.unknown_variant_ids) && order.unknown_variant_ids.length > 0 ? (
                            <div className="admin-muted-text" style={{ color: '#b45309' }}>
                              Unbekannte Variant-ID: {order.unknown_variant_ids.join(', ')}
                            </div>
                          ) : null}
                        </>
                      </MobileInfoRow>

                      <MobileInfoRow label="Firma">
                        {order.company_name || '-'}
                      </MobileInfoRow>

                      <MobileInfoRow label="Zahlung">
                        {order.financial_status || '-'}
                      </MobileInfoRow>

                      <MobileInfoRow label="Match">
                        <>
                          <div>{getMatchSourceLabel(order.match_source)}</div>
                          {order.matched_customer_id ? (
                            <div className="admin-muted-text">
                              Kunde #{order.matched_customer_id}
                            </div>
                          ) : null}
                          {order.matched_verification_id ? (
                            <div className="admin-muted-text">
                              Fall #{order.matched_verification_id}
                            </div>
                          ) : null}
                        </>
                      </MobileInfoRow>

                      <MobileInfoRow label="Anbieter">
                        <>
                          <StatusBadge
                            label={order.provider_label}
                            tone={order.provider === 'stripe' ? 'info' : 'success'}
                          />
                          {order.provider === 'shopify_seal' ? (
                            <div className="admin-muted-text" style={{ marginTop: '6px' }}>
                              Automatik: {order.auto_verification_enabled ? 'AN' : 'AUS'}
                            </div>
                          ) : null}
                          {order.provider === 'shopify_seal' && order.auto_verification_decision ? (
                            <div className="admin-muted-text" style={{ marginTop: '6px' }}>
                              {getAutoDecisionLabel(order.auto_verification_decision)}
                            </div>
                          ) : null}
                          {order.provider === 'shopify_seal' && order.auto_verification_reason ? (
                            <div className="admin-muted-text" style={{ marginTop: '4px' }}>
                              {order.auto_verification_reason}
                            </div>
                          ) : null}
                        </>
                      </MobileInfoRow>

                      <MobileInfoRow label="Datum">
                        {formatDateTime(order.order_date)}
                      </MobileInfoRow>

                      <div className="admin-mobile-card__section">
                        <div className="admin-mobile-actions">
                          {renderOrderActions(order)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </section>

      <section hidden={activeAdminArea !== 'logistics'} id="logistics" className="card admin-section-card admin-section-anchor">
        <div className="admin-section-header">
          <div>
            <h2>Logistik</h2>
            <p className="subtitle">
              Paketweiterleitungen und physischer Sammelversand mit DHL-Tracking.
            </p>
          </div>
          <StatusBadge label={`${visibleLogisticsRequests.length} sichtbar`} tone="info" />
        </div>

        {visibleLogisticsRequests.length === 0 ? (
          <p>Keine Logistik-Aufträge für die aktuelle Filterung gefunden.</p>
        ) : (
          <>
            <div className="admin-dashboard-desktop-only">
              <div className="table-wrap admin-limited-list" tabIndex="0" aria-label="Logistikaufträge">
                <table className="data-table admin-shopify-table admin-logistics-table">
                  <caption className="visually-hidden">Logistikaufträge</caption>
                  <thead>
                    <tr>
                      <th>Kunde</th>
                      <th>Bestellung</th>
                      <th>Leistung</th>
                      <th>Status / Eingang</th>
                      <th>Empfänger / Paket</th>
                      <th>DHL / Notizen</th>
                      <th>Bearbeiten</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLogisticsRequests.map((item) => {
                      const statusMeta = getLogisticsStatusMeta(item.status);

                      return (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.customer_name || '-'}</strong>
                            <br />
                            <small>{item.customer_email || '-'}</small>
                            {item.company_name ? (
                              <>
                                <br />
                                <small>Firma: {item.company_name}</small>
                              </>
                            ) : null}
                          </td>
                          <td>
                            <strong>{item.shopify_order_name || '-'}</strong>
                            <br />
                            <small>{item.shopify_order_id}</small>
                          </td>
                          <td>
                            {getLogisticsServiceLabel(item.service_type, item.package_weight_class)}
                            {item.express_shipping_requested ? (
                              <>
                                <br />
                                <StatusBadge label="Express gewünscht" tone="warning" />
                              </>
                            ) : null}
                          </td>
                          <td>
                            <StatusBadge label={statusMeta.label} tone={statusMeta.tone} />
                            <br />
                            <small>Erwartet: {formatDateOnly(item.expected_arrival_date) || '-'}</small>
                            <br />
                            <small>Eingang: {item.inbound_tracking_number || '-'}</small>
                            <br />
                            <small>Dienstleister: {item.inbound_carrier || '-'}</small>
                          </td>
                          <td>
                            <small>{formatLogisticsRecipientAddress(item)}</small>
                            {item.recipient_phone ? (
                              <>
                                <br />
                                <small>Tel.: {item.recipient_phone}</small>
                              </>
                            ) : null}
                            {item.package_content_notes ? (
                              <>
                                <br />
                                <small>Inhalt: {item.package_content_notes}</small>
                              </>
                            ) : null}
                          </td>
                          <td>
                            <small>Ausgehend: {item.dhl_tracking_number_outbound || '-'}</small>
                            <br />
                            <small>Versandkosten: {formatMoneyValue(item.shipping_cost_amount, item.shipping_cost_currency)}</small>
                            <br />
                            {item.dhl_label_url ? (
                              <a href={item.dhl_label_url} target="_blank" rel="noopener noreferrer">
                                DHL Label öffnen
                              </a>
                            ) : (
                              <small>Label: -</small>
                            )}
                            {item.customer_notes ? (
                              <>
                                <br />
                                <small>Hinweis: {item.customer_notes}</small>
                              </>
                            ) : null}
                          </td>
                          <td>{renderLogisticsAdminFields(item)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="admin-dashboard-mobile-only">
              <div className="admin-mobile-list admin-limited-list">
                {visibleLogisticsRequests.map((item) => {
                  const statusMeta = getLogisticsStatusMeta(item.status);

                  return (
                    <div key={item.id} className="admin-mobile-card">
                      <div className="admin-mobile-card__header">
                        <div>
                          <div className="admin-mobile-card__title">
                            {item.customer_name || item.customer_email || '-'}
                          </div>
                          <div className="admin-mobile-card__subtitle">
                            {item.shopify_order_name || item.shopify_order_id}
                          </div>
                        </div>
                        <StatusBadge label={statusMeta.label} tone={statusMeta.tone} />
                      </div>

                      <MobileInfoRow label="E-Mail">{item.customer_email || '-'}</MobileInfoRow>
                      <MobileInfoRow label="Firma">{item.company_name || '-'}</MobileInfoRow>
                      <MobileInfoRow label="Leistung">
                        {getLogisticsServiceLabel(item.service_type, item.package_weight_class)}
                      </MobileInfoRow>
                      <MobileInfoRow label="Eingang">
                        <>
                          <div>{item.inbound_tracking_number || '-'}</div>
                          <div className="admin-muted-text">
                            {item.inbound_carrier || '-'} · {formatDateOnly(item.expected_arrival_date) || '-'}
                          </div>
                        </>
                      </MobileInfoRow>
                      <MobileInfoRow label="Empfänger">
                        <>
                          <div>{formatLogisticsRecipientAddress(item)}</div>
                          {item.recipient_phone ? (
                            <div className="admin-muted-text">Tel.: {item.recipient_phone}</div>
                          ) : null}
                        </>
                      </MobileInfoRow>
                      <MobileInfoRow label="Paket">
                        <>
                          <div>{item.package_content_notes || '-'}</div>
                          {item.sender_name ? (
                            <div className="admin-muted-text">Absender: {item.sender_name}</div>
                          ) : null}
                        </>
                      </MobileInfoRow>
                      <MobileInfoRow label="DHL">
                        <>
                          <div>{item.dhl_tracking_number_outbound || '-'}</div>
                          <div className="admin-muted-text">
                            Versandkosten: {formatMoneyValue(item.shipping_cost_amount, item.shipping_cost_currency)}
                          </div>
                          {item.dhl_label_url ? (
                            <a href={item.dhl_label_url} target="_blank" rel="noopener noreferrer">
                              Label öffnen
                            </a>
                          ) : null}
                        </>
                      </MobileInfoRow>
                      <div className="admin-mobile-card__section">
                        {renderLogisticsAdminFields(item)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </section>

      <section hidden={activeAdminArea !== 'portal-requests'} id="portal-requests" className="card admin-section-card admin-section-anchor">
        <ControlledInvoiceReview token={token} active={activeAdminArea === 'portal-requests'} />
        <FinanceEmergencyControl token={token} active={activeAdminArea === 'portal-requests'} />
        <div className="admin-section-header">
          <div>
            <h2>Portal-Anfragen</h2>
            <p className="subtitle">
              Kundenänderungen aus dem Portal prüfen, freigeben oder ablehnen.
            </p>
          </div>
          <StatusBadge label={`${portalRequests.length} offen`} tone={portalRequests.length ? 'warning' : 'success'} />
        </div>

        {portalRequests.length === 0 ? (
          <p>Keine offenen Portal-Anfragen.</p>
        ) : (
          <div className="table-wrap admin-limited-list" tabIndex="0" aria-label="Offene Portal-Anfragen">
            <table className="data-table">
              <caption className="visually-hidden">Offene Portal-Anfragen</caption>
              <thead>
                <tr>
                  <th>Kunde</th>
                  <th>Typ</th>
                  <th>Eingereicht</th>
                  <th>Daten</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {compactArray(portalRequests).map((request) => {
                  if (!request) return null;
                  const customer = request.customers || {};
                  const documents = request.documents || {};
                  const payload = request.requested_payload || {};
                  const legalEntity = request.customer_legal_entities || null;
                  const withdrawalPreview = withdrawalPreviews[request.id] || null;
                  const requestedRefundPreview = payload.expected_refund || null;
                  const isStripeWithdrawal = request.request_type === 'subscription_withdrawal' && payload.billing_provider === 'stripe';
                  const mailRequestLabels = {
                    mail_destruction: 'Original löschen',
                    mail_forwarding: 'Physischer Versand',
                  };
                  const isMailRequest = !!mailRequestLabels[request.request_type];
                  const mailItems = request.customer_portal_request_mail_items || [];
                  const isBillingAddressRequest = request.request_type === 'billing_address';
                  const mailPayments = request.additional_service_payments || [];
                  const mailServiceFee = mailPayments.find((payment) => payment.payment_stage === 'service_fee');
                  const mailPostage = mailPayments.find((payment) => payment.payment_stage === 'actual_postage');
                  const mailFullyPaid = mailServiceFee?.status === 'paid' && mailPostage?.status === 'paid';
                  const requestLabel = request.request_type === 'display_profile'
                    ?
                     'Namensanzeige / Logo'
                    : request.request_type === 'subscription_withdrawal'
                      ? 'Widerrufsanfrage'
                      : request.request_type === 'founding_completion'
                        ? 'Unternehmensgründung abgeschlossen'
                      : isBillingAddressRequest
                        ? 'Rechnungsadresse synchronisiert'
                      : mailRequestLabels[request.request_type] || 'Firmendaten';

                  return (
                    <tr key={request.id}>
                      <td>
                        <strong>{customer.company_name || customer.name || '-'}</strong>
                        <br />
                        <small>{customer.email || '-'}</small>
                      </td>
                      <td>
                        <StatusBadge label={requestLabel} tone={request.request_type === 'display_profile' ? 'info' : ['founding_completion', 'billing_address'].includes(request.request_type) ? 'success' : 'warning'} />
                      </td>
                      <td>{formatDateTime(request.created_at)}</td>
                      <td>
                        {legalEntity ? (
                          <div className="info-box" style={{ marginBottom: '0.6rem' }}>
                            <strong>Unternehmensprofil:</strong> {legalEntity.legal_name || 'Gründungsprofil'} · {legalEntity.legal_form || legalEntity.entity_type}<br />
                            <small>ID: {legalEntity.id} · Profilstatus: {legalEntity.status || '-'} · Verifizierung: Identität {request.verifications?.identity_status || '-'}, Unternehmen {request.verifications?.business_status || '-'}</small><br />
                            <strong>Nächster Schritt:</strong>{' '}
                            {request.request_type === 'founding_completion'
                              ? 'Nachweis und Registerdaten prüfen; danach Freigabe für genau dieses Profil. Der Kunde kann anschließend die versionierte Nutzungsvereinbarung erzeugen.'
                              : request.request_type === 'display_profile'
                                ? 'Schildname und Logo für genau dieses Profil prüfen; keine Daten eines anderen Profils übernehmen.'
                                : 'Geänderte Felder und Nachweis gegen den Verifizierungsfall prüfen; Unternehmensfreigabe nicht pauschal übernehmen.'}
                          </div>
                        ) : null}
                        <div className="admin-compact-json">
                          {Object.entries(payload).filter(([key, value]) => !['expected_refund', 'shipping_address', 'address', 'synchronization'].includes(key) && value !== null && value !== '').map(([key, value]) => {
                            const previousValue = getPortalSnapshotValue(request, key);
                            return (
                            <div key={key}>
                              <strong>{getPortalFieldLabel(key)}:</strong>
                              {previousValue !== undefined ? (
                                <span> alt: {formatPortalValue(previousValue)} · </span>
                              ) : ' '}
                              <span>neu: {formatPortalValue(value)}</span>
                            </div>
                            );
                          })}
                          {documents.original_filename ? (
                            <div>
                              <strong>Nachweis:</strong> {documents.original_filename}{' '}
                              <button type="button" className="link-button" onClick={() => openAdminFile(`${API_URL}/admin/files/verification/${documents.id}`)}>
                                Dokument öffnen
                              </button>
                            </div>
                          ) : null}
                          {request.request_type === 'subscription_withdrawal' ? (
                            <div className="info-box" style={{ marginTop: '0.6rem' }}>
                              <strong>Kundenvorschau bei Anfrage:</strong>{' '}
                              {formatRefundPreviewAmount(requestedRefundPreview)}
                              <br />
                              <strong>{isStripeWithdrawal ? 'Aktuelle Stripe-Vorschau:' : 'Aktuelle Shopify-Vorschau:'}</strong>{' '}
                              {withdrawalPreview ? formatRefundPreviewAmount(withdrawalPreview) : 'noch nicht geladen'}
                              {withdrawalPreview?.order_name ? <><br /><span>Bestellung: {withdrawalPreview.order_name}</span></> : null}
                              {withdrawalPreview?.transactions?.[0]?.gateway ? <><br /><span>Zahlungsweg: {withdrawalPreview.transactions[0].gateway}</span></> : null}
                              <br />
                              <small>Die Vorschau ist noch keine Gutschrift und löst keine Zahlung aus.</small>
                            </div>
                          ) : null}
                          {isBillingAddressRequest ? (
                            <div className="info-box" style={{ marginTop: '0.6rem' }}>
                              <strong>Synchronisationsnachweis</strong><br />
                              Stripe: <strong>{payload.synchronization?.results?.stripe || 'nicht verbunden'}</strong><br />
                              sevDesk: <strong>{payload.synchronization?.results?.sevdesk || 'nicht verbunden'}</strong><br />
                              <small>Nur Feldgruppe und technischer Status; keine externen IDs oder Geheimwerte.</small>
                            </div>
                          ) : null}
                          {request.request_type === 'mail_forwarding' ? (
                            <div className="info-box" style={{ marginTop: '0.6rem' }}>
                              <strong>Empfängeradresse</strong><br />
                              {payload.shipping_address ? (
                                <>
                                  {[
                                    payload.shipping_address.recipient_name,
                                    payload.shipping_address.recipient_company,
                                    payload.shipping_address.recipient_street,
                                    payload.shipping_address.recipient_address_extra,
                                    [payload.shipping_address.recipient_zip, payload.shipping_address.recipient_city].filter(Boolean).join(' '),
                                    payload.shipping_address.recipient_country,
                                  ].filter(Boolean).map((line) => <span key={line}>{line}<br /></span>)}
                                  Telefon: <strong>{payload.shipping_address.recipient_phone || '-'}</strong>
                                  <br /><br />
                                </>
                              ) : (
                                <><span className="admin-warning-text">Bei diesem Altauftrag wurde noch keine Empfängeradresse gespeichert.</span><br /><br /></>
                              )}
                              <strong>Stripe-Zahlungsstatus</strong><br />
                              Servicegebühr: <strong>{mailServiceFee?.status === 'paid' ? 'bezahlt' : mailServiceFee?.status || 'nicht erstellt'}</strong><br />
                              Tatsächliche Versandkosten: <strong>{mailPostage?.status === 'paid' ? 'bezahlt' : mailPostage?.status || 'noch nicht berechnet'}</strong>
                              {mailServiceFee?.hosted_invoice_url ? <><br /><a href={mailServiceFee.hosted_invoice_url} target="_blank" rel="noreferrer">Rechnung Servicegebühr</a></> : null}
                              {mailPostage?.hosted_invoice_url ? <><br /><a href={mailPostage.hosted_invoice_url} target="_blank" rel="noreferrer">Rechnung Versandkosten</a></> : null}
                            </div>
                          ) : null}
                          {isMailRequest ? (
                            <div className="info-box" style={{ marginTop: '0.6rem' }}>
                              <strong>Betroffene Poststücke ({mailItems.length})</strong>
                              {mailItems.length ? (
                                <ul>
                                  {mailItems.map((item) => (
                                    <li key={item.id}>
                                      {item.filename}{' '}
                                      <button
                                        type="button"
                                        className="link-button"
                                        onClick={() => openAdminFile(`${API_URL}/admin/portal-requests/${request.id}/mail-items/${item.id}/file`)}
                                      >
                                        Vorschau öffnen
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              ) : <p>Keine eindeutig verknüpften Poststücke.</p>}
                            </div>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="admin-action-grid admin-action-grid--responsive">
                          {isBillingAddressRequest ? (
                            <span className="admin-muted-text">Vom Kunden bestätigt; bei Teilfehler sicher im Kundenbereich wiederholbar.</span>
                          ) : null}
                          {!isBillingAddressRequest ? <>
                          {request.request_type === 'mail_forwarding' && !mailPostage ? (
                            <div className="admin-inline-form">
                              <input
                                type="text"
                                inputMode="decimal"
                                placeholder="DHL-Kosten in EUR"
                                value={postageDrafts[request.id] || ''}
                                onChange={(event) => setPostageDrafts((current) => ({ ...current, [request.id]: event.target.value }))}
                              />
                              <button
                                type="button"
                                className="action-button"
                                disabled={creatingPostageCheckoutId === request.id || mailServiceFee?.status !== 'paid'}
                                onClick={() => createMailPostageCheckout(request.id)}
                              >
                                {creatingPostageCheckoutId === request.id ? 'Wird erstellt...' : 'Stripe-Link für Versandkosten'}
                              </button>
                              {mailServiceFee?.status !== 'paid' ? <small>Erst nach bezahlter Servicegebühr verfügbar.</small> : null}
                            </div>
                          ) : null}
                          {request.request_type === 'subscription_withdrawal' && !withdrawalPreview ? (
                            <button
                              type="button"
                              className="action-button action-button--secondary"
                              disabled={loadingWithdrawalPreviewId === request.id}
                              onClick={() => loadWithdrawalPreview(request.id)}
                            >
                              {loadingWithdrawalPreviewId === request.id ? 'Vorschau lädt...' : 'Erstattungsvorschau öffnen'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="action-button action-button--success"
                              disabled={workingPortalRequestId === request.id || (request.request_type === 'mail_forwarding' && !mailFullyPaid)}
                              onClick={() => {
                                if (request.request_type === 'subscription_withdrawal') {
                                  const confirmed = window.confirm(isStripeWithdrawal
                                    ? `Letzte Sicherheitsprüfung\n\nAktuell von Stripe berechnete Rückerstattung: ${formatRefundPreviewAmount(withdrawalPreview)}.\n\nJetzt wirklich Stripe-Abonnement beenden, Stripe-Rückerstattung ausführen und technischen Zugang bereinigen?`
                                    : `Letzte Sicherheitsprüfung\n\nAktuell von Shopify berechnete Rückerstattung: ${formatRefundPreviewAmount(withdrawalPreview)}.\n\nJetzt wirklich Seal-Abonnement beenden, Shopify-Rückerstattung ausführen und technischen Zugang bereinigen?`
                                  );
                                  if (!confirmed) return;
                                }
                                if (isMailRequest) {
                                  const confirmed = window.confirm(
                                    `Wurde „${requestLabel}“ für dieses Poststück physisch vollständig erledigt? Die Datei wird anschließend in den endgültigen Nextcloud-Ordner verschoben.`
                                  );
                                  if (!confirmed) return;
                                }
                                handlePortalRequestAction(
                                  request.id,
                                  'approve',
                                  request.request_type === 'subscription_withdrawal'
                                    ? { confirmed_refund_amount: withdrawalPreview?.amount }
                                    : {}
                                );
                              }}
                            >
                              {request.request_type === 'subscription_withdrawal'
                                ? 'Widerruf endgültig durchführen'
                                : isMailRequest
                                  ? request.request_type === 'mail_forwarding' && !mailFullyPaid
                                    ? 'Zahlungen noch offen'
                                    : 'Physische Aktion erledigt'
                                  : 'Freigeben'}
                            </button>
                          )}
                          <button
                            type="button"
                            className="action-button action-button--danger"
                            disabled={workingPortalRequestId === request.id}
                            onClick={() => handlePortalRequestAction(request.id, 'reject')}
                          >
                            {isMailRequest ? 'Ablehnen / zurück in Posteingang' : 'Ablehnen'}
                          </button>
                          </> : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <CompanyDataChangeHistory
          items={companyDataHistory}
          language={getLang()}
          showCustomer
        />
      </section>

      <section hidden={activeAdminArea !== 'dashboard-focus'} id="verifications" className="card admin-section-card admin-section-anchor">
        <div className="admin-section-header">
          <div>
            <h2>Verifizierungen</h2>
            <p className="subtitle">Status, Nachforderungen, Freigaben und Dokumente pro Fall.</p>
          </div>
          <div className="admin-toolbar admin-toolbar--compact">
            {newUploadCount > 0 && (
              <StatusBadge
                label={`${newUploadCount} neue Nachreichung${newUploadCount > 1 ? 'en' : ''}`}
                tone="warning"
              />
            )}
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={hideCompleted}
                onChange={(e) => setHideCompleted(e.target.checked)}
              />
              <span>Vollständig freigegebene ausblenden</span>
            </label>
          </div>
        </div>

        <div className="admin-archive-toolbar">
          <label className="admin-inline-field">
            Archivansicht
            <select value={archiveFilter} onChange={(event) => setArchiveFilter(event.target.value)}>
              <option value="active">Live-Fälle</option>
              <option value="completed">Normales Archiv</option>
              <option value="cancelled">Kündigungsarchiv</option>
              <option value="archived">Alle archivierten</option>
              <option value="all">Alle Fälle</option>
            </select>
          </label>
          <button
            type="button"
            className="action-button action-button--secondary"
            disabled={runningArchiveMaintenance}
            onClick={runArchiveMaintenance}
          >
            {runningArchiveMaintenance ? 'Archivierung läuft...' : 'Archivierung ausführen'}
          </button>
          <StatusBadge label={`${visibleVerifications.length} sichtbar`} tone="info" />
        </div>

        {visibleVerifications.length === 0 ? (
          <p>Keine Verifizierungen gefunden.</p>
        ) : (
          <>
            <div className="admin-dashboard-desktop-only">
              <div className="table-wrap admin-limited-list" tabIndex="0" aria-label="Verifizierungsfälle">
                <table className="data-table admin-verifications-table">
                  <caption className="visually-hidden">Verifizierungsfälle</caption>
                  <thead>
                    <tr>
                      <th>Kunde</th>
                      <th>Produkt</th>
                      <th>Identitätsstatus</th>
                      <th>Unternehmensstatus</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleVerifications.map((ver) => {
                      const draft = followupDrafts[ver.id] || {
                        open: false,
                        requestedDocuments: [],
                        requestedMessage: '',
                      };

                      const identityMeta = getIdentityStatusMeta(ver.identity_status);
                      const businessMeta = getBusinessStatusMeta(ver.business_status);

                      return (
                        <React.Fragment key={ver.id}>
                          <tr
                            ref={(node) => {
                              desktopVerificationRowRefs.current[ver.id] = node;
                            }}
                            style={
                              highlightedVerificationId === ver.id
                                ?
                                 { backgroundColor: '#fff7ed' }
                                : undefined
                            }
                          >
                            <td>
                              <strong>{ver.customers.name || '-'}</strong>
                              <br />
                              <small>{ver.customers.email || '-'}</small>
                              <br />
                              <small>
                                Kundentyp: {getCustomerTypeLabel(ver.customers.customer_type)}
                              </small>
                              {ver.customers.company_name ? (
                                <>
                                  <br />
                                  <small>Firma: {ver.customers.company_name}</small>
                                </>
                              ) : null}
                              {ver.express_activation ? (
                                <>
                                  <br />
                                  <StatusBadge
                                    label={
                                      ver.express_deadline
                                        ? new Date(ver.express_deadline).getTime() < Date.now()
                                          ? 'Express-Prüfung überfällig'
                                          : 'Express-Prüfung läuft'
                                        : 'Express-Prüfung gebucht'
                                    }
                                    tone={
                                      ver.express_deadline && new Date(ver.express_deadline).getTime() < Date.now()
                                        ? 'danger'
                                        : 'warning'
                                    }
                                  />
                                  {ver.express_deadline ? (
                                    <>
                                      <br />
                                      <small>Entscheidung bis: {formatDateTime(ver.express_deadline)}</small>
                                    </>
                                  ) : (
                                    <>
                                      <br />
                                      <small>Frist beginnt nach vollständiger Einreichung.</small>
                                    </>
                                  )}
                                </>
                              ) : null}
                              {ver.latest_document_uploaded_at ? (
                                <>
                                  <br />
                                  <small>
                                    Letzter Upload: {formatDateTime(ver.latest_document_uploaded_at)}
                                  </small>
                                </>
                              ) : null}
                              {normalizeUtfText(ver.archive_status).toLowerCase() === 'archived' ? (
                                <>
                                  <br />
                                  <StatusBadge
                                    label={normalizeUtfText(ver.archive_bucket) === 'cancelled' ? 'Kündigungsarchiv' : 'Archiv'}
                                    tone={normalizeUtfText(ver.archive_bucket) === 'cancelled' ? 'warning' : 'neutral'}
                                  />
                                  {ver.retention_delete_after ? (
                                    <>
                                      <br />
                                      <small>Löschdatum: {formatDateTime(ver.retention_delete_after)}</small>
                                    </>
                                  ) : null}
                                </>
                              ) : null}
                              {ver.latest_upload_event ? (
                                <>
                                  <br />
                                  <StatusBadge
                                    label={ver.latest_upload_event.event_type === 'initial_submission'
                                      ? 'Erstverifizierung eingereicht'
                                      : ver.latest_upload_event.event_type === 'requested_followup'
                                        ? 'Angeforderte Unterlagen eingereicht'
                                        : 'Gründungsunterlagen eingereicht'}
                                    tone="warning"
                                  />
                                </>
                              ) : ver.latest_document_uploaded_at &&
                              ver.followup_request.created_at &&
                              new Date(ver.latest_document_uploaded_at) >
                                new Date(ver.followup_request.created_at) ? (
                                <>
                                  <br />
                                  <StatusBadge label="Neue Nachreichung" tone="warning" />
                                </>
                              ) : null}
                              {renderUsageAgreementBlock(ver)}
                            </td>
                            <td>{ver.orders.product_name || '-'}</td>
                            <td>
                              <StatusBadge label={identityMeta.label} tone={identityMeta.tone} />
                            </td>
                            <td>
                              {isBusinessCustomer(ver.customers.customer_type) ? (
                                <>
                                  <StatusBadge label={businessMeta.label} tone={businessMeta.tone} />
                                  {ver.followup_request.requested_documents.length ? (
                                    <>
                                      <br />
                                      <small>
                                        Offen: {ver.followup_request.requested_documents.join(', ')}
                                      </small>
                                    </>
                                  ) : null}
                                  {ver.followup_request.requested_message ? (
                                    <>
                                      <br />
                                      <small>
                                        Hinweis: {ver.followup_request.requested_message}
                                      </small>
                                    </>
                                  ) : null}
                                </>
                              ) : (
                                <StatusBadge label="Nicht relevant" tone="neutral" />
                              )}
                            </td>
                            <td>{renderVerificationActions(ver)}</td>
                          </tr>

                          {draft.open && (
                            <tr>
                              <td colSpan="5" className="expanded-row-cell">
                                <strong>Nachforderung auswählen</strong>
                                <div style={{ marginTop: '12px' }}>
                                  <div className="checkbox-grid">
                                    {FOLLOWUP_OPTIONS.map((option) => (
                                      <label key={option} className="checkbox-card">
                                        <input
                                          type="checkbox"
                                          checked={draft.requestedDocuments.includes(option)}
                                          onChange={() =>
                                            handleFollowupCheckboxChange(ver.id, option)
                                          }
                                        />
                                        <span>{option}</span>
                                      </label>
                                    ))}
                                  </div>

                                  <div style={{ marginTop: '12px' }}>
                                    <label
                                      style={{
                                        display: 'block',
                                        fontWeight: 600,
                                        marginBottom: '6px',
                                      }}
                                    >
                                      Zusätzlicher Hinweis
                                    </label>
                                    <textarea
                                      value={draft.requestedMessage}
                                      onChange={(e) =>
                                        handleFollowupMessageChange(ver.id, e.target.value)
                                      }
                                      rows={4}
                                      placeholder="Optionaler Hinweis für E-Mail und Upload-Link"
                                    />
                                  </div>

                                  <div className="admin-action-grid" style={{ marginTop: '12px' }}>
                                    <button
                                      onClick={() => submitFollowupRequest(ver.id)}
                                      disabled={savingFollowupId === ver.id}
                                      className="action-button action-button--warning"
                                    >
                                      {savingFollowupId === ver.id
                                        ?
                                         'Wird gesendet...'
                                        : 'Nachforderung senden'}
                                    </button>
                                    <button
                                      onClick={() => toggleFollowupForm(ver.id)}
                                      className="action-button action-button--secondary"
                                    >
                                      Abbrechen
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}

                          {editingVerificationId === ver.id && (
                            <tr>
                              <td colSpan="5" className="expanded-row-cell">
                                {renderVerificationEditPanel(ver)}
                              </td>
                            </tr>
                          )}

                          {openRows[ver.id] && (
                            <tr>
                              <td colSpan="5" className="expanded-row-cell">
                                <strong>Dokumente</strong>
                                <div style={{ marginTop: '10px' }}>
                                  {!documentsByVerification[ver.id] ||
                                  documentsByVerification[ver.id].length === 0 ? (
                                    <p>Keine Dokumente vorhanden.</p>
                                  ) : (
                                    <ul className="document-list">
                                      {documentsByVerification[ver.id].map((doc) => (
                                        <li key={doc.id}>
                                          <strong>{getDocumentLabel(doc.document_type)}</strong>
                                          <span> - </span>
                                          {renderDocumentDownload(doc)}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="admin-dashboard-mobile-only">
              <div className="admin-mobile-list admin-limited-list">
                {visibleVerifications.map((ver) => {
                  const draft = followupDrafts[ver.id] || {
                    open: false,
                    requestedDocuments: [],
                    requestedMessage: '',
                  };

                  const identityMeta = getIdentityStatusMeta(ver.identity_status);
                  const businessMeta = getBusinessStatusMeta(ver.business_status);

                  return (
                    <div
                      key={ver.id}
                      className="admin-mobile-card"
                      ref={(node) => {
                        mobileVerificationRowRefs.current[ver.id] = node;
                      }}
                      style={
                        highlightedVerificationId === ver.id
                          ?
                           { backgroundColor: '#fff7ed' }
                          : undefined
                      }
                    >
                      <div className="admin-mobile-card__header">
                        <div>
                          <div className="admin-mobile-card__title">
                            {ver.customers.name || '-'}
                          </div>
                          <div className="admin-mobile-card__subtitle">
                            {ver.customers.email || '-'}
                          </div>
                        </div>
                        <StatusBadge label={identityMeta.label} tone={identityMeta.tone} />
                      </div>

                      <MobileInfoRow label="Kundentyp">
                        {getCustomerTypeLabel(ver.customers.customer_type)}
                      </MobileInfoRow>

                      <MobileInfoRow label="Firma">
                        {ver.customers.company_name || '-'}
                      </MobileInfoRow>

                      <MobileInfoRow label="Produkt">
                        {ver.orders.product_name || '-'}
                      </MobileInfoRow>

                      {normalizeUtfText(ver.archive_status).toLowerCase() === 'archived' ? (
                        <MobileInfoRow label="Archiv">
                          <StatusBadge
                            label={normalizeUtfText(ver.archive_bucket) === 'cancelled' ? 'Kündigungsarchiv' : 'Archiv'}
                            tone={normalizeUtfText(ver.archive_bucket) === 'cancelled' ? 'warning' : 'neutral'}
                          />
                          {ver.retention_delete_after ? (
                            <div className="admin-muted-text" style={{ marginTop: '6px' }}>
                              Löschdatum: {formatDateTime(ver.retention_delete_after)}
                            </div>
                          ) : null}
                        </MobileInfoRow>
                      ) : null}

                      <MobileInfoRow label="Unternehmen">
                        {isBusinessCustomer(ver.customers.customer_type) ? (
                          <>
                            <StatusBadge label={businessMeta.label} tone={businessMeta.tone} />
                            {ver.followup_request.requested_documents.length ? (
                              <div className="admin-muted-text" style={{ marginTop: '6px' }}>
                                Offen: {ver.followup_request.requested_documents.join(', ')}
                              </div>
                            ) : null}
                            {ver.followup_request.requested_message ? (
                              <div className="admin-muted-text" style={{ marginTop: '4px' }}>
                                Hinweis: {ver.followup_request.requested_message}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <StatusBadge label="Nicht relevant" tone="neutral" />
                        )}
                      </MobileInfoRow>

                      <MobileInfoRow label="Letzter Upload">
                        {formatDateTime(ver.latest_document_uploaded_at)}
                      </MobileInfoRow>

                      <div className="admin-mobile-card__section">
                        {renderUsageAgreementBlock(ver, true)}
                      </div>

                      {ver.latest_upload_event ? (
                        <div style={{ marginTop: '8px' }}>
                          <StatusBadge
                            label={ver.latest_upload_event.event_type === 'initial_submission'
                              ? 'Erstverifizierung eingereicht'
                              : ver.latest_upload_event.event_type === 'requested_followup'
                                ? 'Angeforderte Unterlagen eingereicht'
                                : 'Gründungsunterlagen eingereicht'}
                            tone="warning"
                          />
                        </div>
                      ) : ver.latest_document_uploaded_at &&
                      ver.followup_request.created_at &&
                      new Date(ver.latest_document_uploaded_at) >
                        new Date(ver.followup_request.created_at) ? (
                        <div style={{ marginTop: '8px' }}>
                          <StatusBadge label="Neue Nachreichung" tone="warning" />
                        </div>
                      ) : null}

                      <div className="admin-mobile-card__section">
                        <div className="admin-mobile-actions">
                          {renderVerificationActions(ver)}
                        </div>
                      </div>

                      {draft.open && (
                        <div className="admin-mobile-card__section">
                          <strong>Nachforderung auswählen</strong>
                          <div style={{ marginTop: '12px' }}>
                            <div className="checkbox-grid">
                              {FOLLOWUP_OPTIONS.map((option) => (
                                <label key={option} className="checkbox-card">
                                  <input
                                    type="checkbox"
                                    checked={draft.requestedDocuments.includes(option)}
                                    onChange={() =>
                                      handleFollowupCheckboxChange(ver.id, option)
                                    }
                                  />
                                  <span>{option}</span>
                                </label>
                              ))}
                            </div>

                            <div style={{ marginTop: '12px' }}>
                              <label
                                style={{
                                  display: 'block',
                                  fontWeight: 600,
                                  marginBottom: '6px',
                                }}
                              >
                                Zusätzlicher Hinweis
                              </label>
                              <textarea
                                value={draft.requestedMessage}
                                onChange={(e) =>
                                  handleFollowupMessageChange(ver.id, e.target.value)
                                }
                                rows={4}
                                placeholder="Optionaler Hinweis für E-Mail und Upload-Link"
                              />
                            </div>

                            <div className="admin-mobile-actions" style={{ marginTop: '12px' }}>
                              <button
                                onClick={() => submitFollowupRequest(ver.id)}
                                disabled={savingFollowupId === ver.id}
                                className="action-button action-button--warning"
                              >
                                {savingFollowupId === ver.id
                                  ?
                                   'Wird gesendet...'
                                  : 'Nachforderung senden'}
                              </button>
                              <button
                                onClick={() => toggleFollowupForm(ver.id)}
                                className="action-button action-button--secondary"
                              >
                                Abbrechen
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {editingVerificationId === ver.id ? (
                        <div className="admin-mobile-card__section">
                          {renderVerificationEditPanel(ver)}
                        </div>
                      ) : null}

                      {openRows[ver.id] && (
                        <div className="admin-mobile-card__section">
                          <strong>Dokumente</strong>
                          <div style={{ marginTop: '10px' }}>
                            {!documentsByVerification[ver.id] ||
                            documentsByVerification[ver.id].length === 0 ? (
                              <p>Keine Dokumente vorhanden.</p>
                            ) : (
                              <ul className="document-list">
                                {documentsByVerification[ver.id].map((doc) => (
                                  <li key={doc.id}>
                                    <strong>{getDocumentLabel(doc.document_type)}</strong>
                                    <span> - </span>
                                    {renderDocumentDownload(doc)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </section>
      </div>
      </section>
      {refundDialog ? (
        <div className="admin-refund-modal" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeRefundDialog();
        }}>
          <section className="admin-refund-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-refund-title">
            <div className="admin-section-header">
              <div>
                <h2 id="admin-refund-title">{refundDialog.order_kind === 'shopify_order' ? 'Shopify-/PayPal-Rückerstattung' : 'Stripe-Rückerstattung'}</h2>
                <p className="subtitle">{refundDialog.full_name || refundDialog.email || 'Kunde'} · {refundDialog.order_reference}</p>
              </div>
              <button type="button" className="action-button action-button--secondary" onClick={closeRefundDialog} disabled={refundLoading}>Schließen</button>
            </div>
            {refundError ? <div className="alert alert-error" role="alert">{refundError}</div> : null}
            {refundLoading && !refundDetails ? <p>Zahlungsanbieter- und sevDesk-Daten werden geprüft...</p> : null}
            {refundDetails ? (
              <>
                <div className="admin-refund-summary">
                  <div><span>Ursprünglich</span><strong>{formatRevenue(refundDetails.original_amount_cents, refundDetails.currency)}</strong></div>
                  <div><span>Bereits erstattet</span><strong>{formatRevenue(refundDetails.refunded_amount_cents, refundDetails.currency)}</strong></div>
                  <div><span>Noch erstattbar</span><strong>{formatRevenue(refundDetails.remaining_amount_cents, refundDetails.currency)}</strong></div>
                  <div><span>Zahlungsart</span><strong>{stripePaymentMethodLabel(refundDetails.payment_method_type)}</strong></div>
                  <div><span>Zahlungsstatus</span><strong>{refundDetails.payment_status || '-'}</strong></div>
                </div>
                <p><strong>Originalrechnung:</strong> {refundDetails.sevdesk_invoice_number || refundDetails.sevdesk_invoice_id}</p>
                <fieldset className="admin-refund-fieldset">
                  <legend>Erstattungsumfang</legend>
                  {!refundDetails.full_only ? <label><input type="radio" name="refund-mode" checked={refundDraft.mode === 'partial'} onChange={() => setRefundDraft((value) => ({ ...value, mode: 'partial' }))} /> Teilbetrag</label> : null}
                  <label><input type="radio" name="refund-mode" checked={refundDraft.mode === 'full'} onChange={() => setRefundDraft((value) => ({ ...value, mode: 'full', amount: (refundDetails.remaining_amount_cents / 100).toFixed(2) }))} /> Gesamte Bestellung (offener Restbetrag)</label>
                </fieldset>
                <label className="admin-refund-control">
                  <span>Betrag in {refundDetails.currency}</span>
                  <input type="number" min="0.01" step="0.01" max={(refundDetails.remaining_amount_cents / 100).toFixed(2)} value={refundDraft.amount} disabled={refundDraft.mode === 'full'} onChange={(event) => setRefundDraft((value) => ({ ...value, amount: event.target.value }))} />
                </label>
                <label className="admin-refund-control">
                  <span>Interner Grund (optional)</span>
                  <textarea rows={3} maxLength={1000} value={refundDraft.reason} onChange={(event) => setRefundDraft((value) => ({ ...value, reason: event.target.value }))} />
                </label>
                {refundDetails.payment_method_type === 'sepa_debit' ? (
                  <label className="admin-refund-sepa">
                    <input type="checkbox" checked={refundDraft.sepaAcknowledged} onChange={(event) => setRefundDraft((value) => ({ ...value, sepaAcknowledged: event.target.checked }))} />
                    <span><strong>SEPA-Risiko geprüft:</strong> Die Gutschrift kann mehrere Werktage dauern. Bei paralleler Rücklastschrift oder Zahlungsanfechtung besteht das Risiko einer doppelten Erstattung.</span>
                  </label>
                ) : null}
                {Array.isArray(refundDetails.previous_refunds) && refundDetails.previous_refunds.length ? (
                  <div className="admin-refund-history">
                    <h3>Bisherige Rückerstattungen / Gutschriften</h3>
                    {refundDetails.previous_refunds.map((operation) => (
                      <div key={operation.id}>
                        <span>{formatRevenue(operation.amount_cents, operation.currency)} · {operation.status} · {operation.sevdesk_credit_note_number || 'Gutschrift in Bearbeitung'}</span>
                        {operation.stripe_refund_status === 'succeeded' && operation.email_status !== 'sent' ? (
                          <button type="button" className="action-button action-button--secondary" disabled={refundLoading} onClick={() => retryRefundEmail(operation.id)}>E-Mail erneut senden</button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="admin-refund-warning">
                  Nach Bestätigung wird die Erstattung serverseitig beim angezeigten Zahlungsanbieter angelegt. Vertrag, Seal und Zugang bleiben unverändert.
                </div>
                <button type="button" className="action-button action-button--danger" disabled={refundLoading || refundDetails.remaining_amount_cents <= 0 || (refundDetails.payment_method_type === 'sepa_debit' && !refundDraft.sepaAcknowledged)} onClick={submitRefund}>
                  {refundLoading ? 'Wird verarbeitet...' : 'Rückerstattung verbindlich auslösen'}
                </button>
              </>
            ) : null}
          </section>
        </div>
      ) : null}
    </main>
  );
}

export default AdminDashboard;
